import {
   default as calculateQuote,
   discountPrepaymentCodeToMonths,
   getPriceObjectFromProductCode,
   getPropertyOfActualEffectOnProduct,
   MonthlyPrice,
} from './calculate.mjs'
import { isProrated } from './prorationFunctions.mjs'
import { calculateActivationItemTotals } from './calculateActivationItemTotals.mjs'
import { calculateOngoingMonthlyCost } from './calculateOngoingMonthlyCost.mjs'
import {
   findObjectByKeyValue,
   formatFloatAsCurrencySymbol,
   formatDiscountPercent,
   IsForeignCurrencyQuote,
   isAlwaysUSD,
} from './helpers.mjs'

export default class GenerateQuoteData {
   // Products and Counts Selected
   QuoteExpirationDate = null
   QuoteActivationDate = null
   PayrollFirstPayDate = null
   CoreProductSelected = null
   ProductCoreEECount = 0
   ProductPerformanceEECount = null
   ProductTimeTrackingEECount = null
   ProductTimeAttendanceEECount = 0
   HasTimeClocks = null
   ProductTimeClockDetails = null
   HasUSEmployees = null
   IsATSOnly = false
   ProductBenefitsAdministrationEECount = null
   ProductPayrollEECount = null
   ProductPayrollTotalStateCount = null
   ImpType = null
   CoreCountryCount = 0
   BenAdminImpType = null
   PayrollImpType = null
   DiscountBundle = null
   DiscountNonProfit = null
   TermLength = null
   TermLengthsList = null
   PrepayLength = 1 // NEW: Explicit prepay length in months (decoupled from DiscountPrepaymentCode)
   DiscountPrepaymentCode = null
   DiscountMultiEntity = null
   DiscountHRMonthlyPromotion = null
   DiscountHRDealDeskMonthly = null
   DiscountReseller = null
   DiscountQuickstartTraining = null
   DiscountImpPromotion = null
   DiscountDealDeskHRImp = null
   DiscountDealDeskPayrollImp = null
   DiscountSecondEIN = null
   DiscountGroupImp = null
   ProductPayrollEINCount = 0
   ProductPayrollEECounts = []
   ProductPayrollMonthlyCosts = []
   ProductPayrollStateCounts = []
   ProductPayrollEINNames = []
   ProductPayrollEINIntegrations = []
   ProductPayrollMonthlyStateCosts = []
   DiscountDealDeskHRImpRate = 0
   DiscountDealDeskQuickstartImpRate = 0
   DiscountDealDeskPayrollImpRate = 0
   ProductAdditionalStorageCount = 0
   ProductAdditionalJobsCount = 0
   ProductHistoricalDataImport = false
   ProductMarketplaceListing = false
   ProductSponsorship = false
   ProductPaidServicesAmount = 0
   NumColumns = 6
   Comments = ''
   ProductTestAccount = false
   DifferenceQuote = false
   AccountSnowflake = {}
   QuoteType = 'full'
   PromotionalDiscounts = {}
   AccountBalance = 0
   MonthlyTax = 0
   ProfessionalServices = []
   ConsultingServices = []
   DynamicProducts = new Map() //<string, number>
   /** 'timeTracking' | 'timeAttendance' — from opportunity; drives time row label only. */
   TimeProductSelected = 'timeTracking'

   // Properties used in generation
   opportunity_id = null
   user_role = null
   product_list = {}
   discount_list = {}
   pricebookId = null
   calculatedValues = null
   hasPEPMCols = false
   hasDiscountPrepaymentCol = false
   host_url = null
   prefix = ''
   ProductCodeToProductCount = {}
   ShowBundleCol = false
   ShowPromotionalCol = false
   ShowDiscountCol = false
   ShowAppliedDiscount1Col = false
   ShowAppliedDiscount2Col = false
   ShowAppliedDiscount3Col = false
   ShowAppliedDiscount4Col = false
   ShowAppliedDiscount5Col = false
   ShowAppliedDiscount6Col = false
   ShowAppliedDiscount7Col = false
   ShowAppliedDiscountSummaryCol = false
   /** True when AD1 is reserved for the Volume discount column. AD1 is only displayed when a Volume discount is actually applied. */
   VolumeInAD1 = false
   /** True when AD2 is reserved for Bundle (US-only). AD1 always holds the top-priority non-bundle discount. False when no US employees, or when 7+ non-bundle discounts crowd out an empty bundle slot. */
   BundleInAD2 = false
   TopAppliedDiscountCodes = []
   TopAppliedDiscountNames = []
   TopAppliedDiscountCodesOneTime = []
   TopAppliedDiscountNamesOneTime = []
   /** Discount codes excluded from the "Other Discounts" sum when shown in dedicated columns (`bundleDiscount`). Prepayment uses `getOtherDiscountsSum` `excludePrepaymentFamily`. */
   MonthlyPdfExcludeAppliedFromOtherSum = []
   NumDiscountCols = 0
   productNameColumnWidth = 0
   productLPColumnWidth = 0
   productPEPMColumnWidth = 0
   productQuantityColumnWidth = 0
   productMonthsColumnWidth = 0
   productDiscountColumnWidth = 0
   productTotalColumnWidth = 0
   currencyObject = {}
   previousCurrencyObject = {}
   currencyObjectUSD = {}
   timeData = null
   NextInvoiceDate = null
   IsForeignCurrencyQuote = false
   UseAltDescription = false
   prepaymentActivationUseDifference = false
   maxEECount = 0

   // Rows to return
   HRHeaderRow = []
   ProductCoreRow = []
   ProductTimeTrackingRow = []
   TimeClocksHeaderRow = []
   TimeClocksProductRows = []
   ProductTimeClockMonthlySupportRow = []
   ProfessionalServicesHeaderRow = []
   ProfessionalServicesRow = []
   ConsultingServicesHeaderRow = []
   ConsultingServicesRow = []
   ProductBenefitsAdministrationRow = []
   ProductPerformanceRow = []
   ProductAdditionalJobsRow = []
   ProductAdditionalStorageRow = []
   PayrollHeaderRow = []
   ProductPayrollRows = []
   ProductPayrollStateRows = []
   ProductPayrollIntegrationsRows = []
   PayrollTotalRow = []
   ProductPayrollImpMonthlyCosts = []
   // ProductPayrollImpRows = [];
   ProductHistoricalDataImportRow = []
   ProductMarketplaceListingRow = []
   ProductSponsorshipRow = []
   ProductPaidServicesRow = []
   ProductTestAccountRow = []
   MonthlyHeaderRow = []
   MonthlyTotalRow = []
   OneTimeHeaderRow = []
   OneTimeTotalRow = []
   MonthlyDynamicProductRows = []
   PayrollDynamicProductRows = []
   OneTimeDynamicProductRows = []
   AlwaysUSDPayrollRows = []
   AlwaysUSDPayrollOneTimeRows = []
   AlwaysUSDPayrollOneTimeHeaderRow = []
   // HRImpRow = [];
   TotalItems = []
   PayrollTotalItems = []
   ActivationTotalItems = []
   TotalDiscountItems = []
   PayrollTotalDiscountItems = []
   RelatedEntityDiscountRow = []
   NonprofitDiscountRow = []
   PrepaymentDiscountRow = []
   HRImplementationRow = []
   BenAdminImplementationRow = []
   ImplementationCountriesRow = []
   PayrollImplementationRow = []
   AccountBalanceRow = []
   MonthlyTaxRow = []
   AllPayrollRows = []
   DifferenceQuoteHeader = []
   DifferenceQuoteRows = []

   //Values to return
   HRMonthlyTotal = 0
   PayrollMonthlyTotal = 0
   PayrollCombinedListPrice = 0
   PayrollCombinedTotalPrice = 0
   PayrollCombinedPreviousTotalPrice = 0
   PayrollCombinedTotalPriceDifference = 0
   PayrollCombinedAnnualListPrice = 0
   PayrollCombinedAnnualTotalPrice = 0
   PayrollCombinedTotalDiscount = 0
   PayrollCombinedTotalDiscountPercent = 0
   AlwaysUSDOneTimeTotal = 0
   AlwaysUSDOneTimeTotalListPrice = 0
   PayrollCombinedFirstYearListPrice = 0
   PayrollCombinedFirstYearTotalPrice = 0
   PayrollCombinedFirstYearDiscount = 0
   MonthlyGrandTotalListPrice = 0
   AnnualGrandTotalListPrice = 0
   MonthlyGrandTotal = 0
   MonthlyOngoingTotal = 0
   FirstYearTotal = 0
   PreviousMonthlyTotal = 0
   PreviousAnnualTotal = 0
   AnnualOngoingTotal = 0
   AnnualOngoingTotalListPrice = 0
   MonthlyDifference = 0
   MonthlyOngoingDifference = 0
   AnnualDifference = 0

   AnnualGrandTotal = 0
   OneTimeTotalListPrice = 0
   OneTimeTotal = 0
   OneTimeTotalDiscount = 0
   OneTimeTotalDiscountPercent = 0
   MonthlyGrandTotalDiscount = 0
   MonthlyGrandTotalDiscountPercent = 0
   TotalEffectiveDiscount = 0
   TotalEffectiveDiscountPercent = 0
   TotalFirstYearDiscount = 0
   TotalFirstYearDiscountPercent = 0
   ActivationPrice = 0
   ActivationListPrice = 0
   ActivationDiscount = 0
   ActivationDiscountPercent = 0
   MixedOverUnder25 = false
   HasPayrollSelected = false
   HasHRProducts = false
   HasNonPayrollDynamicProducts = false
   AnnualTotalListPrice = 0
   AnnualTotal = 0
   AnnualDiscount = 0
   AnnualDiscountPercent = 0
   HasOneTimeServices = false
   HasMonthlyServices = false
   MonthsSelected = 1
   AllOverMin = false
   IncludePayrollImpInActivation = false
   IsProrated = false
   DaysPaidInMonth = ''
   ShowEffectiveDiscountOnQuote = false
   EstimatedPrepaymentCoverage = ''
   ShowEstimatedCoveragePeriod = false
   HasEOR = false
   HasTimeLimitedDiscount = false
   HasManagedOrConsultingService = false

   //Permissions
   AllPermissions = {}
   PayrollImpInActivation = {}
   PromotionalDiscountColumnUTMs = {}
   ShowEffectiveDiscountOnQuotePermissions = {}
   UseAltProductDescription = {}

   TermContractFeature = {} // TODO: Remove this when feature goes live
   VolumeComponentToDisplay = {} // TODO: Remove this when feature goes live
   LimitVolumeDiscount = false
   calculationUserRole = null // Saved opp user_role for calculation permissions; falls back to this.user_role

   // Term Contract Varibales
   TermLengthObject = null // Product Representation of the Term Contract
   HRISEstimateCost = null // Estimate Cost of HRIS to show in the quote

   constructor(
      opportunity_id,
      user_role,
      hasPEPMCols = false,
      prefix = '',
      hasDiscountPrepaymentCol = false,
   ) {
      this.opportunity_id = opportunity_id
      this.user_role = user_role
      this.calculatedValues = null
      this.hasPEPMCols = hasPEPMCols
      this.hasDiscountPrepaymentCol = hasDiscountPrepaymentCol
      this.prefix = prefix !== '' ? prefix + ' ' : ''

      this.host_url = process.env.REACT_APP_HOST_URL

      this.Stage = null // Initialize stage property
      this.productCoreRow = []
   }

   static generateDifferenceObject(difference) {
      if (difference > 0) {
         return {
            class: 'currency',
            value: formatFloatAsCurrencySymbol(
               difference,
               this.currencyObject,
               true,
            ),
         }
      } else if (difference < 0) {
         return {
            class: 'discount currency',
            value:
               '(' +
               formatFloatAsCurrencySymbol(
                  Math.abs(difference, true),
                  this.currencyObject,
               ) +
               ')',
         }
      } else {
         return ''
      }
   }

   generateColumnWidths() {
      if (this.NumDiscountCols === 0) {
         if (this.hasPEPMCols) {
            // List Price + Effective PEPM must both get width; otherwise the PEPM
            // column is 0% and values read as one string (e.g. $699.00$699.00).
            this.productNameColumnWidth = 52
            this.productLPColumnWidth = 11
            this.productPEPMColumnWidth = 11
            this.productQuantityColumnWidth = 9
            this.productMonthsColumnWidth = 5
            this.productDiscountColumnWidth = 0
            this.productTotalColumnWidth = 12
         } else {
            this.productNameColumnWidth = 65
            this.productLPColumnWidth = 10
            this.productPEPMColumnWidth = 0
            this.productQuantityColumnWidth = 10
            this.productMonthsColumnWidth = 5
            this.productDiscountColumnWidth = 0
            this.productTotalColumnWidth = 10
         }
      } else if (this.hasPEPMCols) {
         if (this.NumDiscountCols === 1) {
            this.productNameColumnWidth = 47
            this.productLPColumnWidth = 10
            this.productPEPMColumnWidth = 10
            this.productQuantityColumnWidth = 10
            this.productMonthsColumnWidth = 5
            this.productDiscountColumnWidth = 9
            this.productTotalColumnWidth = 9
         }
         if (this.NumDiscountCols === 2) {
            this.productNameColumnWidth = 40
            this.productLPColumnWidth = 15
            this.productPEPMColumnWidth = 10
            this.productQuantityColumnWidth = 10
            this.productMonthsColumnWidth = 5
            this.productDiscountColumnWidth = 10
            this.productTotalColumnWidth = 10
         }
         if (this.NumDiscountCols === 3) {
            this.productNameColumnWidth = 40
            this.productLPColumnWidth = 15
            this.productPEPMColumnWidth = 10
            this.productQuantityColumnWidth = 10
            this.productMonthsColumnWidth = 5
            this.productDiscountColumnWidth = 10
            this.productTotalColumnWidth = 10
         }
         if (this.NumDiscountCols === 4) {
            this.productNameColumnWidth = 35
            this.productLPColumnWidth = 15
            this.productPEPMColumnWidth = 10
            this.productQuantityColumnWidth = 10
            this.productMonthsColumnWidth = 7
            this.productDiscountColumnWidth = 10
            this.productTotalColumnWidth = 13
         }
         if (this.NumDiscountCols === 5) {
            this.productNameColumnWidth = 30
            this.productLPColumnWidth = 12
            this.productPEPMColumnWidth = 9
            this.productQuantityColumnWidth = 9
            this.productMonthsColumnWidth = 6
            this.productDiscountColumnWidth = 8
            this.productTotalColumnWidth = 10
         }
         if (this.NumDiscountCols >= 6) {
            this.productNameColumnWidth = 28
            this.productLPColumnWidth = 11
            this.productPEPMColumnWidth = 8
            this.productQuantityColumnWidth = 8
            this.productMonthsColumnWidth = 5
            this.productDiscountColumnWidth = 7
            this.productTotalColumnWidth = 9
         }
      } else {
         if (this.NumDiscountCols === 1) {
            this.productNameColumnWidth = 55
            this.productLPColumnWidth = 10
            this.productPEPMColumnWidth = 0
            this.productQuantityColumnWidth = 10
            this.productMonthsColumnWidth = 5
            this.productDiscountColumnWidth = 10
            this.productTotalColumnWidth = 10
         }
         if (this.NumDiscountCols === 2) {
            this.productNameColumnWidth = 55
            this.productLPColumnWidth = 10
            this.productPEPMColumnWidth = 0
            this.productQuantityColumnWidth = 10
            this.productMonthsColumnWidth = 5
            this.productDiscountColumnWidth = 10
            this.productTotalColumnWidth = 10
         }
         if (this.NumDiscountCols === 3) {
            this.productNameColumnWidth = 51
            this.productLPColumnWidth = 14
            this.productPEPMColumnWidth = 0
            this.productQuantityColumnWidth = 10
            this.productMonthsColumnWidth = 5
            this.productDiscountColumnWidth = 10
            this.productTotalColumnWidth = 10
         }
         if (this.NumDiscountCols === 4) {
            this.productNameColumnWidth = 45
            this.productLPColumnWidth = 10
            this.productPEPMColumnWidth = 0
            this.productQuantityColumnWidth = 8
            this.productMonthsColumnWidth = 5
            this.productDiscountColumnWidth = 7
            this.productTotalColumnWidth = 9
         }
         if (this.NumDiscountCols === 5) {
            this.productNameColumnWidth = 40
            this.productLPColumnWidth = 10
            this.productPEPMColumnWidth = 0
            this.productQuantityColumnWidth = 8
            this.productMonthsColumnWidth = 5
            this.productDiscountColumnWidth = 7
            this.productTotalColumnWidth = 9
         }
         if (this.NumDiscountCols === 6) {
            this.productNameColumnWidth = 38
            this.productLPColumnWidth = 9
            this.productPEPMColumnWidth = 0
            this.productQuantityColumnWidth = 7
            this.productMonthsColumnWidth = 5
            this.productDiscountColumnWidth = 7
            this.productTotalColumnWidth = 9
         }
         if (this.NumDiscountCols >= 7) {
            this.productNameColumnWidth = 35
            this.productLPColumnWidth = 9
            this.productPEPMColumnWidth = 0
            this.productQuantityColumnWidth = 7
            this.productMonthsColumnWidth = 5
            this.productDiscountColumnWidth = 6
            this.productTotalColumnWidth = 9
         }
      }
   }

   generateMonthlyHeader() {
      let currencyCode = this.currencyObject.Code
      // Only show USD if foreign currency quote with payroll but no HR products AND no non-Payroll dynamic products
      if (
         this.IsForeignCurrencyQuote &&
         this.HasPayrollSelected &&
         !this.HasHRProducts &&
         !this.HasNonPayrollDynamicProducts
      )
         currencyCode = this.currencyObjectUSD.Code
      return [
         /*Product*/ {
            value: 'Product',
            class: 'tableHeader2 productNameColumnHeader',
            style: { '--width': `${this.productNameColumnWidth}%` },
         },
         /*List Price*/ ...(this.hasPEPMCols && !this.MixedOverUnder25
            ? [
                 {
                    value: 'PEPM List Price',
                    class: 'tableHeader2 currency productListPriceColumnHeader',
                    style: { '--width': `${this.productLPColumnWidth}%` },
                 },
              ]
            : [
                 {
                    value: 'List Price',
                    class: 'tableHeader2 currency productListPriceColumnHeader',
                    style: { '--width': `${this.productLPColumnWidth}%` },
                 },
              ]),
         /*Effective PEPM*/ ...(this.hasPEPMCols
            ? [
                 {
                    value: 'Effective\nPEPM',
                    class: 'tableHeader2 currency productPEPMColumnHeader',
                    style: { '--width': `${this.productPEPMColumnWidth}%` },
                 },
              ]
            : []),
         /*Quantity*/ {
            value: 'Quantity',
            class: 'tableHeader2 qty productQuantityColumnHeader',
            style: { '--width': `${this.productQuantityColumnWidth}%` },
         },
         /*Months*/ {
            value: 'Months',
            class: 'tableHeader2 qty productMonthsColumnHeader',
            style: { '--width': `${this.productMonthsColumnWidth}%` },
         },
         /*Bundle Discount*/ ...(this.ShowDiscountCol
            ? [
                 {
                    value: 'Discount',
                    class: 'tableHeader2 qty productListPriceColumnHeader',
                    style: {
                       '--width': `${this.productDiscountColumnWidth}%`,
                    },
                 },
              ]
            : this.ShowOnetimeDiscountCol
              ? [
                   {
                      value: '',
                      class: 'tableHeader2 qty productListPriceColumnHeader',
                      style: {
                         '--width': `${this.productDiscountColumnWidth}%`,
                      },
                   },
                ]
              : []),
         /*Promotional Discount*/ ...(this.ShowPromotionalCol
            ? [
                 {
                    value: 'Promotional\nDiscount',
                    class: 'tableHeader2 qty productDiscountColumnHeader',
                    style: { '--width': `${this.productDiscountColumnWidth}%` },
                 },
              ]
            : []),
         /*Applied Discount 1*/ ...(this.ShowAppliedDiscount1Col
            ? [{ value: this.TopAppliedDiscountNames[0] ?? '', class: 'tableHeader2 qty productDiscountColumnHeader', style: { '--width': `${this.productDiscountColumnWidth}%` } }]
            : []),
         /*Applied Discount 2*/ ...(this.ShowAppliedDiscount2Col
            ? [{ value: this.TopAppliedDiscountNames[1] ?? '', class: 'tableHeader2 qty productDiscountColumnHeader', style: { '--width': `${this.productDiscountColumnWidth}%` } }]
            : []),
         /*Applied Discount 3*/ ...(this.ShowAppliedDiscount3Col
            ? [{ value: this.TopAppliedDiscountNames[2] ?? '', class: 'tableHeader2 qty productDiscountColumnHeader', style: { '--width': `${this.productDiscountColumnWidth}%` } }]
            : []),
         /*Applied Discount 4*/ ...(this.ShowAppliedDiscount4Col
            ? [{ value: this.TopAppliedDiscountNames[3] ?? '', class: 'tableHeader2 qty productDiscountColumnHeader', style: { '--width': `${this.productDiscountColumnWidth}%` } }]
            : []),
         /*Applied Discount 5*/ ...(this.ShowAppliedDiscount5Col
            ? [{ value: this.TopAppliedDiscountNames[4] ?? '', class: 'tableHeader2 qty productDiscountColumnHeader', style: { '--width': `${this.productDiscountColumnWidth}%` } }]
            : []),
         /*Applied Discount 6*/ ...(this.ShowAppliedDiscount6Col
            ? [{ value: this.TopAppliedDiscountNames[5] ?? '', class: 'tableHeader2 qty productDiscountColumnHeader', style: { '--width': `${this.productDiscountColumnWidth}%` } }]
            : []),
         /*Applied Discount 7*/ ...(this.ShowAppliedDiscount7Col
            ? [{ value: this.TopAppliedDiscountNames[6] ?? '', class: 'tableHeader2 qty productDiscountColumnHeader', style: { '--width': `${this.productDiscountColumnWidth}%` } }]
            : []),
         /*Applied Discounts Summary*/ ...(this.ShowAppliedDiscountSummaryCol
            ? [{ value: 'Other\nDiscounts', class: 'tableHeader2 qty productDiscountColumnHeader', style: { '--width': `${this.productDiscountColumnWidth}%` } }]
            : []),
         /*Total Price*/ {
            value: `Total (${currencyCode})`,
            class: 'tableHeader2 currency productTotalColumnHeader',
            style: { '--width': `${this.productTotalColumnWidth}%` },
         },
      ]
   }
   generateHRProductHeader() {
      return [
         /*Product*/ {
            class: 'productHeader',
            value: 'HR',
            style: { textAlign: 'left', verticalAlign: 'bottom' },
         },
         /*List Price*/ { class: 'currency', value: '' },
         /*Effective PEPM*/ ...(this.hasPEPMCols
            ? [{ value: '', class: 'currency' }]
            : []),
         /*Quantity*/ { class: 'qty', value: '' },
         /*Months*/ { class: 'qty', value: '' },
         /*Bundle Discount*/ ...(this.ShowOnetimeDiscountCol ||
         this.ShowDiscountCol
            ? [{ class: 'qty discount', value: '' }]
            : []),
         /*Applied Discount 1*/ ...(this.ShowAppliedDiscount1Col ? [{ class: 'qty discount', value: '' }] : []),
         /*Applied Discount 2*/ ...(this.ShowAppliedDiscount2Col ? [{ class: 'qty discount', value: '' }] : []),
         /*Applied Discount 3*/ ...(this.ShowAppliedDiscount3Col ? [{ class: 'qty discount', value: '' }] : []),
         /*Applied Discount 4*/ ...(this.ShowAppliedDiscount4Col ? [{ class: 'qty discount', value: '' }] : []),
         /*Applied Discount 5*/ ...(this.ShowAppliedDiscount5Col ? [{ class: 'qty discount', value: '' }] : []),
         /*Applied Discount 6*/ ...(this.ShowAppliedDiscount6Col ? [{ class: 'qty discount', value: '' }] : []),
         /*Applied Discount 7*/ ...(this.ShowAppliedDiscount7Col ? [{ class: 'qty discount', value: '' }] : []),
         /*Applied Discounts Summary*/ ...(this.ShowAppliedDiscountSummaryCol ? [{ class: 'qty discount', value: '' }] : []),
         /*Total Price*/ { class: 'currency bold', value: '' },
      ]
   }

   generateProductCoreRow() {
      // Returns nothing if the product is not in the pricebook
      if (this.product_list[this.CoreProductSelected]?.NotInPricebook) return
      const coreCosts = this.calculatedValues.productCoreMonthlyCosts
      return [
         /*Product*/ {
            value:
               this.prefix +
               this.product_list[this.CoreProductSelected]['ProductName'],
            class: 'indent',
         },
         /*List Price*/ ...(this.ProductCoreEECount >= 25
            ? [
                 {
                    class: 'currency',
                    value: formatFloatAsCurrencySymbol(
                       this.calculatedValues.productCoreMonthlyCosts
                          .listPEPMCost,
                       this.currencyObject,
                       true,
                    ),
                 },
              ]
            : [
                 {
                    class: 'currency',
                    value: formatFloatAsCurrencySymbol(
                       this.calculatedValues.productCoreMonthlyCosts.listPrice,
                       this.currencyObject,
                       true,
                    ),
                 },
              ]),
         /*Effective PEPM*/ ...(this.calculatedValues.productCoreMonthlyCosts
            .pdfDisplayDiscountedPEPMCost <=
            this.calculatedValues.productCoreMonthlyCosts.listPEPMCost
            ? [
                 {
                    class: 'currency',
                    value: formatFloatAsCurrencySymbol(
                       this.calculatedValues.productCoreMonthlyCosts
                          .pdfDisplayDiscountedPEPMCost,
                       this.currencyObject,
                       true,
                    ),
                 },
              ]
            : this.hasPEPMCols
              ? [{ class: 'currency', value: '' }]
              : []),
         /*Quantity*/ { class: 'qty', value: this.ProductCoreEECount },
         /*Months*/ { class: 'qty', value: this.MonthsSelected },
         /*Bundle Discount*/ ...(this.ShowDiscountCol
            ? [{ class: 'qty discount', value: '' }]
            : this.ShowOnetimeDiscountCol
              ? [{ class: 'qty discount', value: '' }]
              : []),
         /*Promotional Discount*/ ...(this.ShowPromotionalCol
            ? [
                 {
                    class: 'qty discount',
                    value: getPromotionalDiscountPercent(
                       this.calculatedValues.productCoreMonthlyCosts,
                       this.PromoUTM,
                    ),
                 },
              ]
            : []),
         /*Applied Discount 1*/ ...(this.ShowAppliedDiscount1Col
            ? [{ class: 'qty discount', value: formatDiscountPercent(getDiscountAmountByCode(coreCosts, this.TopAppliedDiscountCodes[0])) }]
            : []),
         /*Applied Discount 2*/ ...(this.ShowAppliedDiscount2Col
            ? [{ class: 'qty discount', value: formatDiscountPercent(getDiscountAmountByCode(coreCosts, this.TopAppliedDiscountCodes[1])) }]
            : []),
         /*Applied Discount 3*/ ...(this.ShowAppliedDiscount3Col
            ? [{ class: 'qty discount', value: formatDiscountPercent(getDiscountAmountByCode(coreCosts, this.TopAppliedDiscountCodes[2])) }]
            : []),
         /*Applied Discount 4*/ ...(this.ShowAppliedDiscount4Col
            ? [{ class: 'qty discount', value: formatDiscountPercent(getDiscountAmountByCode(coreCosts, this.TopAppliedDiscountCodes[3])) }]
            : []),
         /*Applied Discount 5*/ ...(this.ShowAppliedDiscount5Col
            ? [{ class: 'qty discount', value: formatDiscountPercent(getDiscountAmountByCode(coreCosts, this.TopAppliedDiscountCodes[4])) }]
            : []),
         /*Applied Discount 6*/ ...(this.ShowAppliedDiscount6Col
            ? [{ class: 'qty discount', value: formatDiscountPercent(getDiscountAmountByCode(coreCosts, this.TopAppliedDiscountCodes[5])) }]
            : []),
         /*Applied Discount 7*/ ...(this.ShowAppliedDiscount7Col
            ? [{ class: 'qty discount', value: formatDiscountPercent(getDiscountAmountByCode(coreCosts, this.TopAppliedDiscountCodes[6])) }]
            : []),
         /*Applied Discounts Summary*/ ...(this.ShowAppliedDiscountSummaryCol
            ? [{ class: 'qty discount', value: formatDiscountPercent(getOtherDiscountsSum(coreCosts, this.TopAppliedDiscountCodes, this.MonthlyPdfExcludeAppliedFromOtherSum, { excludePrepaymentFamily: false })) }]
            : []),
         /*Total Price*/ ...(this.hasPEPMCols &&
         this.MixedOverUnder25 &&
         this.ProductCoreEECount >= 25
            ? [
                 {
                    class: 'currency',
                    value:
                       formatFloatAsCurrencySymbol(
                          this.calculatedValues.productCoreMonthlyCosts
                             .pdfDisplayTotal,
                          this.currencyObject,
                          true,
                       ) + '*',
                 },
              ]
            : this.hasPEPMCols && this.ProductCoreEECount < 25
              ? [
                   {
                      class: 'currency',
                      value:
                         formatFloatAsCurrencySymbol(
                            this.calculatedValues.productCoreMonthlyCosts
                               .pdfDisplayTotal,
                            this.currencyObject,
                            true,
                         ) + '**',
                   },
                ]
              : [
                   {
                      class: 'currency',
                      value:
                         formatFloatAsCurrencySymbol(
                            this.calculatedValues.productCoreMonthlyCosts
                               .pdfDisplayTotal,
                            this.currencyObject,
                            true,
                         ) + '*',
                   },
                ]),
         /*Product Code*/ {
            value: this.CoreProductSelected,
            class: 'productCode',
         },
      ]
   }

   generateProductBenefitsAdministrationRow() {
      // Returns nothing if the product is not in the pricebook
      if (this.product_list['benAdmin']?.NotInPricebook) return
      const benAdCosts = this.calculatedValues.productBenefitsAdministrationMonthlyCosts
      return [
         /*Product*/ {
            value: this.prefix + this.product_list['benAdmin']['ProductName'],
            class: 'indent',
         },
         /*List Price*/ ...(this.ProductBenefitsAdministrationEECount >= 25
            ? [
                 {
                    class: 'currency',
                    value: formatFloatAsCurrencySymbol(
                       this.calculatedValues
                          .productBenefitsAdministrationMonthlyCosts
                          .listPEPMCost,
                       this.currencyObject,
                       true,
                    ),
                 },
              ]
            : [
                 {
                    class: 'currency',
                    value: formatFloatAsCurrencySymbol(
                       this.calculatedValues
                          .productBenefitsAdministrationMonthlyCosts.listPrice,
                       this.currencyObject,
                       true,
                    ),
                 },
              ]),
         /*Effective PEPM*/ ...(!this.hasPEPMCols
            ? []
            : this.calculatedValues.productBenefitsAdministrationMonthlyCosts
                   .pdfDisplayDiscountedPEPMCost <=
                   this.calculatedValues
                      .productBenefitsAdministrationMonthlyCosts.listPEPMCost
              ? [
                   {
                      class: 'currency',
                      value: formatFloatAsCurrencySymbol(
                         this.calculatedValues
                            .productBenefitsAdministrationMonthlyCosts
                            .pdfDisplayDiscountedPEPMCost,
                         this.currencyObject,
                         true,
                      ),
                   },
                ]
              : [{ class: 'currency', value: '' }]),
         /*Quantity*/ {
            class: 'qty',
            value: this.ProductBenefitsAdministrationEECount,
         },
         /*Months*/ { class: 'qty', value: this.MonthsSelected },
         /*Bundle Discount*/ ...(this.ShowDiscountCol
            ? [{ class: 'qty discount', value: '' }]
            : this.ShowOnetimeDiscountCol
              ? [{ class: 'qty discount', value: '' }]
              : []),
         /*Promotional Discount*/ ...(this.ShowPromotionalCol
            ? [
                 {
                    class: 'qty discount',
                    value: getPromotionalDiscountPercent(
                       this.calculatedValues
                          .productBenefitsAdministrationMonthlyCosts,
                       this.PromoUTM,
                    ),
                 },
              ]
            : []),
         /*Applied Discount 1*/ ...(this.ShowAppliedDiscount1Col ? [{ class: 'qty discount', value: formatDiscountPercent(getDiscountAmountByCode(benAdCosts, this.TopAppliedDiscountCodes[0])) }] : []),
         /*Applied Discount 2*/ ...(this.ShowAppliedDiscount2Col ? [{ class: 'qty discount', value: formatDiscountPercent(getDiscountAmountByCode(benAdCosts, this.TopAppliedDiscountCodes[1])) }] : []),
         /*Applied Discount 3*/ ...(this.ShowAppliedDiscount3Col ? [{ class: 'qty discount', value: formatDiscountPercent(getDiscountAmountByCode(benAdCosts, this.TopAppliedDiscountCodes[2])) }] : []),
         /*Applied Discount 4*/ ...(this.ShowAppliedDiscount4Col ? [{ class: 'qty discount', value: formatDiscountPercent(getDiscountAmountByCode(benAdCosts, this.TopAppliedDiscountCodes[3])) }] : []),
         /*Applied Discount 5*/ ...(this.ShowAppliedDiscount5Col ? [{ class: 'qty discount', value: formatDiscountPercent(getDiscountAmountByCode(benAdCosts, this.TopAppliedDiscountCodes[4])) }] : []),
         /*Applied Discount 6*/ ...(this.ShowAppliedDiscount6Col ? [{ class: 'qty discount', value: formatDiscountPercent(getDiscountAmountByCode(benAdCosts, this.TopAppliedDiscountCodes[5])) }] : []),
         /*Applied Discount 7*/ ...(this.ShowAppliedDiscount7Col ? [{ class: 'qty discount', value: formatDiscountPercent(getDiscountAmountByCode(benAdCosts, this.TopAppliedDiscountCodes[6])) }] : []),
         /*Applied Discounts Summary*/ ...(this.ShowAppliedDiscountSummaryCol ? [{ class: 'qty discount', value: formatDiscountPercent(getOtherDiscountsSum(benAdCosts, this.TopAppliedDiscountCodes, this.MonthlyPdfExcludeAppliedFromOtherSum, { excludePrepaymentFamily: false })) }] : []),
         /*Total Price*/ ...(this.hasPEPMCols &&
         this.ProductBenefitsAdministrationEECount >= 25
            ? [
                 {
                    class: 'currency',
                    value:
                       formatFloatAsCurrencySymbol(
                          this.calculatedValues
                             .productBenefitsAdministrationMonthlyCosts
                             .pdfDisplayTotal,
                          this.currencyObject,
                          true,
                       ) + '*',
                 },
              ]
            : this.hasPEPMCols && this.ProductBenefitsAdministrationEECount < 25
              ? [
                   {
                      class: 'currency',
                      value:
                         formatFloatAsCurrencySymbol(
                            this.calculatedValues
                               .productBenefitsAdministrationMonthlyCosts
                               .pdfDisplayTotal,
                            this.currencyObject,
                            true,
                         ) + '**',
                   },
                ]
              : [
                   {
                      class: 'currency',
                      value:
                         formatFloatAsCurrencySymbol(
                            this.calculatedValues
                               .productBenefitsAdministrationMonthlyCosts
                               .pdfDisplayTotal,
                            this.currencyObject,
                            true,
                         ) + '*',
                   },
                ]),
         /*Product Code*/ { value: 'benAdmin', class: 'productCode' },
      ]
   }

   /**
    * Name shown on the fixed monthly time row (label follows TimeProductSelected).
    * Excludes timeAttendance from dynamic rows; label follows DB time_product_selected.
    */
   getTimeProductRowDisplayName() {
      if (
         this.TimeProductSelected === 'timeAttendance' &&
         this.product_list['timeAttendance'] &&
         !this.product_list['timeAttendance']?.NotInPricebook
      ) {
         return this.product_list['timeAttendance']['ProductName']
      }
      return this.product_list['timeTracking']?.['ProductName'] ?? ''
   }

   generateProductTimeTrackingRow() {
      const isAttendance = this.TimeProductSelected === 'timeAttendance'
      const activeProductCode = isAttendance ? 'timeAttendance' : 'timeTracking'
      // Returns nothing if the active time product is not in the pricebook
      if (this.product_list[activeProductCode]?.NotInPricebook) return
      const activeCosts = isAttendance
         ? this.calculatedValues.productTimeAttendanceMonthlyCosts
         : this.calculatedValues.productTimeTrackingMonthlyCosts
      const activeEECount = isAttendance
         ? this.ProductTimeAttendanceEECount
         : this.ProductTimeTrackingEECount
      // Don't render a zero-quantity row for whichever time product isn't in use.
      if (!activeEECount || activeEECount <= 0) return
      return [
         /*Product*/ {
            value: this.prefix + this.getTimeProductRowDisplayName(),
            class: 'indent',
         },
         /*List Price*/ {
            class: 'currency',
            value: formatFloatAsCurrencySymbol(
               activeCosts.listPEPMCost,
               this.currencyObject,
               true,
            ),
         },
         /*Effective PEPM*/ ...(!this.hasPEPMCols
            ? []
            : activeCosts.pdfDisplayDiscountedPEPMCost <=
                activeCosts.listPEPMCost
              ? [
                   {
                      class: 'currency',
                      value: formatFloatAsCurrencySymbol(
                         activeCosts.pdfDisplayDiscountedPEPMCost,
                         this.currencyObject,
                         true,
                      ),
                   },
                ]
              : [{ class: 'currency', value: '' }]),
         /*Quantity*/ { class: 'qty', value: activeEECount },
         /*Months*/ { class: 'qty', value: this.MonthsSelected },
         /*Bundle Discount*/ ...(this.ShowPromotionalCol ||
         this.ShowOnetimeDiscountCol ||
         this.ShowDiscountCol
            ? [{ class: 'qty discount', value: '' }]
            : []),
         /*Applied Discount 1*/ ...(this.ShowAppliedDiscount1Col ? [{ class: 'qty discount', value: formatDiscountPercent(getDiscountAmountByCode(activeCosts, this.TopAppliedDiscountCodes[0])) }] : []),
         /*Applied Discount 2*/ ...(this.ShowAppliedDiscount2Col ? [{ class: 'qty discount', value: formatDiscountPercent(getDiscountAmountByCode(activeCosts, this.TopAppliedDiscountCodes[1])) }] : []),
         /*Applied Discount 3*/ ...(this.ShowAppliedDiscount3Col ? [{ class: 'qty discount', value: formatDiscountPercent(getDiscountAmountByCode(activeCosts, this.TopAppliedDiscountCodes[2])) }] : []),
         /*Applied Discount 4*/ ...(this.ShowAppliedDiscount4Col ? [{ class: 'qty discount', value: formatDiscountPercent(getDiscountAmountByCode(activeCosts, this.TopAppliedDiscountCodes[3])) }] : []),
         /*Applied Discount 5*/ ...(this.ShowAppliedDiscount5Col ? [{ class: 'qty discount', value: formatDiscountPercent(getDiscountAmountByCode(activeCosts, this.TopAppliedDiscountCodes[4])) }] : []),
         /*Applied Discount 6*/ ...(this.ShowAppliedDiscount6Col ? [{ class: 'qty discount', value: formatDiscountPercent(getDiscountAmountByCode(activeCosts, this.TopAppliedDiscountCodes[5])) }] : []),
         /*Applied Discount 7*/ ...(this.ShowAppliedDiscount7Col ? [{ class: 'qty discount', value: formatDiscountPercent(getDiscountAmountByCode(activeCosts, this.TopAppliedDiscountCodes[6])) }] : []),
         /*Applied Discounts Summary*/ ...(this.ShowAppliedDiscountSummaryCol ? [{ class: 'qty discount', value: formatDiscountPercent(getOtherDiscountsSum(activeCosts, this.TopAppliedDiscountCodes, this.MonthlyPdfExcludeAppliedFromOtherSum, { excludePrepaymentFamily: false })) }] : []),
         /*Total Price*/ ...(this.hasPEPMCols && activeEECount >= 25
            ? [
                 {
                    class: 'currency',
                    value:
                       formatFloatAsCurrencySymbol(
                          activeCosts.pdfDisplayTotal,
                          this.currencyObject,
                          true,
                       ) + '*',
                 },
              ]
            : this.hasPEPMCols && activeEECount < 25
              ? [
                   {
                      class: 'currency',
                      value:
                         formatFloatAsCurrencySymbol(
                            activeCosts.pdfDisplayTotal,
                            this.currencyObject,
                            true,
                         ) + '**',
                   },
                ]
              : [
                   {
                      class: 'currency',
                      value:
                         formatFloatAsCurrencySymbol(
                            activeCosts.pdfDisplayTotal,
                            this.currencyObject,
                            true,
                         ) + '*',
                   },
                ]),
         /*Product Code*/ { value: activeProductCode, class: 'productCode' },
      ]
   }

   generateProductTimeClockMonthlySupportRow() {
      const tcCosts = this.calculatedValues.TimeClockMonthlyCost
      return [
         /*Product*/ {
            value:
               this.prefix +
               this.product_list['clockMonthlySupport']['ProductName'],
            class: 'indent',
         },
         /*List Price*/ {
            class: 'currency',
            value: formatFloatAsCurrencySymbol(
               this.calculatedValues.TimeClockMonthlyCost.listPEPMCost,
               this.currencyObject,
               true,
            ),
         },
         /*Effective PEPM*/ ...(!this.hasPEPMCols
            ? []
            : [
                 {
                    class: 'currency',
                    value: formatFloatAsCurrencySymbol(0, this.currencyObject),
                 },
              ]),
         /*Quantity*/ {
            class: 'qty',
            value: this.ProductTimeClockDetails.reduce(
               (acc, item) => acc + (item.timeClockCode ? item.count : 0),
               0,
            ),
         },
         /*Months*/ { class: 'qty', value: this.MonthsSelected },
         /*Bundle Discount*/ ...(this.ShowOnetimeDiscountCol ||
         this.ShowDiscountCol
            ? [{ class: 'qty discount', value: '' }]
            : []),
         /*Promotional Discount*/ ...(this.ShowPromotionalCol
            ? [{ class: 'qty discount', value: '' }]
            : []),
         /*Applied Discount 1*/ ...(this.ShowAppliedDiscount1Col ? [{ class: 'qty discount', value: formatDiscountPercent(getDiscountAmountByCode(tcCosts, this.TopAppliedDiscountCodes[0])) }] : []),
         /*Applied Discount 2*/ ...(this.ShowAppliedDiscount2Col ? [{ class: 'qty discount', value: formatDiscountPercent(getDiscountAmountByCode(tcCosts, this.TopAppliedDiscountCodes[1])) }] : []),
         /*Applied Discount 3*/ ...(this.ShowAppliedDiscount3Col ? [{ class: 'qty discount', value: formatDiscountPercent(getDiscountAmountByCode(tcCosts, this.TopAppliedDiscountCodes[2])) }] : []),
         /*Applied Discount 4*/ ...(this.ShowAppliedDiscount4Col ? [{ class: 'qty discount', value: formatDiscountPercent(getDiscountAmountByCode(tcCosts, this.TopAppliedDiscountCodes[3])) }] : []),
         /*Applied Discount 5*/ ...(this.ShowAppliedDiscount5Col ? [{ class: 'qty discount', value: formatDiscountPercent(getDiscountAmountByCode(tcCosts, this.TopAppliedDiscountCodes[4])) }] : []),
         /*Applied Discount 6*/ ...(this.ShowAppliedDiscount6Col ? [{ class: 'qty discount', value: formatDiscountPercent(getDiscountAmountByCode(tcCosts, this.TopAppliedDiscountCodes[5])) }] : []),
         /*Applied Discount 7*/ ...(this.ShowAppliedDiscount7Col ? [{ class: 'qty discount', value: formatDiscountPercent(getDiscountAmountByCode(tcCosts, this.TopAppliedDiscountCodes[6])) }] : []),
         /*Applied Discounts Summary*/ ...(this.ShowAppliedDiscountSummaryCol ? [{ class: 'qty discount', value: formatDiscountPercent(getOtherDiscountsSum(tcCosts, this.TopAppliedDiscountCodes, this.MonthlyPdfExcludeAppliedFromOtherSum, { excludePrepaymentFamily: false })) }] : []),
         /*Total Price*/ ...(this.hasPEPMCols
            ? [
                 {
                    class: 'currency',
                    value: formatFloatAsCurrencySymbol(
                       this.calculatedValues.TimeClockMonthlyCost.pdfDisplayTotal,
                       this.currencyObject,
                       true,
                    ),
                 },
              ]
            : [
                 {
                    class: 'currency',
                    value: formatFloatAsCurrencySymbol(
                       this.calculatedValues.TimeClockMonthlyCost.pdfDisplayTotal,
                       this.currencyObject,
                       true,
                    ),
                 },
              ]),
      ]
   }

   generateRelatedEntityDiscountRow() {
      return [
         //TODO: This doesn't have a nice name {class:" desc", value:this.prefix+this.discount_list['relatedEntity']["DiscountName"], style:{textAlign:"left", paddingLeft:"2em"}},
         /*Product*/ {
            value: this.prefix + 'Related Entity Discount',
            class: 'double-indent',
         },
         /*List Price*/ { class: 'currency', value: '' },
         /*Effective PEPM*/ ...(this.hasPEPMCols
            ? [{ class: 'currency', value: '' }]
            : []),
         /*Quantity*/ { class: 'qty', value: '' },
         /*Months*/ { class: 'qty', value: '' },

         /*Bundle Discount or Promotional Discount*/ ...(this.ShowDiscountCol
            ? [{ class: 'qty discount', value: '' }]
            : []),
         /*Applied Discount 1*/ ...(this.ShowAppliedDiscount1Col ? [{ class: 'qty discount', value: '' }] : []),
         /*Applied Discount 2*/ ...(this.ShowAppliedDiscount2Col ? [{ class: 'qty discount', value: '' }] : []),
         /*Applied Discount 3*/ ...(this.ShowAppliedDiscount3Col ? [{ class: 'qty discount', value: '' }] : []),
         /*Applied Discount 4*/ ...(this.ShowAppliedDiscount4Col ? [{ class: 'qty discount', value: '' }] : []),
         /*Applied Discount 5*/ ...(this.ShowAppliedDiscount5Col ? [{ class: 'qty discount', value: '' }] : []),
         /*Applied Discount 6*/ ...(this.ShowAppliedDiscount6Col ? [{ class: 'qty discount', value: '' }] : []),
         /*Applied Discount 7*/ ...(this.ShowAppliedDiscount7Col ? [{ class: 'qty discount', value: '' }] : []),
         /*Applied Discounts Summary*/ ...(this.ShowAppliedDiscountSummaryCol ? [{ class: 'qty discount', value: '' }] : []),
         /*Total Price*/ {
            class: 'currency discount',
            value:
               '(' +
               formatFloatAsCurrencySymbol(
                  this.calculatedValues.totalRelatedEntityDiscountAmount,
                  this.currencyObject,
                  true,
               ) +
               ')',
         },
      ]
   }

   generateProductAdditionalJobsRow() {
      // Returns nothing if the product is not in the pricebook
      if (this.product_list['addJobPosting']?.NotInPricebook) return
      return [
         //TODO: Get 'addJobPosting' somewhere else
         /*Product*/ {
            value:
               this.prefix + this.product_list['addJobPosting']['ProductName'],
            class: 'indent',
         },
         /*List Price*/ {
            class: 'currency',
            value: formatFloatAsCurrencySymbol(
               this.calculatedValues.AdditionalJobsCosts.listPEPMCost,
               this.currencyObject,
               true,
            ),
         },
         /*Effective PEPM*/ ...(this.hasPEPMCols
            ? [{ class: 'currency', value: '' }]
            : []),
         /*Quantity*/ { class: 'qty', value: this.ProductAdditionalJobsCount },
         /*Months*/ { class: 'qty', value: this.MonthsSelected },
         /*Bundle Discount or Promotional Discount*/ ...(this.ShowOnetimeDiscountCol ||
         this.ShowDiscountCol
            ? [{ class: 'qty discount', value: '' }]
            : []),
         /*Applied Discount 1*/ ...(this.ShowAppliedDiscount1Col ? [{ class: 'qty discount', value: '' }] : []),
         /*Applied Discount 2*/ ...(this.ShowAppliedDiscount2Col ? [{ class: 'qty discount', value: '' }] : []),
         /*Applied Discount 3*/ ...(this.ShowAppliedDiscount3Col ? [{ class: 'qty discount', value: '' }] : []),
         /*Applied Discount 4*/ ...(this.ShowAppliedDiscount4Col ? [{ class: 'qty discount', value: '' }] : []),
         /*Applied Discount 5*/ ...(this.ShowAppliedDiscount5Col ? [{ class: 'qty discount', value: '' }] : []),
         /*Applied Discount 6*/ ...(this.ShowAppliedDiscount6Col ? [{ class: 'qty discount', value: '' }] : []),
         /*Applied Discount 7*/ ...(this.ShowAppliedDiscount7Col ? [{ class: 'qty discount', value: '' }] : []),
         /*Applied Discounts Summary*/ ...(this.ShowAppliedDiscountSummaryCol ? [{ class: 'qty discount', value: '' }] : []),
         /*Total Price*/ {
            class: 'currency',
            value: formatFloatAsCurrencySymbol(
               this.calculatedValues.AdditionalJobsCosts.pdfDisplayTotal,
               this.currencyObject,
               true,
            ),
         },
      ]
   }

   generateProductAdditionalStorageRow() {
      // Returns nothing if the product is not in the pricebook
      if (this.product_list['addStorage']?.NotInPricebook) return
      return [
         //TODO: Get 'addStorage' somewhere else
         /*Product*/ {
            value: this.prefix + this.product_list['addStorage']['ProductName'],
            class: 'indent',
         },
         /*List Price*/ {
            class: 'currency',
            value: formatFloatAsCurrencySymbol(
               this.calculatedValues.AdditionalStorageCosts.listPEPMCost,
               this.currencyObject,
               true,
            ),
         },
         /*Effective PEPM*/ ...(this.hasPEPMCols
            ? [{ class: 'currency', value: '' }]
            : []),
         /*Quantity*/ {
            class: 'qty',
            value: this.ProductAdditionalStorageCount,
         },
         /*Months*/ { class: 'qty', value: this.MonthsSelected },
         /*Bundle Discount or Promotional Discount*/ ...(this.ShowOnetimeDiscountCol ||
         this.ShowDiscountCol
            ? [{ class: 'qty discount', value: '' }]
            : []),
         /*Applied Discount 1*/ ...(this.ShowAppliedDiscount1Col ? [{ class: 'qty discount', value: '' }] : []),
         /*Applied Discount 2*/ ...(this.ShowAppliedDiscount2Col ? [{ class: 'qty discount', value: '' }] : []),
         /*Applied Discount 3*/ ...(this.ShowAppliedDiscount3Col ? [{ class: 'qty discount', value: '' }] : []),
         /*Applied Discount 4*/ ...(this.ShowAppliedDiscount4Col ? [{ class: 'qty discount', value: '' }] : []),
         /*Applied Discount 5*/ ...(this.ShowAppliedDiscount5Col ? [{ class: 'qty discount', value: '' }] : []),
         /*Applied Discount 6*/ ...(this.ShowAppliedDiscount6Col ? [{ class: 'qty discount', value: '' }] : []),
         /*Applied Discount 7*/ ...(this.ShowAppliedDiscount7Col ? [{ class: 'qty discount', value: '' }] : []),
         /*Applied Discounts Summary*/ ...(this.ShowAppliedDiscountSummaryCol ? [{ class: 'qty discount', value: '' }] : []),
         /*Total Price*/ {
            class: 'currency',
            value: formatFloatAsCurrencySymbol(
               this.calculatedValues.AdditionalStorageCosts.pdfDisplayTotal,
               this.currencyObject,
               true,
            ),
         },
         /*Product Code*/ { value: 'addStorage', class: 'productCode' },
      ]
   }

   generateProductTestAccountRow() {
      // Returns nothing if the product is not in the pricebook
      if (this.product_list['testAccount']?.NotInPricebook) return
      return [
         /*Product*/ {
            value:
               this.prefix + this.product_list['testAccount']['ProductName'],
            class: 'indent',
         },
         /*List Price*/ {
            class: 'currency',
            value: formatFloatAsCurrencySymbol(
               this.calculatedValues.TestAccountCosts.listPrice,
               this.currencyObject,
               true,
            ),
         },
         /*Effective PEPM*/ ...(this.hasPEPMCols
            ? [{ class: 'currency', value: '' }]
            : []),
         /*Quantity*/ { class: 'qty', value: '1' },
         /*Months*/ {
            class: 'qty',
            value: this.PrepayLength > 1 ? this.PrepayLength : discountPrepaymentCodeToMonths(this.DiscountPrepaymentCode),
         },
         /*Bundle Discount or Promotional Discount*/ ...(this.ShowOnetimeDiscountCol ||
         this.ShowDiscountCol
            ? [{ class: 'qty discount', value: '' }]
            : []),
         /*Applied Discount 1*/ ...(this.ShowAppliedDiscount1Col ? [{ class: 'qty discount', value: '' }] : []),
         /*Applied Discount 2*/ ...(this.ShowAppliedDiscount2Col ? [{ class: 'qty discount', value: '' }] : []),
         /*Applied Discount 3*/ ...(this.ShowAppliedDiscount3Col ? [{ class: 'qty discount', value: '' }] : []),
         /*Applied Discount 4*/ ...(this.ShowAppliedDiscount4Col ? [{ class: 'qty discount', value: '' }] : []),
         /*Applied Discount 5*/ ...(this.ShowAppliedDiscount5Col ? [{ class: 'qty discount', value: '' }] : []),
         /*Applied Discount 6*/ ...(this.ShowAppliedDiscount6Col ? [{ class: 'qty discount', value: '' }] : []),
         /*Applied Discount 7*/ ...(this.ShowAppliedDiscount7Col ? [{ class: 'qty discount', value: '' }] : []),
         /*Applied Discounts Summary*/ ...(this.ShowAppliedDiscountSummaryCol ? [{ class: 'qty discount', value: '' }] : []),
         /*Total Price*/ {
            class: 'currency',
            value: formatFloatAsCurrencySymbol(
               this.calculatedValues.TestAccountCosts.pdfDisplayTotal,
               this.currencyObject,
               true,
            ),
         },
         /*Product Code*/ { value: 'testAccount', class: 'productCode' },
      ]
   }

   generateProductPerformanceRow() {
      // Returns nothing if the product is not in the pricebook
      if (this.product_list['performanceMan']?.NotInPricebook) {
         return
      }
      const perfCosts = this.calculatedValues.productPerformanceMonthlyCosts
      return [
         /*Product*/ {
            value: this.prefix + 'Performance Management',
            class: 'indent',
         }, //TODO: fix this to get from product_list
         /*List Price*/ ...(this.hasPEPMCols
            ? [
                 {
                    class: 'currency',
                    value: formatFloatAsCurrencySymbol(
                       this.calculatedValues.productPerformanceMonthlyCosts
                          .listPEPMCost,
                       this.currencyObject,
                       true,
                    ),
                 },
              ]
            : [
                 {
                    class: 'currency',
                    value: formatFloatAsCurrencySymbol(
                       this.calculatedValues.productPerformanceMonthlyCosts
                          .listPrice,
                       this.currencyObject,
                       true,
                    ),
                 },
              ]),
         /*Effective PEPM*/ ...(!this.hasPEPMCols
            ? []
            : this.calculatedValues.productPerformanceMonthlyCosts
                   .pdfDisplayDiscountedPEPMCost <=
                   this.calculatedValues.productPerformanceMonthlyCosts
                      .listPEPMCost
              ? [
                   {
                      class: 'currency',
                      value: formatFloatAsCurrencySymbol(
                         this.calculatedValues.productPerformanceMonthlyCosts
                            .pdfDisplayDiscountedPEPMCost,
                         this.currencyObject,
                         true,
                      ),
                   },
                ]
              : [{ class: 'currency', value: '' }]),
         /*Quantity*/ { class: 'qty', value: this.ProductPerformanceEECount },
         /*Months*/ { class: 'qty', value: this.MonthsSelected },
         /*Bundle Discount*/ ...(this.ShowOnetimeDiscountCol ||
         this.ShowDiscountCol
            ? [{ class: 'qty discount', value: '' }]
            : []),
         /*Promotional Discount*/ ...(this.ShowPromotionalCol ||
         this.ShowOnetimeDiscountCol ||
         this.ShowDiscountCol
            ? [{ class: 'qty discount', value: '' }]
            : []),
         /*Applied Discount 1*/ ...(this.ShowAppliedDiscount1Col ? [{ class: 'qty discount', value: formatDiscountPercent(getDiscountAmountByCode(perfCosts, this.TopAppliedDiscountCodes[0])) }] : []),
         /*Applied Discount 2*/ ...(this.ShowAppliedDiscount2Col ? [{ class: 'qty discount', value: formatDiscountPercent(getDiscountAmountByCode(perfCosts, this.TopAppliedDiscountCodes[1])) }] : []),
         /*Applied Discount 3*/ ...(this.ShowAppliedDiscount3Col ? [{ class: 'qty discount', value: formatDiscountPercent(getDiscountAmountByCode(perfCosts, this.TopAppliedDiscountCodes[2])) }] : []),
         /*Applied Discount 4*/ ...(this.ShowAppliedDiscount4Col ? [{ class: 'qty discount', value: formatDiscountPercent(getDiscountAmountByCode(perfCosts, this.TopAppliedDiscountCodes[3])) }] : []),
         /*Applied Discount 5*/ ...(this.ShowAppliedDiscount5Col ? [{ class: 'qty discount', value: formatDiscountPercent(getDiscountAmountByCode(perfCosts, this.TopAppliedDiscountCodes[4])) }] : []),
         /*Applied Discount 6*/ ...(this.ShowAppliedDiscount6Col ? [{ class: 'qty discount', value: formatDiscountPercent(getDiscountAmountByCode(perfCosts, this.TopAppliedDiscountCodes[5])) }] : []),
         /*Applied Discount 7*/ ...(this.ShowAppliedDiscount7Col ? [{ class: 'qty discount', value: formatDiscountPercent(getDiscountAmountByCode(perfCosts, this.TopAppliedDiscountCodes[6])) }] : []),
         /*Applied Discounts Summary*/ ...(this.ShowAppliedDiscountSummaryCol ? [{ class: 'qty discount', value: formatDiscountPercent(getOtherDiscountsSum(perfCosts, this.TopAppliedDiscountCodes, this.MonthlyPdfExcludeAppliedFromOtherSum, { excludePrepaymentFamily: false })) }] : []),
         /*Total Price*/ ...(this.hasPEPMCols &&
         this.ProductPerformanceEECount >= 25
            ? [
                 {
                    class: 'currency',
                    value:
                       formatFloatAsCurrencySymbol(
                          this.calculatedValues.productPerformanceMonthlyCosts
                             .pdfDisplayTotal,
                          this.currencyObject,
                          true,
                       ) + '*',
                 },
              ]
            : this.hasPEPMCols && this.ProductPerformanceEECount < 25
              ? [
                   {
                      class: 'currency',
                      value:
                         formatFloatAsCurrencySymbol(
                            this.calculatedValues.productPerformanceMonthlyCosts
                               .pdfDisplayTotal,
                            this.currencyObject,
                            true,
                         ) + '**',
                   },
                ]
              : [
                   {
                      class: 'currency',
                      value:
                         formatFloatAsCurrencySymbol(
                            this.calculatedValues.productPerformanceMonthlyCosts
                               .pdfDisplayTotal,
                            this.currencyObject,
                            true,
                         ) + '*',
                   },
                ]),
         /*Product Code*/ { value: 'performanceMan', class: 'productCode' },
      ]
   }

   generateMonthlyDynamicProductRows() {
      let rows = []
      let payrollRows = [] // Separate array for Payroll-heading products
      // Create a Map from QuoteHeading to an array of product codes <string, string[]>
      const productsByHeading = new Map()

      // Iterate through each entry in DynamicProducts
      for (const [productCode, count] of this.DynamicProducts) {
         // timeAttendance is rendered on the fixed time row, not here.
         if (productCode === 'timeAttendance') continue
         // Get the product details from product_list
         const product = this.product_list[productCode]
         if (
            !product ||
            product?.NotInPricebook ||
            product?.BillingFreq !== 'monthly' ||
            this.DynamicProducts.get(productCode) <= 0
         )
            continue

         // On foreign currency quotes, AlwaysUSD products go in the payroll section instead
         if (this.IsForeignCurrencyQuote && isAlwaysUSD(productCode))
            continue

         // Determine the heading - use 'none' if QuoteHeading is not defined
         // This groups products with no QuoteHeading together under the first section
         const heading = product?.QuoteHeading || 'none'

         // If this heading doesn't exist in our map yet, initialize it with an empty array
         if (!productsByHeading.has(heading)) {
            productsByHeading.set(heading, [])
         }

         // Add this product code to the appropriate heading's array
         productsByHeading.get(heading)?.push(productCode)
      }

      // Get all headings
      const headings = Array.from(productsByHeading.keys())

      // Sort headings to ensure 'none' is first
      const sortedHeadings = headings.sort((a, b) => {
         if (a === 'none') return -1 // Move 'none' to the beginning
         if (b === 'none') return 1 // Keep 'none' before other items
         return a.localeCompare(b) // Sort other headings alphabetically
      })

      for (const Heading of sortedHeadings) {
         let headerRow = [
            /*Product*/ {
               class: 'productHeader',
               value: Heading,
               style: { textAlign: 'left', verticalAlign: 'bottom' },
            },
            /*List Price*/ { class: 'currency', value: '' },
            /*Effective PEPM*/ ...(this.hasPEPMCols
               ? [{ value: '', class: 'currency' }]
               : []),
            /*Quantity*/ { class: 'qty', value: '' },
            /*Months*/ { class: 'qty', value: '' },
            /*One-time discount column placeholder*/ ...(this.HasOnetimeDiscount
               ? [{ class: 'qty discount', value: '' }]
               : this.NumDiscountCols > 0
                 ? [
                      {
                         value: '',
                         class: 'tableHeader2 qty',
                         style: {
                            minWidth: '2em',
                            width: '5em',
                            verticalAlign: 'top',
                            paddingTop: '0.5em',
                         },
                      },
                   ]
               : []),
            /*Applied Discount 1*/ ...(this.ShowAppliedDiscount1Col ? [{ class: 'qty discount', value: '' }] : []),
            /*Applied Discount 2*/ ...(this.ShowAppliedDiscount2Col ? [{ class: 'qty discount', value: '' }] : []),
            /*Applied Discount 3*/ ...(this.ShowAppliedDiscount3Col ? [{ class: 'qty discount', value: '' }] : []),
            /*Applied Discount 4*/ ...(this.ShowAppliedDiscount4Col ? [{ class: 'qty discount', value: '' }] : []),
            /*Applied Discount 5*/ ...(this.ShowAppliedDiscount5Col ? [{ class: 'qty discount', value: '' }] : []),
            /*Applied Discount 6*/ ...(this.ShowAppliedDiscount6Col ? [{ class: 'qty discount', value: '' }] : []),
            /*Applied Discount 7*/ ...(this.ShowAppliedDiscount7Col ? [{ class: 'qty discount', value: '' }] : []),
            /*Applied Discounts Summary*/ ...(this.ShowAppliedDiscountSummaryCol ? [{ class: 'qty discount', value: '' }] : []),
            /*Total Price*/ { class: 'currency bold', value: '' },
         ]
         // Skip header for 'none' and 'Payroll' (Payroll products go under existing Payroll section)
         if (Heading !== 'none' && Heading !== 'Payroll') rows.push(headerRow)
         // Get products for this heading and sort them by DisplayOrder
         const productsForHeading = productsByHeading.get(Heading) || []
         const sortedProducts = productsForHeading.sort((a, b) => {
            const orderA = this.product_list[a]?.DisplayOrder || 999999
            const orderB = this.product_list[b]?.DisplayOrder || 999999
            return orderA - orderB // Sort from low to high
         })
         for (const productCode of sortedProducts) {
            const product = this.product_list[productCode]
            const productCosts =
               this.calculatedValues?.selectedProductCosts?.[productCode] ??
               new MonthlyPrice()
            const isPEPM = product?.IsPEPM
            const count = (() => {
               const rawCount =
                  productCosts.productEECount > 0
                     ? productCosts.productEECount
                     : this.DynamicProducts.get(productCode)
               if (!product || rawCount === undefined) return 0
               switch (product.InputType) {
                  case 'boolean':
                     return 1
                  case 'integer':
                     return Math.floor(rawCount)
                  case 'decimal':
                     return Number(rawCount.toFixed(2))
                  default:
                     return rawCount
               }
            })()
            const row = [
               /*Product*/ {
                  value: this.prefix + product?.ProductName,
                  class: 'indent',
               },
               /*List Price*/ {
                  class: 'currency',
                  value: formatFloatAsCurrencySymbol(
                     product?.IsPEPM && count >= (product?.MinimumEmployees ?? 0)
                        ? productCosts.listPEPMCost 
                        : productCosts.listPrice,
                     this.currencyObject,
                     true,
                  ),
               },
               /*Effective PEPM*/ ...(!this.hasPEPMCols
                  ? []
                  : isPEPM
                    ? [
                         {
                            class: 'currency',
                            value: formatFloatAsCurrencySymbol(
                               productCosts.listPEPMCost,
                               this.currencyObject,
                            ),
                         },
                      ]
                    : [{ class: 'currency', value: '' }]),
               /*Quantity*/ { class: 'qty', value: count },
               /*Months*/ { class: 'qty', value: this.MonthsSelected },
               /*Bundle Discount*/ ...(this.ShowOnetimeDiscountCol ||
               this.ShowDiscountCol
                  ? [{ class: 'qty discount', value: '' }]
                  : []),
               /*Promotional Discount*/ ...(this.ShowPromotionalCol
                  ? [{ class: 'qty discount', value: '' }]
                  : []),
               /*Applied Discount 1*/ ...(this.ShowAppliedDiscount1Col ? [{ class: 'qty discount', value: formatDiscountPercent(getDiscountAmountByCode(productCosts, this.TopAppliedDiscountCodes[0])) }] : []),
               /*Applied Discount 2*/ ...(this.ShowAppliedDiscount2Col ? [{ class: 'qty discount', value: formatDiscountPercent(getDiscountAmountByCode(productCosts, this.TopAppliedDiscountCodes[1])) }] : []),
               /*Applied Discount 3*/ ...(this.ShowAppliedDiscount3Col ? [{ class: 'qty discount', value: formatDiscountPercent(getDiscountAmountByCode(productCosts, this.TopAppliedDiscountCodes[2])) }] : []),
               /*Applied Discount 4*/ ...(this.ShowAppliedDiscount4Col ? [{ class: 'qty discount', value: formatDiscountPercent(getDiscountAmountByCode(productCosts, this.TopAppliedDiscountCodes[3])) }] : []),
               /*Applied Discount 5*/ ...(this.ShowAppliedDiscount5Col ? [{ class: 'qty discount', value: formatDiscountPercent(getDiscountAmountByCode(productCosts, this.TopAppliedDiscountCodes[4])) }] : []),
               /*Applied Discount 6*/ ...(this.ShowAppliedDiscount6Col ? [{ class: 'qty discount', value: formatDiscountPercent(getDiscountAmountByCode(productCosts, this.TopAppliedDiscountCodes[5])) }] : []),
               /*Applied Discount 7*/ ...(this.ShowAppliedDiscount7Col ? [{ class: 'qty discount', value: formatDiscountPercent(getDiscountAmountByCode(productCosts, this.TopAppliedDiscountCodes[6])) }] : []),
               /*Applied Discounts Summary*/ ...(this.ShowAppliedDiscountSummaryCol ? [{ class: 'qty discount', value: formatDiscountPercent(getOtherDiscountsSum(productCosts, this.TopAppliedDiscountCodes, this.MonthlyPdfExcludeAppliedFromOtherSum, { excludePrepaymentFamily: false })) }] : []),
               /*Total Price*/ ...(this.hasPEPMCols
                  ? [
                       {
                          class: 'currency',
                          value: formatFloatAsCurrencySymbol(
                             productCosts.pdfDisplayTotal,
                             this.currencyObject,
                             true,
                          ),
                       },
                    ]
                  : [
                       {
                          class: 'currency',
                          value: formatFloatAsCurrencySymbol(
                             productCosts.pdfDisplayTotal,
                             this.currencyObject,
                             true,
                          ),
                       },
                    ]),
               /*Product Code*/ { value: productCode, class: 'productCode' },
            ]
            // Push Payroll products to separate array
            if (Heading === 'Payroll') {
               payrollRows.push(row)
            } else {
               rows.push(row)
            }
         }
      }
      return { rows, payrollRows }
   }

   generateOneTimeDynamicProductRows() {
      let rows = []
      // Create a Map from QuoteHeading to an array of product codes <string, string[]>
      const productsByHeading = new Map()

      // Iterate through each entry in DynamicProducts
      for (const [productCode, count] of this.DynamicProducts) {
         // Get the product details from product_list
         const product = this.product_list[productCode]
         if (
            !product ||
            product?.NotInPricebook ||
            product?.BillingFreq !== 'once' ||
            this.DynamicProducts.get(productCode) <= 0
         )
            continue

         // On foreign currency quotes, AlwaysUSD products go in the payroll section instead
         if (this.IsForeignCurrencyQuote && isAlwaysUSD(productCode))
            continue

         // Determine the heading - use 'none' if QuoteHeading is not defined
         // This groups products with no QuoteHeading together under the first section
         const heading = product?.QuoteHeading || 'none'

         // If this heading doesn't exist in our map yet, initialize it with an empty array
         if (!productsByHeading.has(heading)) {
            productsByHeading.set(heading, [])
         }

         // Add this product code to the appropriate heading's array
         productsByHeading.get(heading)?.push(productCode)
      }

      // Get all headings
      const headings = Array.from(productsByHeading.keys())

      // Sort headings to ensure 'none' is first
      const sortedHeadings = headings.sort((a, b) => {
         if (a === 'none') return -1 // Move 'none' to the beginning
         if (b === 'none') return 1 // Keep 'none' before other items
         return a.localeCompare(b) // Sort other headings alphabetically
      })

      for (const Heading of sortedHeadings) {
         let headerRow = [
            /*Product*/ {
               class: 'productHeader',
               value: Heading,
               style: { textAlign: 'left', verticalAlign: 'bottom' },
            },
            /*List Price*/ { class: 'currency', value: '' },
            /*Effective PEPM*/ ...(this.hasPEPMCols
               ? [{ value: '', class: 'currency' }]
               : []),
            /*Quantity*/ { class: 'qty', value: '' },
            /*Months*/ { class: 'qty', value: '' },
            ...this.oneTimeBundleDiscountSlotBodyCells(),
            ...this.oneTimePostBundleDiscountEmptyBodyCells(),
            ...this.oneTimeLineItemDiscountValueCells(),
            /*Total Price*/ { class: 'currency bold', value: '' },
         ]
         if (Heading !== 'none') rows.push(headerRow)
         // Get products for this heading and sort them by DisplayOrder
         const productsForHeading = productsByHeading.get(Heading) || []
         const sortedProducts = productsForHeading.sort((a, b) => {
            const orderA = this.product_list[a]?.DisplayOrder || 999999
            const orderB = this.product_list[b]?.DisplayOrder || 999999
            return orderA - orderB // Sort from low to high
         })
         for (const productCode of sortedProducts) {
            const product = this.product_list[productCode]
            const productCosts =
               this.calculatedValues?.selectedProductCosts?.[productCode] ??
               new OneTimePrice()
            const count = (() => {
               const rawCount =
                  productCosts.productEECount > 0
                     ? productCosts.productEECount
                     : this.DynamicProducts.get(productCode)
               if (!product || rawCount === undefined) return 0
               switch (product.InputType) {
                  case 'boolean':
                     return 1
                  case 'integer':
                     return Math.floor(rawCount)
                  case 'decimal':
                     return Number(rawCount.toFixed(2))
                  default:
                     return rawCount
               }
            })()
            const row = [
               /*Product*/ {
                  value: this.prefix + product?.ProductName,
                  class: 'indent',
               },
               /*List Price*/ {
                  class: 'currency',
                  value: formatFloatAsCurrencySymbol(
                     productCosts.listPrice,
                     this.currencyObject,
                     true,
                  ),
               },
               /*Effective PEPM*/ ...(this.hasPEPMCols
                  ? [{ value: '', class: 'currency' }]
                  : []),
               /*Quantity*/ { class: 'qty', value: count },
               /*Months*/ { class: 'qty', value: '' },
               ...this.oneTimeBundleDiscountSlotBodyCells(productCosts),
               ...this.oneTimePostBundleDiscountBodyCells(productCosts),
               ...this.oneTimeLineItemDiscountValueCells({ productCosts }),
               /*Total Price*/ {
                  class: 'currency',
                  value: formatFloatAsCurrencySymbol(
                     productCosts.pdfDisplayTotal,
                     this.currencyObject,
                     true,
                  ),
               },
               /*Product Code*/ { value: productCode, class: 'productCode' },
            ]
            rows.push(row)
         }
      }
      return rows
   }

   generatePayrollProductHeader() {
      let totalHeader = `Total (${this.currencyObjectUSD.Code})`
      // Hide the Total header if there are no HR products and no non-Payroll dynamic products
      // (since the main Monthly header will already show USD)
      if (
         this.IsForeignCurrencyQuote &&
         this.HasPayrollSelected &&
         !this.HasHRProducts &&
         !this.HasNonPayrollDynamicProducts
      )
         totalHeader = ''
      return [
         /*Product*/ {
            class: 'productHeader',
            value: 'Payroll',
            style: { textAlign: 'left' },
         },
         /*List Price*/ { class: 'currency', value: '' },
         /*Effective PEPM*/ ...(this.hasPEPMCols
            ? [{ value: '', class: 'currency' }]
            : []),
         /*Quantity*/ { class: 'qty', value: '' },
         /*Months*/ { class: 'qty', value: '' },
         /*Bundle Discount or Promotional Discount*/ ...(this.ShowOnetimeDiscountCol ||
         this.ShowDiscountCol
            ? [{ class: 'qty discount', value: '' }]
            : []),
         /*Applied Discount 1*/ ...(this.ShowAppliedDiscount1Col ? [{ class: 'qty discount', value: '' }] : []),
         /*Applied Discount 2*/ ...(this.ShowAppliedDiscount2Col ? [{ class: 'qty discount', value: '' }] : []),
         /*Applied Discount 3*/ ...(this.ShowAppliedDiscount3Col ? [{ class: 'qty discount', value: '' }] : []),
         /*Applied Discount 4*/ ...(this.ShowAppliedDiscount4Col ? [{ class: 'qty discount', value: '' }] : []),
         /*Applied Discount 5*/ ...(this.ShowAppliedDiscount5Col ? [{ class: 'qty discount', value: '' }] : []),
         /*Applied Discount 6*/ ...(this.ShowAppliedDiscount6Col ? [{ class: 'qty discount', value: '' }] : []),
         /*Applied Discount 7*/ ...(this.ShowAppliedDiscount7Col ? [{ class: 'qty discount', value: '' }] : []),
         /*Applied Discounts Summary*/ ...(this.ShowAppliedDiscountSummaryCol ? [{ class: 'qty discount', value: '' }] : []),
         /*Total Price*/ ...(this.IsForeignCurrencyQuote
            ? [
                 {
                    class: 'productHeader',
                    value: totalHeader,
                    style: { textAlign: 'right', verticalAlign: 'bottom' },
                 },
              ]
            : [{ class: 'productHeader', value: '' }]),
      ]
   }

   generateAlwaysUSDPayrollOneTimeHeader() {
      let totalHeader = `Total (${this.currencyObjectUSD.Code})`
      return [
         /*Product*/ {
            class: 'productHeader',
            value: 'Payroll',
            style: { textAlign: 'left' },
         },
         /*List Price*/ { class: 'currency', value: '' },
         /*Effective PEPM*/ ...(this.hasPEPMCols
            ? [{ value: '', class: 'currency' }]
            : []),
         /*Quantity*/ { class: 'qty', value: '' },
         /*Months*/ { class: 'qty', value: '' },
         ...this.oneTimeBundleDiscountSlotBodyCells(),
         ...this.oneTimePostBundleDiscountEmptyBodyCells(),
         ...this.oneTimeLineItemDiscountValueCells(),
         /*Total Price*/ ...(this.IsForeignCurrencyQuote
            ? [
                 {
                    class: 'productHeader',
                    value: totalHeader,
                    style: { textAlign: 'right', verticalAlign: 'bottom' },
                 },
              ]
            : [{ class: 'productHeader', value: '' }]),
      ]
   }

   generateProductPayrollRow(EINNumber = 0) {
      let EINName = this.ProductPayrollEINNames[EINNumber]
      if (EINName && this.ProductPayrollEINCount > 1) {
         EINName = ' ' + EINName
      } else if (this.ProductPayrollEINCount > 1) {
         EINName = 'EIN ' + (EINNumber + 1).toString()
      } else {
         EINName = this.product_list['payroll']['ProductName']
      }
      const payCosts = this.ProductPayrollMonthlyCosts[EINNumber]

      return [
         /*Product*/ { value: this.prefix + EINName, class: 'indent' },
         /*List Price*/ ...(this.ProductPayrollEECounts[EINNumber] >= 25
            ? [
                 {
                    class: 'currency',
                    value: formatFloatAsCurrencySymbol(
                       this.ProductPayrollMonthlyCosts[EINNumber].listPEPMCost,
                       this.currencyObjectUSD,
                       true,
                    ),
                 },
              ]
            : [
                 {
                    class: 'currency',
                    value: formatFloatAsCurrencySymbol(
                       this.ProductPayrollMonthlyCosts[EINNumber].listPrice,
                       this.currencyObjectUSD,
                       true,
                    ),
                 },
              ]),
         /*Effective PEPM*/ ...(!this.hasPEPMCols
            ? []
            : this.ProductPayrollMonthlyCosts[EINNumber]
                   .pdfDisplayDiscountedPEPMCost <=
                   this.ProductPayrollMonthlyCosts[EINNumber].listPEPMCost
              ? [
                   {
                      class: 'currency',
                      value: formatFloatAsCurrencySymbol(
                         this.ProductPayrollMonthlyCosts[EINNumber]
                            .pdfDisplayDiscountedPEPMCost,
                         this.currencyObjectUSD,
                         true,
                      ),
                   },
                ]
              : [{ class: 'currency', value: '' }]),
         /*Quantity*/ {
            class: 'qty',
            value: this.ProductPayrollEECounts[EINNumber],
         },
         /*Months*/ { class: 'qty', value: this.MonthsSelected },
         /*Bundle Discount*/ ...(this.ShowDiscountCol
            ? [{ class: 'qty discount', value: '' }]
            : this.ShowOnetimeDiscountCol
              ? [{ class: 'qty discount', value: '' }]
              : []),
         /*Promotional Discount*/ ...(this.ShowPromotionalCol
            ? [
                 {
                    class: 'qty discount',
                    value: formatDiscountPercent(
                       this.ProductPayrollMonthlyCosts[EINNumber]
                          .bundleDiscountedPEPMPercent,
                    ),
                 },
              ]
            : []),
         /*Applied Discount 1*/ ...(this.ShowAppliedDiscount1Col ? [{ class: 'qty discount', value: formatDiscountPercent(getDiscountAmountByCode(payCosts, this.TopAppliedDiscountCodes[0])) }] : []),
         /*Applied Discount 2*/ ...(this.ShowAppliedDiscount2Col ? [{ class: 'qty discount', value: formatDiscountPercent(getDiscountAmountByCode(payCosts, this.TopAppliedDiscountCodes[1])) }] : []),
         /*Applied Discount 3*/ ...(this.ShowAppliedDiscount3Col ? [{ class: 'qty discount', value: formatDiscountPercent(getDiscountAmountByCode(payCosts, this.TopAppliedDiscountCodes[2])) }] : []),
         /*Applied Discount 4*/ ...(this.ShowAppliedDiscount4Col ? [{ class: 'qty discount', value: formatDiscountPercent(getDiscountAmountByCode(payCosts, this.TopAppliedDiscountCodes[3])) }] : []),
         /*Applied Discount 5*/ ...(this.ShowAppliedDiscount5Col ? [{ class: 'qty discount', value: formatDiscountPercent(getDiscountAmountByCode(payCosts, this.TopAppliedDiscountCodes[4])) }] : []),
         /*Applied Discount 6*/ ...(this.ShowAppliedDiscount6Col ? [{ class: 'qty discount', value: formatDiscountPercent(getDiscountAmountByCode(payCosts, this.TopAppliedDiscountCodes[5])) }] : []),
         /*Applied Discount 7*/ ...(this.ShowAppliedDiscount7Col ? [{ class: 'qty discount', value: formatDiscountPercent(getDiscountAmountByCode(payCosts, this.TopAppliedDiscountCodes[6])) }] : []),
         /*Applied Discounts Summary*/ ...(this.ShowAppliedDiscountSummaryCol ? [{ class: 'qty discount', value: formatDiscountPercent(getOtherDiscountsSum(payCosts, this.TopAppliedDiscountCodes, this.MonthlyPdfExcludeAppliedFromOtherSum, { excludePrepaymentFamily: false })) }] : []),
         /*Total Price*/ ...(this.hasPEPMCols &&
         this.ProductPayrollEECounts[EINNumber] >= 25
            ? [
                 {
                    class: 'currency',
                    value:
                       formatFloatAsCurrencySymbol(
                          this.ProductPayrollMonthlyCosts[EINNumber].pdfDisplayTotal,
                          this.currencyObjectUSD,
                          true,
                       ) + '*',
                 },
              ]
            : this.hasPEPMCols && this.ProductPayrollEECounts[EINNumber] < 25
              ? [
                   {
                      class: 'currency',
                      value:
                         formatFloatAsCurrencySymbol(
                            this.ProductPayrollMonthlyCosts[EINNumber]
                               .pdfDisplayTotal,
                            this.currencyObjectUSD,
                            true,
                         ) + '**',
                   },
                ]
              : [
                   {
                      class: 'currency',
                      value:
                         formatFloatAsCurrencySymbol(
                            this.ProductPayrollMonthlyCosts[EINNumber]
                               .pdfDisplayTotal,
                            this.currencyObjectUSD,
                            true,
                         ) + '*',
                   },
                ]),
         /*Product Code*/ { value: 'payroll', class: 'productCode' },
      ]
   }

   generateProductPayrollAdditionalStateTaxRow(EINNumber) {
      return [
         //TODO: product list name is not good {class:" desc", value:this.prefix+this.product_list["states"]["ProductName"]+EINName, style:{textAlign:"left", paddingLeft:"2em"}},
         /*Product*/ {
            value: this.prefix + 'Additional State Tax Service',
            class: 'double-indent',
         },
         /*List Price*/ {
            class: 'currency',
            value: formatFloatAsCurrencySymbol(
               this.product_list['states']['EmpListPrice'],
               this.currencyObjectUSD,
               true,
            ),
         },
         /*Effective PEPM*/ ...(this.hasPEPMCols
            ? [{ class: 'currency', value: '' }]
            : []),
         /*Quantity*/ {
            class: 'qty',
            value: this.ProductPayrollStateCounts[EINNumber] - 1,
         },
         /*Months*/ { class: 'qty', value: this.MonthsSelected },
         /*Bundle Discount or Promotional Discount*/ ...(this.ShowOnetimeDiscountCol ||
         this.ShowDiscountCol
            ? [{ class: 'qty discount', value: '' }]
            : []),
         /*Applied Discount 1*/ ...(this.ShowAppliedDiscount1Col ? [{ class: 'qty discount', value: '' }] : []),
         /*Applied Discount 2*/ ...(this.ShowAppliedDiscount2Col ? [{ class: 'qty discount', value: '' }] : []),
         /*Applied Discount 3*/ ...(this.ShowAppliedDiscount3Col ? [{ class: 'qty discount', value: '' }] : []),
         /*Applied Discount 4*/ ...(this.ShowAppliedDiscount4Col ? [{ class: 'qty discount', value: '' }] : []),
         /*Applied Discount 5*/ ...(this.ShowAppliedDiscount5Col ? [{ class: 'qty discount', value: '' }] : []),
         /*Applied Discount 6*/ ...(this.ShowAppliedDiscount6Col ? [{ class: 'qty discount', value: '' }] : []),
         /*Applied Discount 7*/ ...(this.ShowAppliedDiscount7Col ? [{ class: 'qty discount', value: '' }] : []),
         /*Applied Discounts Summary*/ ...(this.ShowAppliedDiscountSummaryCol ? [{ class: 'qty discount', value: '' }] : []),
         /*Total Price*/ {
            class: 'currency',
            value: formatFloatAsCurrencySymbol(
               this.ProductPayrollMonthlyStateCosts[EINNumber].listPrice,
               this.currencyObjectUSD,
               true,
            ),
         },
         /*Product Code*/ { value: 'states', class: 'productCode' },
      ]
   }

   generateProductPayrollIntegrationsRow(EINNumber) {
      return [
         //TODO: product list name is not good {class:" desc", value:this.prefix+this.product_list["states"]["ProductName"]+EINName, style:{textAlign:"left", paddingLeft:"2em"}},
         /*Product*/ {
            value: this.prefix + 'Accounting General Ledger Integration',
            class: 'double-indent',
         },
         /*List Price*/ {
            class: 'currency',
            value: formatFloatAsCurrencySymbol(
               this.calculatedValues.productEINIntegrationsCosts[EINNumber]
                  .listPrice,
               this.currencyObjectUSD,
               true,
            ),
         },
         /*Effective PEPM*/ ...(this.hasPEPMCols
            ? [{ class: 'currency', value: '' }]
            : []),
         /*Quantity*/ {
            class: 'qty',
            value: this.calculatedValues.productEINIntegrationsCosts[EINNumber]
               .productEECount,
         },
         /*Months*/ { class: 'qty', value: this.MonthsSelected },
         /*Bundle Discount or Promotional Discount*/ ...(this.ShowOnetimeDiscountCol ||
         this.ShowDiscountCol
            ? [{ class: 'qty discount', value: '' }]
            : []),
         /*Applied Discount 1*/ ...(this.ShowAppliedDiscount1Col ? [{ class: 'qty discount', value: '' }] : []),
         /*Applied Discount 2*/ ...(this.ShowAppliedDiscount2Col ? [{ class: 'qty discount', value: '' }] : []),
         /*Applied Discount 3*/ ...(this.ShowAppliedDiscount3Col ? [{ class: 'qty discount', value: '' }] : []),
         /*Applied Discount 4*/ ...(this.ShowAppliedDiscount4Col ? [{ class: 'qty discount', value: '' }] : []),
         /*Applied Discount 5*/ ...(this.ShowAppliedDiscount5Col ? [{ class: 'qty discount', value: '' }] : []),
         /*Applied Discount 6*/ ...(this.ShowAppliedDiscount6Col ? [{ class: 'qty discount', value: '' }] : []),
         /*Applied Discount 7*/ ...(this.ShowAppliedDiscount7Col ? [{ class: 'qty discount', value: '' }] : []),
         /*Applied Discounts Summary*/ ...(this.ShowAppliedDiscountSummaryCol ? [{ class: 'qty discount', value: '' }] : []),
         /*Total Price*/ {
            class: 'currency',
            value: formatFloatAsCurrencySymbol(
               this.calculatedValues.productEINIntegrationsCosts[EINNumber]
                  .pdfDisplayTotal,
               this.currencyObjectUSD,
               true,
            ),
         },
         /*Product Code*/ {
            value: 'payrollIntegrations',
            class: 'productCode',
         },
      ]
   }

   generateAllPayrollRows() {
      let interlaced = []
      for (let i = 0; i < this.ProductPayrollEINCount; i++) {
         interlaced.push(this.ProductPayrollRows[i])
         interlaced.push(this.ProductPayrollStateRows[i])
         interlaced.push(this.ProductPayrollIntegrationsRows[i])
      }
      return interlaced
   }

   generateAlwaysUSDPayrollRows() {
      return this._generateAlwaysUSDRows('monthly')
   }

   generateAlwaysUSDPayrollOneTimeRows() {
      return this._generateAlwaysUSDRows('once')
   }

   _generateAlwaysUSDRows(billingFreq) {
      let rows = []
      for (const [productCode, count] of this.DynamicProducts) {
         const product = this.product_list[productCode]
         if (
            !product ||
            product?.NotInPricebook ||
            !isAlwaysUSD(productCode) ||
            count <= 0 ||
            product.BillingFreq !== billingFreq
         )
            continue

         const productCosts =
            this.calculatedValues?.selectedProductCosts?.[productCode]
         if (!productCosts) continue

         const displayCount = (() => {
            const rawCount =
               productCosts.productEECount > 0
                  ? productCosts.productEECount
                  : count
            switch (product.InputType) {
               case 'boolean':
                  return 1
               case 'integer':
                  return Math.floor(rawCount)
               case 'decimal':
                  return Number(rawCount.toFixed(2))
               default:
                  return rawCount
            }
         })()

         const row = [
            /*Product*/ {
               value: this.prefix + product?.ProductName,
               class: 'indent',
            },
            /*List Price*/ {
               class: 'currency',
               value: formatFloatAsCurrencySymbol(
                  productCosts.listPrice,
                  this.currencyObjectUSD,
                  true,
               ),
            },
            /*Effective PEPM*/ ...(this.hasPEPMCols
               ? [{ value: '', class: 'currency' }]
               : []),
            /*Quantity*/ { class: 'qty', value: displayCount },
            /*Months*/ {
               class: 'qty',
               value: billingFreq === 'monthly' ? this.MonthsSelected : '',
            },
            ...(billingFreq === 'once'
               ? [
                    ...this.oneTimeBundleDiscountSlotBodyCells(productCosts),
                    ...this.oneTimePostBundleDiscountBodyCells(productCosts),
                    ...this.oneTimeLineItemDiscountValueCells({ productCosts }),
                 ]
               : [
                    ...(this.ShowOnetimeDiscountCol ||
                    this.ShowDiscountCol
                       ? [{ class: 'qty discount', value: '' }]
                       : []),
                    ...(this.ShowAppliedDiscount1Col
                       ? [{ class: 'qty discount', value: '' }]
                       : []),
                    ...(this.ShowAppliedDiscount2Col
                       ? [{ class: 'qty discount', value: '' }]
                       : []),
                    ...(this.ShowAppliedDiscount3Col
                       ? [{ class: 'qty discount', value: '' }]
                       : []),
                    ...(this.ShowAppliedDiscount4Col
                       ? [{ class: 'qty discount', value: '' }]
                       : []),
                    ...(this.ShowAppliedDiscount5Col
                       ? [{ class: 'qty discount', value: '' }]
                       : []),
                    ...(this.ShowAppliedDiscount6Col
                       ? [{ class: 'qty discount', value: '' }]
                       : []),
                    ...(this.ShowAppliedDiscount7Col
                       ? [{ class: 'qty discount', value: '' }]
                       : []),
                    ...(this.ShowAppliedDiscountSummaryCol
                       ? [{ class: 'qty discount', value: '' }]
                       : []),
                 ]),
            /*Total Price*/ {
               class: 'currency',
               value: formatFloatAsCurrencySymbol(
                  productCosts.pdfDisplayTotal ?? productCosts.totalPrice,
                  this.currencyObjectUSD,
                  true,
               ),
            },
            /*Product Code*/ { value: productCode, class: 'productCode' },
         ]
         rows.push(row)
      }
      return rows
   }

   generateMonthlyTotalRow() {
      return [
         {
            class: 'totalLabel',
            value: 'Estimated Total Monthly Cost',
            style: { textAlign: 'left' },
         },
         { class: 'currency', value: '', style: { paddingRight: '1em' } },
         ...(this.hasPEPMCols ? [{ value: '', class: 'currency' }] : []),
         { class: 'qty', value: '' },
         { class: 'qty', value: '' },
         ...(this.ShowOnetimeDiscountCol ||
         this.ShowDiscountCol
            ? [{ class: 'qty discount', value: '' }]
            : []),
         ...(this.ShowAppliedDiscount1Col ? [{ class: 'qty discount', value: '' }] : []),
         ...(this.ShowAppliedDiscount2Col ? [{ class: 'qty discount', value: '' }] : []),
         ...(this.ShowAppliedDiscount3Col ? [{ class: 'qty discount', value: '' }] : []),
         ...(this.ShowAppliedDiscount4Col ? [{ class: 'qty discount', value: '' }] : []),
         ...(this.ShowAppliedDiscount5Col ? [{ class: 'qty discount', value: '' }] : []),
         ...(this.ShowAppliedDiscount6Col ? [{ class: 'qty discount', value: '' }] : []),
         ...(this.ShowAppliedDiscount7Col ? [{ class: 'qty discount', value: '' }] : []),
         ...(this.ShowAppliedDiscountSummaryCol ? [{ class: 'qty discount', value: '' }] : []),
         {
            class: 'currency productHeader',
            value: formatFloatAsCurrencySymbol(
               this.MonthlyGrandTotal,
               this.currencyObject,
               true,
            ),
         },
      ]
   }

   generatePayrollTotalRow() {
      return [
         {
            class: 'totalLabel',
            value: 'Estimated Payroll Monthly Cost',
            style: { textAlign: 'left' },
         },
         { class: 'currency', value: '', style: { paddingRight: '1em' } },
         ...(this.hasPEPMCols ? [{ value: '', class: 'currency' }] : []),
         { class: 'qty', value: '' },
         { class: 'qty', value: '' },
         ...(this.ShowOnetimeDiscountCol ||
         this.ShowDiscountCol
            ? [{ class: 'qty discount', value: '' }]
            : []),
         ...(this.ShowAppliedDiscount1Col ? [{ class: 'qty discount', value: '' }] : []),
         ...(this.ShowAppliedDiscount2Col ? [{ class: 'qty discount', value: '' }] : []),
         ...(this.ShowAppliedDiscount3Col ? [{ class: 'qty discount', value: '' }] : []),
         ...(this.ShowAppliedDiscount4Col ? [{ class: 'qty discount', value: '' }] : []),
         ...(this.ShowAppliedDiscount5Col ? [{ class: 'qty discount', value: '' }] : []),
         ...(this.ShowAppliedDiscount6Col ? [{ class: 'qty discount', value: '' }] : []),
         ...(this.ShowAppliedDiscount7Col ? [{ class: 'qty discount', value: '' }] : []),
         ...(this.ShowAppliedDiscountSummaryCol ? [{ class: 'qty discount', value: '' }] : []),
         {
            class: 'currency productHeader',
            value: formatFloatAsCurrencySymbol(
               (this.calculatedValues.payrollMonthlyTotal.totalPrice || 0) +
                  (this.calculatedValues.alwaysUSDMonthlyTotal?.totalPrice || 0),
               this.currencyObjectUSD,
               true,
            ),
         },
      ]
   }

   // generateNonprofitDiscountRow() {
   //     return(
   //         [
   //             //TODO: This doesn't have a nice name {class:" desc", value:this.prefix+this.discount_list['relatedEntity']["DiscountName"], style:{textAlign:"left", paddingLeft:"2em"}},
   //             {class:"bold desc", value:this.prefix+"Nonprofit Discount", style:{textAlign:"left", paddingLeft:"4em"}},
   //             {class:"currency", value:""},
   //             ... this.hasPEPMCols ? [{class:"currency", value:""}]: [],
   //             {class:"qty", value:""},
   //             {class:"qty", value:""},
   //             ... this.NumDiscountCols > 1? [{class:"qty discount", value:""}]:[],
   //             {class:"qty discount", value:formatDiscountPercent(this.discount_list['nonProfit']['DiscountAmount'])},
   //             {class:"currency discount", value:'($'+ this.calculatedValues.totalNonprofitDiscountAmount.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})+')'},
   //             ... this.DifferenceQuote? [{class:"currency col-bhr-blue", value:""}]:[],
   //             ... this.DifferenceQuote? [GenerateQuoteData.generateDifferenceObject(0)]:[],
   //         ]
   //     );
   // }

   // generatePrepaymentDiscountRow() {
   //     return(
   //         [
   //             //TODO: This doesn't have a nice name {class:" desc", value:this.prefix+this.discount_list['relatedEntity']["DiscountName"], style:{textAlign:"left", paddingLeft:"2em"}},
   //             {class:"bold desc", value:this.prefix+"Prepayment Discount", style:{textAlign:"left", paddingLeft:"4em"}},
   //             {class:"currency", value:""},
   //             ... this.hasPEPMCols ? [{class:"currency", value:""}]: [],
   //             {class:"qty", value:""},
   //             {class:"qty", value:""},
   //             ... this.NumDiscountCols > 1? [{class:"qty discount", value:""}]:[],
   //             {class:"qty discount", value:formatDiscountPercent(this.discount_list[this.DiscountPrepaymentCode]['DiscountAmount'])},
   //             {class:"currency discount", value:'($'+ this.calculatedValues.totalPrepaymentDiscountAmount.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})+')'},
   //             ... this.DifferenceQuote? [{class:"currency col-bhr-blue", value:""}]:[],
   //             ... this.DifferenceQuote? [GenerateQuoteData.generateDifferenceObject(0)]:[],
   //         ]
   //     );
   // }

   generateTotalItems() {
      let prepaymentAmount = this.MonthsSelected >= 12 ? 12 : 1
      let totalItems = [
         ...(this.CoreProductSelected != null &&
         this.CoreProductSelected !== 'core0' &&
         this.ProductCoreEECount > 0 &&
         !this.product_list[this.CoreProductSelected]?.NotInPricebook
            ? [
                 {
                    productCode:
                       this.prefix +
                       this.product_list[this.CoreProductSelected][
                          'ProductCode'
                       ],
                    label:
                       this.prefix +
                       this.product_list[this.CoreProductSelected][
                          'ProductName'
                       ],
                    value: formatFloatAsCurrencySymbol(
                       this.calculatedValues.productCoreMonthlyCosts.listPrice *
                          prepaymentAmount,
                       this.currencyObject,
                       true,
                    ),
                 },
              ]
            : []),
         ...(this.HasBenAdminSelected &&
         !this.product_list['benAdmin']?.NotInPricebook
            ? [
                 {
                    productCode:
                       this.prefix +
                       this.product_list['benAdmin']['ProductCode'],
                    label:
                       this.prefix +
                       this.product_list['benAdmin']['ProductName'],
                    value: formatFloatAsCurrencySymbol(
                       this.calculatedValues
                          .productBenefitsAdministrationMonthlyCosts.listPrice *
                          prepaymentAmount,
                       this.currencyObject,
                       true,
                    ),
                 },
              ]
            : []),
         ...(this.ProductPerformanceEECount > 0 &&
         !this.product_list['performanceMan']?.NotInPricebook
            ? [
                 {
                    productCode:
                       this.prefix +
                       this.product_list['performanceMan']['ProductCode'],
                    label:
                       this.prefix +
                       this.product_list['performanceMan']['ProductName'],
                    value: formatFloatAsCurrencySymbol(
                       this.calculatedValues.productPerformanceMonthlyCosts
                          .listPrice * prepaymentAmount,
                       this.currencyObject,
                       true,
                    ),
                 },
              ]
            : []),
         ...(() => {
            const isAttendance = this.TimeProductSelected === 'timeAttendance'
            const activeCode = isAttendance ? 'timeAttendance' : 'timeTracking'
            const activeEECount = isAttendance
               ? this.ProductTimeAttendanceEECount
               : this.ProductTimeTrackingEECount
            const activeCosts = isAttendance
               ? this.calculatedValues.productTimeAttendanceMonthlyCosts
               : this.calculatedValues.productTimeTrackingMonthlyCosts
            if (
               !activeEECount ||
               activeEECount <= 0 ||
               this.product_list[activeCode]?.NotInPricebook
            ) {
               return []
            }
            return [
               {
                  productCode:
                     this.prefix + this.product_list[activeCode]['ProductCode'],
                  label: this.prefix + this.getTimeProductRowDisplayName(),
                  value: formatFloatAsCurrencySymbol(
                     activeCosts.listPrice * prepaymentAmount,
                     this.currencyObject,
                     true,
                  ),
               },
            ]
         })(),
         ...(this.HasTimeClocks &&
         this.product_list['clockMonthlySupport'] &&
         !this.product_list['clockMonthlySupport']?.NotInPricebook &&
         (this.ProductTimeClockDetails?.length ?? 0) > 0
            ? [
                 {
                    productCode:
                       this.prefix +
                       this.product_list['clockMonthlySupport']['ProductCode'],
                    label:
                       this.prefix +
                       this.product_list['clockMonthlySupport']['ProductName'],
                    value: formatFloatAsCurrencySymbol(
                       this.calculatedValues.TimeClockMonthlyCost.listPrice *
                          prepaymentAmount,
                       this.currencyObject,
                       true,
                    ),
                 },
              ]
            : []),
         ...(this.ProductAdditionalJobsCount > 0 &&
         !this.product_list['addJobPosting']?.NotInPricebook
            ? [
                 {
                    productCode:
                       this.prefix +
                       this.product_list['addJobPosting']['ProductCode'],
                    label:
                       this.prefix +
                       this.product_list['addJobPosting']['ProductName'],
                    value: formatFloatAsCurrencySymbol(
                       this.calculatedValues.AdditionalJobsCosts.listPrice *
                          prepaymentAmount,
                       this.currencyObject,
                       true,
                    ),
                 },
              ]
            : []),
         ...(this.ProductAdditionalStorageCount > 0 &&
         !this.product_list['addStorage']?.NotInPricebook
            ? [
                 {
                    productCode:
                       this.prefix +
                       this.product_list['addStorage']['ProductCode'],
                    label:
                       this.prefix +
                       this.product_list['addStorage']['ProductName'],
                    value: formatFloatAsCurrencySymbol(
                       this.calculatedValues.AdditionalStorageCosts.listPrice *
                          prepaymentAmount,
                       this.currencyObject,
                       true,
                    ),
                 },
              ]
            : []),
         ...(this.ProductTestAccount &&
         !this.product_list['testAccount']?.NotInPricebook
            ? [
                 {
                    productCode:
                       this.prefix +
                       this.product_list['testAccount']['ProductCode'],
                    label:
                       this.prefix +
                       this.product_list['testAccount']['ProductName'],
                    value: formatFloatAsCurrencySymbol(
                       this.calculatedValues.TestAccountCosts.listPrice *
                          prepaymentAmount,
                       this.currencyObject,
                       true,
                    ),
                 },
              ]
            : []),
      ]
      for (const [productCode, count] of this.DynamicProducts) {
         // timeAttendance is accounted for on the fixed time row, not here.
         if (productCode === 'timeAttendance') continue
         if (count > 0) {
            // AlwaysUSD products are in the payroll totals (monthly) or should not appear in annual costs (one-time)
            if (isAlwaysUSD(productCode))
               continue
            // Only monthly-billed products belong in the Monthly Costs summary;
            // one-time products (e.g. setup fees) are surfaced separately in the
            // Due at Activation section (see generateActivationTotalItems).
            if (this.product_list[productCode]?.BillingFreq !== 'monthly')
               continue
            totalItems.push({
               productCode:
                  this.prefix + this.product_list[productCode]['ProductCode'],
               label:
                  this.prefix + this.product_list[productCode]['ProductName'],
               value: formatFloatAsCurrencySymbol(
                  this.calculatedValues.selectedProductCosts[productCode]
                     .listPrice * prepaymentAmount,
                  this.currencyObject,
                  true,
               ),
            })
         }
      }
      return totalItems
   }

   generatePayrollTotalItems() {
      let prepaymentAmount = this.MonthsSelected >= 12 ? 12 : 1
      return [
         ...(this.HasPayrollSelected &&
         this.calculatedValues.MonthlyTaxCosts.listPrice > 0
            ? [
                 {
                    productCode: 'monthlyTax',
                    label: this.prefix + 'Monthly Tax',
                    value: formatFloatAsCurrencySymbol(
                       this.calculatedValues.MonthlyTaxCosts.listPrice *
                          prepaymentAmount,
                       this.currencyObjectUSD,
                       true,
                    ),
                 },
              ]
            : []),
         ...(this.HasPayrollSelected
            ? [
                 {
                    productCode: this.prefix + 'payroll',
                    label: this.prefix + 'Payroll',
                    value: formatFloatAsCurrencySymbol(
                       this.calculatedValues.productPayrollMonthlyTotalCosts
                          .listPrice * prepaymentAmount,
                       this.currencyObjectUSD,
                       true,
                    ),
                 },
              ]
            : []),
         ...(this.HasPayrollSelected &&
         this.calculatedValues.productPayrollMonthlyStateTotalCosts.listPrice >
            0
            ? [
                 {
                    productCode: 'states',
                    label: this.prefix + 'Additional State Tax Service',
                    value: formatFloatAsCurrencySymbol(
                       this.calculatedValues
                          .productPayrollMonthlyStateTotalCosts.listPrice *
                          prepaymentAmount,
                       this.currencyObjectUSD,
                       true,
                    ),
                 },
              ]
            : []),
         ...(this.HasPayrollSelected &&
         this.calculatedValues.productEINIntegrationsTotalCosts.listPrice > 0
            ? [
                 {
                    productCode: 'states',
                    label:
                       this.prefix + 'Accounting General Ledger Integration',
                    value: formatFloatAsCurrencySymbol(
                       this.calculatedValues.productEINIntegrationsTotalCosts
                          .listPrice * prepaymentAmount,
                       this.currencyObjectUSD,
                       true,
                    ),
                 },
              ]
            : []),
         // AlwaysUSD dynamic products billed with payroll (monthly only for annual costs)
         ...(() => {
            const items = []
            for (const [productCode, count] of this.DynamicProducts) {
               if (count > 0 && isAlwaysUSD(productCode)) {
                  const product = this.product_list[productCode]
                  const productCosts = this.calculatedValues?.selectedProductCosts?.[productCode]
                  // Only include monthly AlwaysUSD products in payroll total items
                  // One-time products should appear in the regular one-time section, not annual costs
                  if (product && productCosts && productCosts.listPrice > 0 && product.BillingFreq === 'monthly') {
                     items.push({
                        productCode: productCode,
                        label: this.prefix + product.ProductName,
                        value: formatFloatAsCurrencySymbol(
                           productCosts.listPrice * prepaymentAmount,
                           this.currencyObjectUSD,
                           true,
                        ),
                     })
                  }
               }
            }
            return items
         })(),
      ]
   }

   generateHRMonthlyTotal() {
      return isNaN(this.calculatedValues.hrMonthlyTotal.pdfDisplayTotal)
         ? 0
         : this.calculatedValues.hrMonthlyTotal.pdfDisplayTotal
   }

   generatePayrollMonthlyTotal() {
      return isNaN(this.calculatedValues.payrollMonthlyTotal.pdfDisplayTotal)
         ? 0
         : this.calculatedValues.payrollMonthlyTotal.pdfDisplayTotal
   }

   generateTotalDiscount() {
      return (
         (this.calculatedValues.monthlyTotal.totalDiscount /
            this.calculatedValues.monthlyTotal.listPrice) *
         100
      )
   }

   /**
    * Checks if any product has a prepayment discount (DiscountFamily === 'Prepayment Discount') applied.
    * Used for one-time table layout and `getOtherDiscountsSum` when a dedicated prepayment slot exists there.
    * @returns {boolean} True if any product has a prepayment discount applied
    */
   hasPrepaymentDiscountApplied() {
      if (!this.calculatedValues) return false

      // List of monthly product cost objects to check
      const monthlyProducts = [
         this.calculatedValues.productCoreMonthlyCosts,
         this.calculatedValues.productBenefitsAdministrationMonthlyCosts,
         this.calculatedValues.productTimeTrackingMonthlyCosts,
         this.calculatedValues.productPerformanceMonthlyCosts,
         this.calculatedValues.TimeClockMonthlyCost,
      ]

      // Check each monthly product for a prepayment discount
      for (const product of monthlyProducts) {
         if (product?.appliedDiscounts?.length > 0) {
            const hasPrepayDiscount = product.appliedDiscounts.some(
               (discount) => discount.DiscountFamily === 'Prepayment Discount'
            )
            if (hasPrepayDiscount) return true
         }
      }

      // Check dynamic products
      if (this.calculatedValues.MonthlyDynamicCosts?.length > 0) {
         for (const product of this.calculatedValues.MonthlyDynamicCosts) {
            if (product?.appliedDiscounts?.length > 0) {
               const hasPrepayDiscount = product.appliedDiscounts.some(
                  (discount) => discount.DiscountFamily === 'Prepayment Discount'
               )
               if (hasPrepayDiscount) return true
            }
         }
      }

      // Check payroll products
      if (this.ProductPayrollMonthlyCosts) {
         for (const einNumber in this.ProductPayrollMonthlyCosts) {
            const product = this.ProductPayrollMonthlyCosts[einNumber]
            if (product?.appliedDiscounts?.length > 0) {
               const hasPrepayDiscount = product.appliedDiscounts.some(
                  (discount) => discount.DiscountFamily === 'Prepayment Discount'
               )
               if (hasPrepayDiscount) return true
            }
         }
      }

      return false
   }

   generateTotalDiscountItems() {
      let prepaymentAmount = this.MonthsSelected >= 12 ? 12 : 1
      let discountItems = []
      // Hero Discount Rows
      if (
         this.PromotionalDiscounts &&
         this.discount_list &&
         Object.keys(this.PromotionalDiscounts).length > 0 &&
         Object.keys(this.discount_list).length > 0
      ) {
         for (const discount in this.PromotionalDiscounts) {
            if (
               discount in this.discount_list &&
               this.discount_list[discount]?.HeroDiscount
            ) {
               let discountLabel = this.discount_list[discount]['DiscountName']
               let discountPercent = 0
               let discountAmount = 0
               for (const product of this.discount_list[
                  discount
               ]?.ApplyTo.split(',')) {
                  let priceObject = getPriceObjectFromProductCode(
                     this.calculatedValues,
                     product,
                  )
                  const isApplied = priceObject &&
                     findObjectByKeyValue(
                        priceObject?.appliedDiscounts,
                        'DiscountCode',
                        discount,
                     )
                  // For number input discounts with custom values, display even if not in applied discounts
                  const hasCustomValue = this.PromotionalDiscounts?.numberDiscountValues?.[discount] > 0

                  if (isApplied || (hasCustomValue && this.discount_list[discount]?.InputType === 'Number')) {
                     if (discountLabel?.includes('$PRODUCTNAME')) {
                        discountLabel = discountLabel.replace(
                           '$PRODUCTNAME',
                           this.product_list[product]?.ProductName,
                        )
                     }
                     if (
                        this.discount_list[discount]['BillingFreq'] ===
                        'monthly'
                     )
                        discountAmount =
                           getPropertyOfActualEffectOnProduct(
                              this.calculatedValues.hrMonthlyTotal,
                              discount,
                              'changeInDollars',
                           ) * prepaymentAmount
                     else if(this.discount_list[discount]['BillingFreq'] === 'once') {
                        discountAmount = getPropertyOfActualEffectOnProduct(this.calculatedValues.oneTimeTotal, discount, 'changeInDollars');
                        // For fixed/flatrate discounts with custom number values, use the entered value
                        if (
                           this.discount_list[discount]['DiscountType'] !== 'percent' &&
                           this.PromotionalDiscounts?.numberDiscountValues?.[discount]
                        ) {
                           discountAmount = this.PromotionalDiscounts.numberDiscountValues[discount];
                        }
                     }
                     if (
                        this.discount_list[discount]['DiscountType'] ===
                        'percent'
                     )
                        discountPercent =
                           this.PromotionalDiscounts?.numberDiscountValues?.[discount] ?? this.discount_list[discount]['DiscountAmount']
                     else {
                        const totalPrice =
                           this.discount_list[discount]['BillingFreq'] === 'once'
                              ? (this?.calculatedValues?.oneTimeTotal?.totalPrice ?? 0)
                              : (this?.calculatedValues?.hrMonthlyTotal?.totalPrice ?? 0)
                        discountPercent =
                           totalPrice > 0
                              ? (discountAmount / totalPrice) * 100
                              : 0
                     }
                     discountItems.push({
                        label: discountLabel,
                        discount: formatFloatAsCurrencySymbol(
                           discountAmount,
                           this.currencyObject,
                        ),
                        discountPercent: discountPercent,
                     })
                     break
                  }
               }
            }
         }
      }
      // Effective Discount Row
      if (this.ShowEffectiveDiscountOnQuote) {
         //TODO: calculate this properly for 6 month or 3 month terms
         if (
            this.MonthsSelected < 12 &&
            this.calculatedValues.monthlyTotal.totalDiscount > 0 &&
            this.calculatedValues.monthlyTotal.totalDiscountPercent > 0
         ) {
            discountItems.push({
               label: 'Effective Total Discount',
               discount: formatFloatAsCurrencySymbol(
                  this.calculatedValues.monthlyTotal.pdfDisplayTotalDiscount *
                     prepaymentAmount,
                  this.currencyObject,
               ),
               discountPercent:
                  this.calculatedValues.monthlyTotal.pdfDisplayTotalDiscountPercent,
            })
            //Use new improved annual total discount calculation for 12+ month terms
         } else if (
            this.MonthsSelected >= 12 &&
            this.calculatedValues.annualTotal.totalDiscount > 0 &&
            this.calculatedValues.annualTotal.totalDiscountPercent > 0
         ) {
            discountItems.push({
               label: 'Effective Total Discount',
               discount: formatFloatAsCurrencySymbol(
                  this.calculatedValues.annualTotal.totalDiscount,
                  this.currencyObject,
               ),
               discountPercent:
                  this.calculatedValues.annualTotal.totalDiscountPercent,
            })
         }
      }
      return discountItems
   }

   generatePayrollTotalDiscountItems() {
      let prepaymentAmount = this.MonthsSelected >= 12 ? 12 : 1
      let discountItems = []
      // Hero Discount Rows
      if (
         this.PromotionalDiscounts &&
         this.discount_list &&
         Object.keys(this.PromotionalDiscounts).length > 0 &&
         Object.keys(this.discount_list).length > 0
      ) {
         for (const discount in this.PromotionalDiscounts) {
            if (
               discount in this.discount_list &&
               this.discount_list[discount]?.HeroDiscount &&
               this.discount_list[discount]['ApplyTo']?.includes('payroll')
            ) {
               let discountLabel = this.discount_list[discount]['DiscountName']
               let discountPercent = 0
               let discountAmount = 0
               let priceObject = getPriceObjectFromProductCode(
                  this.calculatedValues,
                  'payroll',
               )
               const isApplied = findObjectByKeyValue(
                  priceObject?.appliedDiscounts,
                  'DiscountCode',
                  discount,
               )
               // For number input discounts with custom values, display even if not in applied discounts
               const hasCustomValue = this.PromotionalDiscounts?.numberDiscountValues?.[discount] > 0

               if (isApplied || (hasCustomValue && this.discount_list[discount]?.InputType === 'Number')) {
                  if (discountLabel?.includes('$PRODUCTNAME')) {
                     discountLabel = discountLabel.replace(
                        '$PRODUCTNAME',
                        this.product_list['payroll']?.ProductName,
                     )
                  }
                  discountAmount =
                     getPropertyOfActualEffectOnProduct(
                        priceObject,
                        discount,
                        'changeInDollars',
                     ) * prepaymentAmount
                  // For fixed discounts with custom number values, use the entered value
                  if (
                     !isApplied &&
                     this.discount_list[discount]['DiscountType'] !== 'percent' &&
                     hasCustomValue
                  ) {
                     discountAmount = this.PromotionalDiscounts.numberDiscountValues[discount];
                  }
                  discountPercent =
                     this.discount_list[discount]['DiscountType'] === 'percent'
                        ? (this.PromotionalDiscounts?.numberDiscountValues?.[discount] ?? this.discount_list[discount]['DiscountAmount'])
                        : (this.calculatedValues.totalPrice > 0
                           ? (discountAmount / this.calculatedValues.totalPrice) * 100
                           : 0)
                  discountItems.push({
                     label: discountLabel,
                     discount: formatFloatAsCurrencySymbol(
                        discountAmount * prepaymentAmount,
                        this.currencyObject,
                     ),
                     discountPercent: discountPercent,
                  })
                  break
               }
            }
         }
      }
      // Effective Discount Row - includes payroll + alwaysUSD combined discounts
      const combinedPayrollDiscount =
         (this.calculatedValues.payrollMonthlyTotal.totalDiscount || 0) +
         (this.calculatedValues.alwaysUSDMonthlyTotal?.totalDiscount || 0)
      const combinedPayrollListPrice =
         (this.calculatedValues.payrollMonthlyTotal.listPrice || 0) +
         (this.calculatedValues.alwaysUSDMonthlyTotal?.listPrice || 0)
      const combinedPayrollDiscountPercent =
         combinedPayrollListPrice > 0
            ? (combinedPayrollDiscount / combinedPayrollListPrice) * 100
            : 0
      if (
         this.ShowEffectiveDiscountOnQuote &&
         combinedPayrollDiscount > 0 &&
         combinedPayrollDiscountPercent > 0
      ) {
         discountItems.push({
            label: 'Effective Total Payroll Discount',
            discount: formatFloatAsCurrencySymbol(
               combinedPayrollDiscount * prepaymentAmount,
               this.currencyObjectUSD,
            ),
            discountPercent: combinedPayrollDiscountPercent,
         })
      }
      return discountItems
   }

   generateActivationTotalItems() {
      let proratedFraction = 1
      let firstLabel = ''
      let prorationLabel = ''
      //Only show monthly products if the monthly difference is greater than 0 (not including payroll) on a difference quote

      const {
         proratedCoreTotal,
         proratedBenefitsTotal,
         proratedMonthlyTaxTotal,
         proratedPerformanceTotal,
         proratedTimeTrackingTotal,
         proratedTimeAttendanceTotal,
         impTotalsPrice,
         payrollImplementationTotal,
         historicalDataImportTotal,
         marketplaceListingTotal,
         sponsorshipTotal,
         proratedAdditionalJobsTotal,
         proratedAdditionalStorageTotal,
         testAccountTotal,
         paidServicesTotal,
         timeClocksTotal,
         consultingServicesTotal,
         accountBalanceTotal,
         dynamicProductTotals,
      } = calculateActivationItemTotals(
         this.product_list,
         this.discount_list,
         this.pricebookId,
         this.QuoteType,
         this.CoreProductSelected,
         this.ProductCoreEECount,
         this.ProductPerformanceEECount,
         this.ProductTimeTrackingEECount,
         this.HasTimeClocks,
         this.ProductTimeClockDetails,
         this.ProductBenefitsAdministrationEECount,
         this.ProductPayrollEINCount,
         this.ProductPayrollEECount,
         this.ProductPayrollEECounts,
         this.ProductPayrollTotalStateCount,
         this.ProductPayrollStateCounts,
         this.ProfessionalServices,
         this.ConsultingServices,
         this.ImpType,
         this.BenAdminImpType,
         this.PayrollImpType,
         this.DiscountBundle,
         this.DiscountNonProfit,
         this.PrepayLength, // Use PrepayLength instead of DiscountPrepaymentCode
         this.DiscountMultiEntity,
         this.DiscountHRMonthlyPromotion,
         this.DiscountHRDealDeskMonthly,
         this.DiscountReseller,
         this.DiscountQuickstartTraining,
         this.DiscountImpPromotion,
         this.DiscountDealDeskHRImp,
         this.DiscountDealDeskPayrollImp,
         this.DiscountSecondEIN,
         this.DiscountGroupImp,
         this.DiscountDealDeskHRImpRate,
         this.DiscountDealDeskQuickstartImpRate,
         this.DiscountDealDeskPayrollImpRate,
         this.ProductAdditionalStorageCount,
         this.ProductAdditionalJobsCount,
         this.ProductHistoricalDataImport,
         this.ProductMarketplaceListing,
         this.ProductSponsorship,
         this.ProductPaidServicesAmount,
         this.ProductTestAccount,
         this.QuoteActivationDate,
         this.DifferenceQuote,
         this.AccountSnowflake,
         this.CoreCountryCount,
         this.PromotionalDiscounts,
         this.NextInvoiceDate,
         this.currencyObject,
         this.AccountBalance,
         this.MonthlyTax,
         this.ProductPayrollEINIntegrations,
         this.AllPermissions,
         this.calculationUserRole,
         this.AccountStatus,
         this.pricebooks,
         this.previousCurrencyObject,
         this.ProductPayrollEINImpTypes,
         this.prepaymentActivationUseDifference,
         this.DynamicProducts, // DynamicProducts
         this.TimeProductSelected,
         this.IsATSOnly,
      )
      let prorated = isProrated(this.QuoteActivationDate, this.NextInvoiceDate, this.DifferenceQuote, this.AccountStatus)
      if (prorated) {
         proratedFraction =
            this.calculatedValues.proratedActivationDetails
               .daysToPayAfterActivation /
            this.calculatedValues.proratedActivationDetails.totalDaysInMonth

         prorationLabel = ` (Prorated)`
      } else {
         if (this.MonthsSelected === 1) firstLabel = 'First Month of '
         else if (this.MonthsSelected === 3)
            firstLabel = 'First Three Months of '
         else if (this.MonthsSelected === 4)
            firstLabel = 'First Four Months of '
         else if (this.MonthsSelected === 6) firstLabel = 'First Six Months of '
         else if (this.MonthsSelected === 9)
            firstLabel = 'First Nine Months of '
         else if (this.MonthsSelected === 12) firstLabel = 'First Year of '
         else if (this.MonthsSelected === 24) firstLabel = 'First Two Years of '
         else if (this.MonthsSelected === 36)
            firstLabel = 'First Three Years of '
      }

      const payrollImpType =
         this.PayrollImpType === 'foundations'
            ? 'payrollImpFoundations'
            : 'payrollImp'
      const ImpNotInPricebook =
         this.product_list[`${this.CoreProductSelected}Imp`]?.NotInPricebook ||
         this.product_list[`${this.CoreProductSelected}Quickstart`]
            ?.NotInPricebook
      let impName = ''
      if (
         this.ImpType === 'full' ||
         this.BenAdminImpType == 'full' ||
         this.ImpType == 'premier'
      )
         impName = 'HR Implementation'
      else if (this.ImpType === 'quickstart') impName = 'QuickStart Training'

      let MonthlyActivationItems = [
         ...(this.CoreProductSelected != null &&
         this.CoreProductSelected !== 'core0' &&
         this.HasMonthlyServices &&
         proratedCoreTotal > 0
            ? [
                 {
                    productCode: this.CoreProductSelected,
                    label:
                       firstLabel +
                       this.product_list[this.CoreProductSelected][
                          'ProductName'
                       ] +
                       prorationLabel,
                    value: formatFloatAsCurrencySymbol(
                       proratedCoreTotal,
                       this.currencyObject,
                       true,
                    ),
                 },
              ]
            : []),
         ...(this.ProductBenefitsAdministrationEECount > 0 &&
         this.HasMonthlyServices &&
         proratedBenefitsTotal > 0
            ? [
                 {
                    productCode: 'benAdmin',
                    label:
                       firstLabel +
                       this.product_list['benAdmin']['ProductName'] +
                       prorationLabel,
                    value: formatFloatAsCurrencySymbol(
                       proratedBenefitsTotal,
                       this.currencyObject,
                       true,
                    ),
                 },
              ]
            : []),
         ...(this.TimeProductSelected === 'timeAttendance' &&
         this.ProductTimeAttendanceEECount > 0 &&
         proratedTimeAttendanceTotal > 0 &&
         this.HasMonthlyServices
            ? [
                 {
                    productCode: 'timeAttendance',
                    label:
                       firstLabel +
                       this.getTimeProductRowDisplayName() +
                       prorationLabel,
                    value: formatFloatAsCurrencySymbol(
                       proratedTimeAttendanceTotal,
                       this.currencyObject,
                       true,
                    ),
                 },
              ]
            : []),
         ...(this.TimeProductSelected !== 'timeAttendance' &&
         this.ProductTimeTrackingEECount > 0 &&
         proratedTimeTrackingTotal > 0 &&
         this.HasMonthlyServices
            ? [
                 {
                    productCode: 'timeTracking',
                    label:
                       firstLabel +
                       this.getTimeProductRowDisplayName() +
                       prorationLabel,
                    value: formatFloatAsCurrencySymbol(
                       proratedTimeTrackingTotal,
                       this.currencyObject,
                       true,
                    ),
                 },
              ]
            : []),
         ...(this.HasTimeClocks &&
         this.product_list['clockMonthlySupport'] &&
         (this.ProductTimeClockDetails?.length ?? 0) > 0 &&
         timeClocksTotal > 0 &&
         this.HasMonthlyServices
            ? [
                 {
                    productCode: 'clockMonthlySupport',
                    label:
                       firstLabel +
                       this.product_list['clockMonthlySupport']['ProductName'],
                    value: formatFloatAsCurrencySymbol(
                       timeClocksTotal,
                       this.currencyObject,
                       true,
                    ),
                 },
              ]
            : []),
         ...(this.ProductAdditionalJobsCount > 0 &&
         proratedAdditionalJobsTotal > 0 &&
         this.HasMonthlyServices
            ? [
                 {
                    productCode: 'addJobPosting',
                    label:
                       firstLabel + 'Additional Job Postings' + prorationLabel,
                    value: formatFloatAsCurrencySymbol(
                       proratedAdditionalJobsTotal,
                       this.currencyObject,
                       true,
                    ),
                 },
              ]
            : []),
         ...(this.ProductAdditionalStorageCount > 0 &&
         proratedAdditionalStorageTotal > 0 &&
         this.HasMonthlyServices
            ? [
                 {
                    productCode: 'addStorage',
                    label:
                       firstLabel +
                       'Additional Storage (10GB Each)' +
                       prorationLabel,
                    value: formatFloatAsCurrencySymbol(
                       proratedAdditionalStorageTotal,
                       this.currencyObject,
                       true,
                    ),
                 },
              ]
            : []),
         ...(this.ProductTestAccount &&
         testAccountTotal > 0 &&
         this.HasMonthlyServices
            ? [
                 {
                    productCode: 'testAccount',
                    label: firstLabel + 'Test Account' + prorationLabel,
                    value: formatFloatAsCurrencySymbol(
                       testAccountTotal,
                       this.currencyObject,
                       true,
                    ),
                 },
              ]
            : []),
         ...(this.MonthlyTax > 0 &&
         proratedMonthlyTaxTotal > 0 &&
         this.HasMonthlyServices &&
         !this.product_list['monthlyTax']?.NotInPricebook
            ? [
                 {
                    productCode: 'monthlyTax',
                    label:
                       firstLabel +
                       this.product_list['monthlyTax']['ProductName'] +
                       prorationLabel,
                    value: formatFloatAsCurrencySymbol(
                       proratedMonthlyTaxTotal,
                       this.currencyObject,
                       true,
                    ),
                 },
              ]
            : []),
         ...(this.ProductPerformanceEECount > 0 &&
         proratedPerformanceTotal > 0 &&
         this.HasMonthlyServices
            ? [
                 {
                    productCode: 'performanceMan',
                    label: firstLabel + 'Performance' + prorationLabel,
                    value: formatFloatAsCurrencySymbol(
                       proratedPerformanceTotal,
                       this.currencyObject,
                       true,
                    ),
                 },
              ]
            : []),
      ]
      const sortedProducts = Array.from(this.DynamicProducts.keys()).sort(
         (a, b) => {
            const orderA = this.product_list[a]?.DisplayOrder || 999999
            const orderB = this.product_list[b]?.DisplayOrder || 999999
            return orderA - orderB // Sort from low to high
         },
      )

      let DynamicMonthlyActivationItems = []
      for (const productCode of sortedProducts) {
         const product = this.product_list[productCode] ?? null
         if (
            product &&
            product.BillingFreq === 'monthly' &&
            dynamicProductTotals.has(productCode) &&
            product.IncludeInActivation
         ) {
            DynamicMonthlyActivationItems.push({
               productCode: productCode,
               label:
                  firstLabel + (product?.ProductName ?? '') + prorationLabel,
               value: formatFloatAsCurrencySymbol(
                  dynamicProductTotals.get(productCode),
                  this.currencyObject,
                  true,
               ),
            })
         }
      }

      let OneTimeActivationItems = [
         ...(!ImpNotInPricebook &&
         ((this.ImpType && this.ImpType !== 'selfSetup') ||
            (this.BenAdminImpType && this.BenAdminImpType !== 'none')) &&
         this.HasOneTimeServices
            ? [
                 {
                    productCode:
                       this.ImpType === 'quickstart'
                          ? `${this.CoreProductSelected}Quickstart`
                          : `${this.CoreProductSelected}Imp`,
                    label: impName,
                    value: formatFloatAsCurrencySymbol(
                       this.calculatedValues.hrImplementationCosts.totalPrice,
                       this.currencyObject,
                       true,
                    ),
                 },
              ]
            : []),
         ...(!this.product_list['benAdminImp']?.NotInPricebook &&
         (this.BenAdminImpType && this.BenAdminImpType !== 'none') &&
         this.HasOneTimeServices
            ? [
                 {
                    productCode: 'benAdminImp',
                    label: 'Benefits Administration Implementation',
                    value: formatFloatAsCurrencySymbol(
                       this.calculatedValues.benefitsAdministrationImplementationCosts.totalPrice,
                       this.currencyObject,
                       true,
                    ),
                 },
              ]
            : []),
         ...(this.IncludePayrollImpInActivation &&
         this.HasOneTimeServices &&
         !this.product_list[payrollImpType]?.NotInPricebook
            ? [
                 {
                    productCode: payrollImpType,
                    label: 'Payroll Implementation',
                    value: formatFloatAsCurrencySymbol(
                       payrollImplementationTotal,
                       this.currencyObject,
                       true,
                    ),
                 },
              ]
            : []),
         ...(this.ProductHistoricalDataImport &&
         !this.product_list['historicalDataImport']?.NotInPricebook &&
         this.HasOneTimeServices
            ? [
                 {
                    productCode: 'historicalDataImport',
                    label: this.product_list['historicalDataImport'][
                       'ProductName'
                    ],
                    value: formatFloatAsCurrencySymbol(
                       historicalDataImportTotal,
                       this.currencyObject,
                       true,
                    ),
                 },
              ]
            : []),
         ...(this.ProductMarketplaceListing &&
         !this.product_list['marketplaceListing']?.NotInPricebook &&
         this.HasOneTimeServices
            ? [
                 {
                    productCode: 'marketplaceListing',
                    label: this.product_list['marketplaceListing'][
                       'ProductName'
                    ],
                    value: formatFloatAsCurrencySymbol(
                       marketplaceListingTotal,
                       this.currencyObject,
                       true,
                    ),
                 },
              ]
            : []),
         ...(this.ProductSponsorship &&
         !this.product_list['sponsorship']?.NotInPricebook &&
         this.HasOneTimeServices
            ? [
                 {
                    productCode: 'sponsorship',
                    label: this.product_list['sponsorship']['ProductName'],
                    value: formatFloatAsCurrencySymbol(
                       sponsorshipTotal,
                       this.currencyObject,
                       true,
                    ),
                 },
              ]
            : []),
         ...(this.ProductPaidServicesAmount > 0 &&
         !this.product_list['paidService']?.NotInPricebook &&
         this.HasOneTimeServices
            ? [
                 {
                    productCode: 'paidService',
                    label: 'Paid Services',
                    value: formatFloatAsCurrencySymbol(
                       paidServicesTotal -
                          (this.calculatedValues.ConsultingServicesCosts.reduce(
                             (acc, val) => acc + (val.totalPrice ?? 0),
                             0,
                          ) +
                             this.calculatedValues.ProfessionalServicesCosts.reduce(
                                (acc, val) => acc + (val.totalPrice ?? 0),
                                0,
                             )),
                       this.currencyObject,
                       true,
                    ),
                 },
              ]
            : []),
         ...(this.AccountBalance > 0 &&
         !this.product_list['accountBalance']?.NotInPricebook &&
         this.HasOneTimeServices
            ? [
                 {
                    productCode: 'accountBalance',
                    label: 'Account Balance',
                    value: formatFloatAsCurrencySymbol(
                       accountBalanceTotal,
                       this.currencyObject,
                       true,
                    ),
                 },
              ]
            : []),
         ...(this.HasTimeClocks &&
         (this.ProductTimeClockDetails?.length ?? 0) > 0 &&
         this.HasOneTimeServices
            ? [
                 {
                    productCode: 'timeClock',
                    label: 'Time Clocks',
                    value: formatFloatAsCurrencySymbol(
                       this.calculatedValues.TimeClockCosts.reduce(
                          (acc, val) => acc + val.pdfDisplayTotal,
                          0,
                       ),
                       this.currencyObject,
                       true,
                    ),
                 },
              ]
            : []),
         ...(this.ProfessionalServices.length > 0 && this.HasOneTimeServices
            ? [
                 {
                    productCode: 'professionalServices',
                    label: 'Professional Services',
                    value: formatFloatAsCurrencySymbol(
                       this.calculatedValues.ProfessionalServicesCosts.reduce(
                          (acc, val) => acc + (val.totalPrice ?? 0),
                          0,
                       ),
                       this.currencyObject,
                       true,
                    ),
                 },
              ]
            : []),
         ...(this.ConsultingServices.length > 0 && this.HasOneTimeServices
            ? [
                 {
                    productCode: 'consultingServices',
                    label: 'Consulting Services',
                    value: formatFloatAsCurrencySymbol(
                       this.calculatedValues.ConsultingServicesCosts.reduce(
                          (acc, val) => acc + (val.totalPrice ?? 0),
                          0,
                       ),
                       this.currencyObject,
                       true,
                    ),
                 },
              ]
            : []),
      ]
      let DynamicOneTimeActivationItems = []
      for (const productCode of sortedProducts) {
         const product = this.product_list[productCode] ?? null
         if (
            product &&
            product.BillingFreq === 'once' &&
            dynamicProductTotals.has(productCode)
         ) {
            DynamicOneTimeActivationItems.push({
               productCode: productCode,
               label: product?.ProductName ?? '',
               value: formatFloatAsCurrencySymbol(
                  dynamicProductTotals.get(productCode),
                  this.currencyObject,
                  true,
               ),
            })
         }
      }
      return MonthlyActivationItems.concat(
         DynamicMonthlyActivationItems,
         OneTimeActivationItems,
         DynamicOneTimeActivationItems,
      )
   }

   /** Bundle / discount placeholder column (same visibility as generateMonthlyHeader). */
   /**
    * Whether the bundle-slot column exists and should be repurposed to show
    * the first one-time discount name (left-to-right fill).
    */
   get _hasBundleSlotForOneTimeDiscounts() {
      return this.ShowOnetimeDiscountCol
   }

   /**
    * Returns an array of {name, code} for every non-summary discount slot in the
    * one-time table, ordered left-to-right (bundle slot first when present).
    * One-time discount names fill from the right so that when the monthly table
    * has more discount columns, the one-time discounts appear right-aligned.
    */
   _oneTimeDiscountSlotAssignments() {
      const hasBundleSlot = this._hasBundleSlotForOneTimeDiscounts
      const slotCount =
         (hasBundleSlot ? 1 : 0) +
         (this.ShowPromotionalCol ? 1 : 0) +
         (this.hasDiscountPrepaymentCol ? 1 : 0) +
         (this.ShowAppliedDiscount1Col ? 1 : 0) +
         (this.ShowAppliedDiscount2Col ? 1 : 0) +
         (this.ShowAppliedDiscount3Col ? 1 : 0) +
         (this.ShowAppliedDiscount4Col ? 1 : 0) +
         (this.ShowAppliedDiscount5Col ? 1 : 0) +
         (this.ShowAppliedDiscount6Col ? 1 : 0) +
         (this.ShowAppliedDiscount7Col ? 1 : 0)
      const oneTimeCount = this.TopAppliedDiscountCodesOneTime.length
      let fillableIdx = 0
      return Array.from({ length: slotCount }, () => {
         const otIdx = fillableIdx++
         return {
            name: otIdx < oneTimeCount ? (this.TopAppliedDiscountNamesOneTime[otIdx] ?? '') : '',
            code: otIdx < oneTimeCount ? (this.TopAppliedDiscountCodesOneTime[otIdx] ?? null) : null,
         }
      })
   }

   oneTimeBundleDiscountSlotHeaderCells() {
      const w = { '--width': `${this.productDiscountColumnWidth}%` }
      if (this.ShowDiscountCol)
         return [
            {
               value: 'Discount',
               class: 'tableHeader2 qty productListPriceColumnHeader',
               style: w,
            },
         ]
      if (this.ShowOnetimeDiscountCol)
         return [
            {
               value: this._oneTimeDiscountSlotAssignments()[0]?.name ?? '',
               class: 'tableHeader2 qty productListPriceColumnHeader',
               style: w,
            },
         ]
      return []
   }

   oneTimeBundleDiscountSlotBodyCells(productCosts = null) {
      if (this.ShowOnetimeDiscountCol) {
         if (productCosts) {
            const code = this._oneTimeDiscountSlotAssignments()[0]?.code ?? null
            const value = code ? formatDiscountPercent(getDiscountAmountByCode(productCosts, code)) : ''
            return [{ class: 'qty discount', value }]
         }
         return [{ class: 'qty discount', value: '' }]
      }
      if (this.ShowDiscountCol)
         return [{ class: 'qty discount', value: '' }]
      return []
   }

   /** Columns after bundle through applied summary — order matches generateMonthlyHeader.
    *  Emits a header cell for every visible AD column so the OT table aligns with Monthly.
    *  Slot data fills left-to-right; visible columns beyond OT's discount count show empty headers. */
   oneTimePostBundleDiscountHeaderCells() {
      const w = { '--width': `${this.productDiscountColumnWidth}%` }
      const disc = 'tableHeader2 qty productDiscountColumnHeader'
      const slots = this._oneTimeDiscountSlotAssignments()
      let slotIdx = this._hasBundleSlotForOneTimeDiscounts ? 1 : 0
      const nextName = () => slots[slotIdx++]?.name ?? ''
      return [
         ...(this.ShowPromotionalCol ? [{ value: nextName(), class: disc, style: w }] : []),
         ...(this.hasDiscountPrepaymentCol ? [{ value: nextName(), class: disc, style: w }] : []),
         ...(this.ShowAppliedDiscount1Col ? [{ value: nextName(), class: disc, style: w }] : []),
         ...(this.ShowAppliedDiscount2Col ? [{ value: nextName(), class: disc, style: w }] : []),
         ...(this.ShowAppliedDiscount3Col ? [{ value: nextName(), class: disc, style: w }] : []),
         ...(this.ShowAppliedDiscount4Col ? [{ value: nextName(), class: disc, style: w }] : []),
         ...(this.ShowAppliedDiscount5Col ? [{ value: nextName(), class: disc, style: w }] : []),
         ...(this.ShowAppliedDiscount6Col ? [{ value: nextName(), class: disc, style: w }] : []),
         ...(this.ShowAppliedDiscount7Col ? [{ value: nextName(), class: disc, style: w }] : []),
         ...(this.ShowAppliedDiscountSummaryCol ? [{ value: 'Other\nDiscounts', class: disc, style: w }] : []),
      ]
   }

   oneTimePostBundleDiscountEmptyBodyCells() {
      return [
         ...(this.ShowPromotionalCol ? [{ class: 'qty discount', value: '' }] : []),
         ...(this.hasDiscountPrepaymentCol ? [{ class: 'qty discount', value: '' }] : []),
         ...(this.ShowAppliedDiscount1Col ? [{ class: 'qty discount', value: '' }] : []),
         ...(this.ShowAppliedDiscount2Col ? [{ class: 'qty discount', value: '' }] : []),
         ...(this.ShowAppliedDiscount3Col ? [{ class: 'qty discount', value: '' }] : []),
         ...(this.ShowAppliedDiscount4Col ? [{ class: 'qty discount', value: '' }] : []),
         ...(this.ShowAppliedDiscount5Col ? [{ class: 'qty discount', value: '' }] : []),
         ...(this.ShowAppliedDiscount6Col ? [{ class: 'qty discount', value: '' }] : []),
         ...(this.ShowAppliedDiscount7Col ? [{ class: 'qty discount', value: '' }] : []),
         ...(this.ShowAppliedDiscountSummaryCol ? [{ class: 'qty discount', value: '' }] : []),
      ]
   }

   oneTimePostBundleDiscountBodyCells(productCosts) {
      const slots = this._oneTimeDiscountSlotAssignments()
      let slotIdx = this._hasBundleSlotForOneTimeDiscounts ? 1 : 0
      const getSlotValue = () => {
         const slot = slots[slotIdx++]
         return slot?.code ? formatDiscountPercent(getDiscountAmountByCode(productCosts, slot.code)) : ''
      }
      return [
         ...(this.ShowPromotionalCol ? [{ class: 'qty discount', value: getSlotValue() }] : []),
         ...(this.hasDiscountPrepaymentCol ? [{ class: 'qty discount', value: getSlotValue() }] : []),
         ...(this.ShowAppliedDiscount1Col ? [{ class: 'qty discount', value: getSlotValue() }] : []),
         ...(this.ShowAppliedDiscount2Col ? [{ class: 'qty discount', value: getSlotValue() }] : []),
         ...(this.ShowAppliedDiscount3Col ? [{ class: 'qty discount', value: getSlotValue() }] : []),
         ...(this.ShowAppliedDiscount4Col ? [{ class: 'qty discount', value: getSlotValue() }] : []),
         ...(this.ShowAppliedDiscount5Col ? [{ class: 'qty discount', value: getSlotValue() }] : []),
         ...(this.ShowAppliedDiscount6Col ? [{ class: 'qty discount', value: getSlotValue() }] : []),
         ...(this.ShowAppliedDiscount7Col ? [{ class: 'qty discount', value: getSlotValue() }] : []),
         ...(this.ShowAppliedDiscountSummaryCol
            ? [{ class: 'qty discount', value: formatDiscountPercent(getOtherDiscountsSum(productCosts, this.TopAppliedDiscountCodesOneTime, (this.ShowAppliedDiscount2Col && this.BundleInAD2) ? ['bundleDiscount'] : undefined, { excludePrepaymentFamily: this.hasDiscountPrepaymentCol })) }]
            : []),
      ]
   }

   /** One-time-only column before Total (line / aggregate discount). */
   oneTimeLineItemDiscountHeaderCells() {
      return []
   }

   /**
    * @param {object} [opts]
    * @param {object} [opts.productCosts]
    * @param {number|null} [opts.discountPercent] — overrides productCosts.totalDiscountPercent when HasOnetimeDiscount
    * @param {boolean} [opts.forTotalsRow] — estimated one-time row: show aggregate % when NumDiscountCols only
    */
   oneTimeLineItemDiscountValueCells() {
      return []
   }

   generateOneTimeHeader() {
      return [
         /*Product*/ {
            value: 'Service',
            class: 'tableHeader2 productNameColumnHeader',
            style: { '--width': `${this.productNameColumnWidth}%` },
         },
         /*List Price*/ {
            value: 'List Price',
            class: 'tableHeader2 currency productListPriceColumnHeader',
            style: { '--width': `${this.productLPColumnWidth}%` },
         },
         /*Effective PEPM*/ ...(this.hasPEPMCols
            ? [
                 {
                    value: '',
                    class: 'tableHeader2 productPEPMColumnHeader',
                    style: { '--width': `${this.productPEPMColumnWidth}%` },
                 },
              ]
            : []),
         /*Quantity*/ {
            value: 'Quantity',
            class: 'tableHeader2 qty productQuantityColumnHeader',
            style: { '--width': `${this.productQuantityColumnWidth}%` },
         },
         /*Months*/ {
            value: '',
            class: 'tableHeader2 qty productMonthsColumnHeader',
            style: { '--width': `${this.productMonthsColumnWidth}%` },
         },
         ...this.oneTimeBundleDiscountSlotHeaderCells(),
         ...this.oneTimePostBundleDiscountHeaderCells(),
         ...this.oneTimeLineItemDiscountHeaderCells(),
         /*Total Price*/ {
            value: `Total (${this.currencyObject.Code})`,
            class: 'tableHeader2 currency productTotalColumnHeader',
            style: { '--width': `${this.productTotalColumnWidth}%` },
         },
      ]
   }

   generateOneTimeProductSectionHeader(sectionTitle) {
      return [
         /*Product*/ {
            class: 'productHeader',
            value: sectionTitle,
            style: { textAlign: 'left', verticalAlign: 'bottom' },
         },
         /*List Price*/ { class: 'currency', value: '' },
         /*Effective PEPM*/ ...(this.hasPEPMCols
            ? [{ value: '', class: 'currency' }]
            : []),
         /*Quantity*/ { class: 'qty', value: '' },
         /*Months*/ { class: 'qty', value: '' },
         ...this.oneTimeBundleDiscountSlotBodyCells(),
         ...this.oneTimePostBundleDiscountEmptyBodyCells(),
         ...this.oneTimeLineItemDiscountValueCells(),
         /*Total Price*/ { class: 'currency bold', value: '' },
      ]
   }

   generateTimeClocksProductHeader() {
      return this.generateOneTimeProductSectionHeader('Time Clocks')
   }

   generateOneTimeLineItemProductRow(lineItemCost) {
      return [
         /*Product*/ {
            value:
               this.prefix +
               this.product_list[lineItemCost.productCode]['ProductName'],
            class: 'indent',
         },
         /*List Price*/ {
            class: 'currency',
            value: formatFloatAsCurrencySymbol(
               lineItemCost.listPrice,
               this.currencyObject,
               true,
            ),
         },
         /*Effective PEPM*/ ...(this.hasPEPMCols
            ? [{ value: '', class: 'currency' }]
            : []),
         /*Quantity*/ { class: 'qty', value: lineItemCost.productEECount },
         /*Months*/ { class: 'qty', value: '' }, //Months
         ...this.oneTimeBundleDiscountSlotBodyCells(lineItemCost),
         ...this.oneTimePostBundleDiscountBodyCells(lineItemCost),
         ...this.oneTimeLineItemDiscountValueCells({ productCosts: lineItemCost }),
         /*Total Price*/ {
            class: 'currency',
            value: formatFloatAsCurrencySymbol(
               lineItemCost.pdfDisplayTotal,
               this.currencyObject,
               true,
            ),
         },
      ]
   }

   generateTimeClockProductRow(clockCost) {
      return this.generateOneTimeLineItemProductRow(clockCost)
   }

   generateConsultingServicesHeader() {
      return this.generateOneTimeProductSectionHeader('Consulting Services')
   }

   generateConsultingServicesRow(serviceCost) {
      return this.generateOneTimeLineItemProductRow(serviceCost)
   }

   generateProfessionalServicesHeader() {
      return this.generateOneTimeProductSectionHeader('Professional Services')
   }

   generateProfessionalServicesRow(serviceCost) {
      return this.generateOneTimeLineItemProductRow(serviceCost)
   }

   generateHRImplementationRow() {
      if (
         this.product_list[`${this.CoreProductSelected}Imp`]?.NotInPricebook ||
         this.product_list[`${this.CoreProductSelected}Quickstart`]
            ?.NotInPricebook
      )
         return

      let impName = ''
      let productCode = ''
      if (this.ImpType === 'full') {
         impName = 'HR Implementation'
         productCode = `${this.CoreProductSelected}Imp` // e.g., "core1Imp"
      } else if (this.ImpType === 'premier') {
         impName = `${this.product_list[`${this.CoreProductSelected}`]['ProductName']} Premier Implementation`
         productCode = `${this.CoreProductSelected}PremierImp` // e.g., "core1PremierImp"
      } else if (this.ImpType === 'quickstart') {
         impName = 'QuickStart Training'
         productCode = `${this.CoreProductSelected}Quickstart` // e.g., "core1Quickstart"
      }

      let impTotalDiscountPercent = (this.calculatedValues.hrImplementationCosts.totalDiscount / this.calculatedValues.hrImplementationCosts.listPrice) * 100

      return [
         //TODO: this shows weird names {class:"desc", value:product_list[CoreProductSelected+"Imp"]["ProductName"], style:{textAlign:"left"}},
         /*Product*/ { value: this.prefix + impName, class: 'indent' },
         /*List Price*/ {
            class: 'currency',
            value: formatFloatAsCurrencySymbol(
               this.calculatedValues.hrImplementationCosts.listPrice,
               this.currencyObject,
               true,
            ),
         },
         /*Effective PEPM*/ ...(this.hasPEPMCols
            ? [{ value: '', class: 'currency' }]
            : []),
         /*Quantity*/ { class: 'qty', value: '1' },
         /*Months*/ { class: 'qty', value: '' }, //Months
         ...this.oneTimeBundleDiscountSlotBodyCells(this.calculatedValues.hrImplementationCosts),
         ...this.oneTimePostBundleDiscountBodyCells(
            this.calculatedValues.hrImplementationCosts,
         ),
         ...this.oneTimeLineItemDiscountValueCells({
            productCosts: this.calculatedValues.hrImplementationCosts,
            discountPercent: impTotalDiscountPercent,
         }),
         /*Total Price*/ {
            class: 'currency',
            value: formatFloatAsCurrencySymbol(
               this.calculatedValues.hrImplementationCosts.pdfDisplayTotal,
               this.currencyObject,
               true,
            ),
         },
         /*Product Code*/ { value: productCode, class: 'productCode' },
      ]
   }

   generateBenAdminImplementationRow() {
      if (this.product_list['benAdminImp']?.NotInPricebook) return

      let impTotalDiscountPercent = (this.calculatedValues.benefitsAdministrationImplementationCosts.totalDiscount / this.calculatedValues.benefitsAdministrationImplementationCosts.listPrice) * 100

      return [
         //TODO: this shows weird names {class:"desc", value:product_list[CoreProductSelected+"Imp"]["ProductName"], style:{textAlign:"left"}},
         /*Product*/ { value: this.prefix + "Benefits Administration Implementation", class: 'indent' },
         /*List Price*/ {
            class: 'currency',
            value: formatFloatAsCurrencySymbol(
               this.calculatedValues.benefitsAdministrationImplementationCosts.listPrice,
               this.currencyObject,
               true,
            ),
         },
         /*Effective PEPM*/ ...(this.hasPEPMCols
            ? [{ value: '', class: 'currency' }]
            : []),
         /*Quantity*/ { class: 'qty', value: '1' },
         /*Months*/ { class: 'qty', value: '' }, //Months
         ...this.oneTimeBundleDiscountSlotBodyCells(this.calculatedValues.benefitsAdministrationImplementationCosts),
         ...this.oneTimePostBundleDiscountBodyCells(
            this.calculatedValues.benefitsAdministrationImplementationCosts,
         ),
         ...this.oneTimeLineItemDiscountValueCells({
            productCosts: this.calculatedValues.benefitsAdministrationImplementationCosts,
            discountPercent: impTotalDiscountPercent,
         }),
         /*Total Price*/ {
            class: 'currency',
            value: formatFloatAsCurrencySymbol(
               this.calculatedValues.benefitsAdministrationImplementationCosts.pdfDisplayTotal,
               this.currencyObject,
               true,
            ),
         },
         /*Product Code*/ { value: "benAdminImp", class: 'productCode' },
      ]
   }

   generatePayrollImplementationRow() {
      const payrollImpType =
         this.PayrollImpType === 'foundations'
            ? 'payrollImpFoundations'
            : 'payrollImp'
      // Returns nothing if the product is not in the pricebook
      if (this.product_list[payrollImpType]?.NotInPricebook) return
      return [
         //TODO: this shows weird names {class:"desc", value:product_list[CoreProductSelected+"Imp"]["ProductName"], style:{textAlign:"left"}},
         /*Product*/ {
            value:
               this.prefix + this.PayrollImpType === 'foundations'
                  ? 'Payroll Foundations Implementation'
                  : 'Payroll Implementation',
            class: 'indent',
         },
         /*List Price*/ {
            class: 'currency',
            value: formatFloatAsCurrencySymbol(
               this.calculatedValues.payrollImplementationTotalCosts.listPrice,
               this.currencyObject,
               true,
            ),
         },
         /*Effective PEPM*/ ...(this.hasPEPMCols
            ? [{ value: '', class: 'currency' }]
            : []),
         /*Quantity*/ { class: 'qty', value: '1' },
         /*Months*/ { class: 'qty', value: '' }, //Months
         ...this.oneTimeBundleDiscountSlotBodyCells(this.calculatedValues.payrollImplementationTotalCosts),
         ...this.oneTimePostBundleDiscountBodyCells(
            this.calculatedValues.payrollImplementationTotalCosts,
         ),
         ...this.oneTimeLineItemDiscountValueCells({
            productCosts: this.calculatedValues.payrollImplementationTotalCosts,
         }),
         /*Total Price*/ {
            class: 'currency',
            value: formatFloatAsCurrencySymbol(
               this.calculatedValues.payrollImplementationTotalCosts.pdfDisplayTotal,
               this.currencyObject,
               true,
            ),
         },
         /*Product Code*/ { value: payrollImpType, class: 'productCode' },
      ]
   }

   generateAccountBalanceRow() {
      // Returns nothing if the product is not in the pricebook
      if (this.product_list['accountBalance']?.NotInPricebook) return
      return [
         //TODO: this shows weird names {class:"desc", value:product_list[CoreProductSelected+"Imp"]["ProductName"], style:{textAlign:"left"}},
         /*Product*/ {
            value: this.prefix + 'Account Balance',
            class: 'indent',
         },
         /*List Price*/ {
            class: 'currency',
            value: formatFloatAsCurrencySymbol(
               this.calculatedValues.AccountBalanceCosts.listPrice,
               this.currencyObject,
               true,
            ),
         },
         /*Effective PEPM*/ ...(this.hasPEPMCols
            ? [{ value: '', class: 'currency' }]
            : []),
         /*Quantity*/ { class: 'qty', value: '1' },
         /*Months*/ { class: 'qty', value: '' }, //Months
         ...this.oneTimeBundleDiscountSlotBodyCells(this.calculatedValues.AccountBalanceCosts),
         ...this.oneTimePostBundleDiscountBodyCells(
            this.calculatedValues.AccountBalanceCosts,
         ),
         ...this.oneTimeLineItemDiscountValueCells({
            productCosts: this.calculatedValues.AccountBalanceCosts,
         }),
         /*Total Price*/ {
            class: 'currency',
            value: formatFloatAsCurrencySymbol(
               this.calculatedValues.AccountBalanceCosts.pdfDisplayTotal,
               this.currencyObject,
               true,
            ),
         },
         /*Product Code*/ { value: 'accountBalance', class: 'productCode' },
      ]
   }

   generateMonthlyTaxRow() {
      // Returns nothing if the product is not in the pricebook
      if (this.product_list['monthlyTax']?.NotInPricebook) return
      return [
         //TODO: this shows weird names {class:"desc", value:product_list[CoreProductSelected+"Imp"]["ProductName"], style:{textAlign:"left"}},
         /*Product*/ { value: this.prefix + 'Monthly Tax', class: 'indent' },
         /*List Price*/ {
            class: 'currency',
            value: formatFloatAsCurrencySymbol(
               this.calculatedValues.MonthlyTaxCosts.listPrice,
               this.currencyObject,
               true,
            ),
         },
         /*Effective PEPM*/ ...(this.hasPEPMCols
            ? [{ value: '', class: 'currency' }]
            : []),
         /*Quantity*/ { class: 'qty', value: '1' },
         /*Months*/ { class: 'qty', value: this.MonthsSelected }, //Months
         /*Bundle Discount*/ ...(this.ShowOnetimeDiscountCol ||
         this.ShowDiscountCol
            ? [{ class: 'qty discount', value: '' }]
            : []),
         /*Applied Discount 1*/ ...(this.ShowAppliedDiscount1Col ? [{ class: 'qty discount', value: '' }] : []),
         /*Applied Discount 2*/ ...(this.ShowAppliedDiscount2Col ? [{ class: 'qty discount', value: '' }] : []),
         /*Applied Discount 3*/ ...(this.ShowAppliedDiscount3Col ? [{ class: 'qty discount', value: '' }] : []),
         /*Applied Discount 4*/ ...(this.ShowAppliedDiscount4Col ? [{ class: 'qty discount', value: '' }] : []),
         /*Applied Discount 5*/ ...(this.ShowAppliedDiscount5Col ? [{ class: 'qty discount', value: '' }] : []),
         /*Applied Discount 6*/ ...(this.ShowAppliedDiscount6Col ? [{ class: 'qty discount', value: '' }] : []),
         /*Applied Discount 7*/ ...(this.ShowAppliedDiscount7Col ? [{ class: 'qty discount', value: '' }] : []),
         /*Applied Discounts Summary*/ ...(this.ShowAppliedDiscountSummaryCol ? [{ class: 'qty discount', value: '' }] : []),
         /*Total Price*/ {
            class: 'currency',
            value: formatFloatAsCurrencySymbol(
               this.calculatedValues.MonthlyTaxCosts.pdfDisplayTotal,
               this.currencyObject,
               true,
            ),
         },
         /*Product Code*/ { value: 'monthlyTax', class: 'productCode' },
      ]
   }

   generateProductHistoricalDataImportRow() {
      //  Returns nothing if the product is not in the pricebook
      if (this.product_list['historicalDataImport']?.NotInPricebook) return
      return [
         /*Product*/ {
            value:
               this.prefix +
               this.product_list['historicalDataImport']['ProductName'],
            class: 'indent',
         },
         /*List Price*/ {
            class: 'currency',
            value: formatFloatAsCurrencySymbol(
               this.calculatedValues.HistoricalDataImportCosts.listPrice,
               this.currencyObject,
               true,
            ),
         },
         /*Effective PEPM*/ ...(this.hasPEPMCols
            ? [{ class: 'currency', value: '' }]
            : []),
         /*Quantity*/ { class: 'qty', value: '1' },
         /*Months*/ { class: 'qty', value: '' },
         ...this.oneTimeBundleDiscountSlotBodyCells(this.calculatedValues.HistoricalDataImportCosts),
         ...this.oneTimePostBundleDiscountBodyCells(
            this.calculatedValues.HistoricalDataImportCosts,
         ),
         ...this.oneTimeLineItemDiscountValueCells({
            productCosts: this.calculatedValues.HistoricalDataImportCosts,
         }),
         /*Total Price*/ {
            class: 'currency',
            value: formatFloatAsCurrencySymbol(
               this.calculatedValues.HistoricalDataImportCosts.pdfDisplayTotal,
               this.currencyObject,
               true,
            ),
         },
         /*Product Code*/ {
            value: 'historicalDataImport',
            class: 'productCode',
         },
      ]
   }

   generateProductMarketplaceListingRow() {
      // Returns nothing if the product is not in the pricebook
      if (this.product_list['marketplaceListing']?.NotInPricebook) return
      return [
         /*Product*/ {
            value:
               this.prefix +
               this.product_list['marketplaceListing']['ProductName'],
            class: 'indent',
         },
         /*List Price*/ {
            class: 'currency',
            value: formatFloatAsCurrencySymbol(
               this.calculatedValues.MarketplaceListingCosts.listPrice,
               this.currencyObject,
               true,
            ),
         },
         /*Effective PEPM*/ ...(this.hasPEPMCols
            ? [{ class: 'currency', value: '' }]
            : []),
         /*Quantity*/ { class: 'qty', value: '1' },
         /*Months*/ { class: 'qty', value: '' },
         ...this.oneTimeBundleDiscountSlotBodyCells(this.calculatedValues.MarketplaceListingCosts),
         ...this.oneTimePostBundleDiscountBodyCells(
            this.calculatedValues.MarketplaceListingCosts,
         ),
         ...this.oneTimeLineItemDiscountValueCells({
            productCosts: this.calculatedValues.MarketplaceListingCosts,
         }),
         /*Total Price*/ {
            class: 'currency',
            value: formatFloatAsCurrencySymbol(
               this.calculatedValues.MarketplaceListingCosts.pdfDisplayTotal,
               this.currencyObject,
               true,
            ),
         },
         /*Product Code*/ { value: 'marketplaceListing', class: 'productCode' },
      ]
   }

   generateProductSponsorshipRow() {
      // Returns nothing if the product is not in the pricebook
      if (this.product_list['sponsorship']?.NotInPricebook) return
      return [
         /*Product*/ {
            value:
               this.prefix + this.product_list['sponsorship']['ProductName'],
            class: 'indent',
         },
         /*List Price*/ {
            class: 'currency',
            value: formatFloatAsCurrencySymbol(
               this.calculatedValues.SponsorshipCosts.listPrice,
               this.currencyObject,
               true,
            ),
         },
         /*Effective PEPM*/ ...(this.hasPEPMCols
            ? [{ class: 'currency', value: '' }]
            : []),
         /*Quantity*/ { class: 'qty', value: '1' },
         /*Months*/ { class: 'qty', value: '' },
         ...this.oneTimeBundleDiscountSlotBodyCells(this.calculatedValues.SponsorshipCosts),
         ...this.oneTimePostBundleDiscountBodyCells(
            this.calculatedValues.SponsorshipCosts,
         ),
         ...this.oneTimeLineItemDiscountValueCells({
            productCosts: this.calculatedValues.SponsorshipCosts,
         }),
         /*Total Price*/ {
            class: 'currency',
            value: formatFloatAsCurrencySymbol(
               this.calculatedValues.SponsorshipCosts.pdfDisplayTotal,
               this.currencyObject,
               true,
            ),
         },
         /*Product Code*/ { value: 'sponsorship', class: 'productCode' },
      ]
   }

   generateProductPaidServicesRow() {
      // Returns nothing if the product is not in the pricebook
      if (this.product_list['paidService']?.NotInPricebook) return
      return [
         //TODO: this shows weird names{class:" desc", value:this.prefix+this.product_list["paidService"]["ProductName"], style:{textAlign:"left", paddingLeft:"2em"}},
         /*Product*/ { value: this.prefix + 'Paid Services', class: 'indent' },
         /*List Price*/ {
            class: 'currency',
            value: formatFloatAsCurrencySymbol(
               this.calculatedValues.PaidServiceCosts.listPrice -
                  (this.calculatedValues.ConsultingServicesCosts.reduce(
                     (acc, val) => acc + (val.pdfDisplayTotal ?? 0),
                     0,
                  ) +
                     this.calculatedValues.ProfessionalServicesCosts.reduce(
                        (acc, val) => acc + (val.pdfDisplayTotal ?? 0),
                        0,
                     )),
               this.currencyObject,
               true,
            ),
         },
         /*Effective PEPM*/ ...(this.hasPEPMCols
            ? [{ class: 'currency', value: '' }]
            : []),
         /*Quantity*/ { class: 'qty', value: '1' },
         /*Months*/ { class: 'qty', value: '' },
         ...this.oneTimeBundleDiscountSlotBodyCells(this.calculatedValues.PaidServiceCosts),
         ...this.oneTimePostBundleDiscountBodyCells(
            this.calculatedValues.PaidServiceCosts,
         ),
         ...this.oneTimeLineItemDiscountValueCells({
            productCosts: this.calculatedValues.PaidServiceCosts,
         }),
         /*Total Price*/ {
            class: 'currency',
            value: formatFloatAsCurrencySymbol(
               this.calculatedValues.PaidServiceCosts.pdfDisplayTotal -
                  (this.calculatedValues.ConsultingServicesCosts.reduce(
                     (acc, val) => acc + (val.pdfDisplayTotal ?? 0),
                     0,
                  ) +
                     this.calculatedValues.ProfessionalServicesCosts.reduce(
                        (acc, val) => acc + (val.pdfDisplayTotal ?? 0),
                        0,
                     )),
               this.currencyObject,
               true,
            ),
         },
         /*Product Code*/ { value: 'paidService', class: 'productCode' },
      ]
   }

   generateOneTimeTotalRow() {
      return [
         //TODO: this shows weird names {class:"desc", value:product_list[coreProductSelected+"Imp"]["ProductName"], style:{textAlign:"left"}},
         {
            class: 'totalLabel',
            value: 'Estimated One-Time Cost',
            style: { textAlign: 'left' },
         },
         ...(this.OneTimeTotalDiscountPercent > 0
            ? [
                 {
                    class: 'currency',
                    value: formatFloatAsCurrencySymbol(
                       this.OneTimeTotalListPrice,
                       this.currencyObject,
                    ),
                 },
              ]
            : [{ class: 'currency', value: '' }]),
         ...(this.hasPEPMCols ? [{ value: '', class: 'currency' }] : []),
         { class: 'qty', value: '' },
         { class: 'qty', value: '' },
         ...this.oneTimeBundleDiscountSlotBodyCells(),
         ...this.oneTimePostBundleDiscountEmptyBodyCells(),
         ...this.oneTimeLineItemDiscountValueCells({ forTotalsRow: true }),
         {
            class: 'currency productHeader',
            value: formatFloatAsCurrencySymbol(
               this.OneTimeTotal,
               this.currencyObject,
               true,
            ),
         },
      ]
   }

   generateDiffQuoteHeader() {
      let currencyCode = this.currencyObject.Code
      if (
         this.IsForeignCurrencyQuote &&
         this.HasPayrollSelected &&
         !this.HasHRProducts &&
         this.calculatedValues.hrMonthlyTotal.previousTotalPrice > 0
      )
         currencyCode = this.currencyObjectUSD.Code

      return [
         /*Product*/ {
            value: 'Product',
            class: 'tableHeader2 productNameColumnHeader',
         },
         /*New*/ {
            value: 'New',
            class: 'tableHeader2 currency productListPriceColumnHeader',
         },
         /*Previous*/ {
            value: 'Previous',
            class: 'tableHeader2 currency col-bhr-blue productListPriceColumnHeader',
         },
         /*Difference*/ ...(this.IsForeignCurrencyQuote
            ? [
                 {
                    value: `Difference (${currencyCode})`,
                    class: 'tableHeader2 currency productTotalColumnHeader',
                 },
              ]
            : [
                 {
                    value: 'Difference',
                    class: 'tableHeader2 currency productTotalColumnHeader',
                 },
              ]),
      ]
   }

   generateDiffRows() {
      function formatDiffPriceDifference(
         totalPriceDifference,
         currencyObject,
         total = false,
      ) {
         if (totalPriceDifference > 0) {
            if (total)
               return /*Difference*/ {
                  class: 'productHeader currency',
                  value: formatFloatAsCurrencySymbol(
                     totalPriceDifference,
                     currencyObject,
                     true,
                  ),
               }
            else
               return /*Difference*/ {
                  class: 'currency',
                  value: formatFloatAsCurrencySymbol(
                     totalPriceDifference,
                     currencyObject,
                     true,
                  ),
               }
         } else {
            return /*Difference*/ {
               class: 'currency discount',
               value: formatFloatAsCurrencySymbol(
                  totalPriceDifference,
                  currencyObject,
                  true,
               ),
            }
         }
      }

      let diff_rows = [
         /*Row*/
         /*Product*/
         /*New*/
         /*Previous*/
         /*Difference*/

         /*HR Header*/
         this.HasHRProducts
            ? [
                 /*Product*/ {
                    class: 'productHeader desc',
                    value: 'HR',
                    style: { textAlign: 'left', verticalAlign: 'bottom' },
                 },
                 /*New*/ {
                    value: '',
                    class: 'tableheader2 currency',
                    style: {
                       textAlign: 'right',
                       minWidth: '2em',
                       width: '5em',
                       verticalAlign: 'top',
                       paddingTop: '0.5em',
                    },
                 },
                 /*Previous*/ {
                    value: '',
                    class: 'tableheader2 currency',
                    style: {
                       textAlign: 'right',
                       minWidth: '2em',
                       width: '5em',
                       verticalAlign: 'top',
                       paddingTop: '0.5em',
                    },
                 },
                 /*Difference*/ {
                    value: '',
                    class: 'tableheader2 currency',
                    style: {
                       textAlign: 'right',
                       minWidth: '2em',
                       width: '5em',
                       verticalAlign: 'top',
                       paddingTop: '0.5em',
                    },
                 },
              ]
            : [],
         /*Core*/
         (this.HasCoreSelected &&
            !this.product_list[this.CoreProductSelected]?.NotInPricebook) ||
         this.calculatedValues.productCoreMonthlyCosts.previousTotalPrice > 0
            ? [
                 /*Product*/ {
                    value:
                       this.prefix +
                       this.product_list[this.CoreProductSelected][
                          'ProductName'
                       ],
                    class: 'indent',
                 },
                 /*New*/ {
                    class: 'currency',
                    value: formatFloatAsCurrencySymbol(
                       this.calculatedValues.productCoreMonthlyCosts.pdfDisplayTotal,
                       this.currencyObject,
                       true,
                    ),
                 },
                 /*Previous*/ {
                    class: 'currency col-bhr-blue',
                    value: formatFloatAsCurrencySymbol(
                       this.calculatedValues.productCoreMonthlyCosts
                          .previousTotalPrice,
                       this.currencyObject,
                    ),
                 },
                 /*Difference*/ formatDiffPriceDifference(
                    this.calculatedValues.productCoreMonthlyCosts
                       .pdfDisplayDifferenceTotal,
                    this.currencyObject,
                 ),
              ]
            : [],
         /*Performance*/
         (this.ProductPerformanceEECount > 0 &&
            !this.product_list['performanceMan']?.NotInPricebook) ||
         this.calculatedValues.productPerformanceMonthlyCosts
            .previousTotalPrice > 0
            ? [
                 /*Product*/ {
                    value:
                       this.prefix +
                       this.product_list.hasOwnProperty('performanceMan')
                          ? this.product_list['performanceMan']['ProductName']
                          : 'Performance Management',
                    class: 'indent',
                 },
                 /*New*/ {
                    class: 'currency',
                    value: formatFloatAsCurrencySymbol(
                       this.calculatedValues.productPerformanceMonthlyCosts
                          .pdfDisplayTotal,
                       this.currencyObject,
                       true,
                    ),
                 },
                 /*Previous*/ {
                    class: 'currency col-bhr-blue',
                    value: formatFloatAsCurrencySymbol(
                       this.calculatedValues.productPerformanceMonthlyCosts
                          .previousTotalPrice,
                       this.currencyObject,
                    ),
                 },
                 /*Difference*/ formatDiffPriceDifference(
                    this.calculatedValues.productPerformanceMonthlyCosts
                       .pdfDisplayDifferenceTotal,
                    this.currencyObject,
                 ),
              ]
            : [],
         /*BenAdmin*/
         (this.HasBenAdminSelected &&
            !this.product_list['benAdmin']?.NotInPricebook) ||
         this.calculatedValues.productBenefitsAdministrationMonthlyCosts
            .previousTotalPrice > 0
            ? [
                 /*Product*/ {
                    value:
                       this.prefix +
                       this.product_list['benAdmin']['ProductName'],
                    class: 'indent',
                 },
                 /*New*/ {
                    class: 'currency',
                    value: formatFloatAsCurrencySymbol(
                       this.calculatedValues
                          .productBenefitsAdministrationMonthlyCosts.pdfDisplayTotal,
                       this.currencyObject,
                       true,
                    ),
                 },
                 /*Previous*/ {
                    class: 'currency col-bhr-blue',
                    value: formatFloatAsCurrencySymbol(
                       this.calculatedValues
                          .productBenefitsAdministrationMonthlyCosts
                          .previousTotalPrice,
                       this.currencyObject,
                    ),
                 },
                 /*Difference*/ formatDiffPriceDifference(
                    this.calculatedValues
                       .productBenefitsAdministrationMonthlyCosts
                       .pdfDisplayDifferenceTotal,
                    this.currencyObject,
                 ),
              ]
            : [],
         /*Time Tracking / Time Attendance*/
         ...(() => {
            const isAttendance = this.TimeProductSelected === 'timeAttendance'
            const activeCode = isAttendance ? 'timeAttendance' : 'timeTracking'
            const activeEECount = isAttendance
               ? this.ProductTimeAttendanceEECount
               : this.ProductTimeTrackingEECount
            const activeCosts = isAttendance
               ? this.calculatedValues.productTimeAttendanceMonthlyCosts
               : this.calculatedValues.productTimeTrackingMonthlyCosts
            if (
               !(
                  (activeEECount > 0 &&
                     !this.product_list[activeCode]?.NotInPricebook) ||
                  activeCosts.previousTotalPrice > 0
               )
            ) {
               return []
            }
            return [
               [
                  /*Product*/ {
                     value: this.prefix + this.getTimeProductRowDisplayName(),
                     class: 'indent',
                  },
                  /*New*/ {
                     class: 'currency',
                     value: formatFloatAsCurrencySymbol(
                        activeCosts.pdfDisplayTotal,
                        this.currencyObject,
                        true,
                     ),
                  },
                  /*Previous*/ {
                     class: 'currency col-bhr-blue',
                     value: formatFloatAsCurrencySymbol(
                        activeCosts.previousTotalPrice,
                        this.currencyObject,
                     ),
                  },
                  /*Difference*/ formatDiffPriceDifference(
                     activeCosts.pdfDisplayDifferenceTotal,
                     this.currencyObject,
                  ),
               ],
            ]
         })(),
         /*Time Clock Monthly Support*/
         (this.HasTimeClocks &&
            this.product_list['clockMonthlySupport'] &&
            (this.ProductTimeClockDetails?.length ?? 0) > 0) ||
         this.calculatedValues.TimeClockMonthlyCost.previousTotalPrice > 0
            ? [
                 /*Product*/ {
                    value:
                       this.prefix +
                       this.product_list['clockMonthlySupport']['ProductName'],
                    class: 'indent',
                 },
                 /*New*/ {
                    class: 'currency',
                    value: formatFloatAsCurrencySymbol(
                       this.calculatedValues.TimeClockMonthlyCost.pdfDisplayTotal,
                       this.currencyObject,
                       true,
                    ),
                 },
                 /*Previous*/ {
                    class: 'currency col-bhr-blue',
                    value: formatFloatAsCurrencySymbol(
                       this.calculatedValues.TimeClockMonthlyCost
                          .previousTotalPrice,
                       this.currencyObject,
                    ),
                 },
                 /*Difference*/ formatDiffPriceDifference(
                    this.calculatedValues.TimeClockMonthlyCost
                       .pdfDisplayDifferenceTotal,
                    this.currencyObject,
                 ),
              ]
            : [],
         /*Additional Job Postings*/
         (this.ProductAdditionalJobsCount > 0 &&
            !this.product_list['addJobPosting']?.NotInPricebook) ||
         this.calculatedValues.AdditionalJobsCosts.previousTotalPrice > 0
            ? [
                 /*Product*/ {
                    value:
                       this.prefix +
                       this.product_list['addJobPosting']['ProductName'],
                    class: 'indent',
                 },
                 /*New*/ {
                    class: 'currency',
                    value: formatFloatAsCurrencySymbol(
                       this.calculatedValues.AdditionalJobsCosts.pdfDisplayTotal,
                       this.currencyObject,
                       true,
                    ),
                 },
                 /*Previous*/ {
                    class: 'currency col-bhr-blue',
                    value: formatFloatAsCurrencySymbol(
                       this.calculatedValues.AdditionalJobsCosts
                          .previousTotalPrice,
                       this.currencyObject,
                    ),
                 },
                 /*Difference*/ formatDiffPriceDifference(
                    this.calculatedValues.AdditionalJobsCosts
                       .pdfDisplayDifferenceTotal,
                    this.currencyObject,
                 ),
              ]
            : [],
         /*Additional Storage*/
         (this.ProductAdditionalStorageCount > 0 &&
            !this.product_list['addStorage']?.NotInPricebook) ||
         this.calculatedValues.AdditionalStorageCosts.previousTotalPrice > 0
            ? [
                 /*Product*/ {
                    value:
                       this.prefix +
                       this.product_list['addStorage']['ProductName'],
                    class: 'indent',
                 },
                 /*New*/ {
                    class: 'currency',
                    value: formatFloatAsCurrencySymbol(
                       this.calculatedValues.AdditionalStorageCosts.pdfDisplayTotal,
                       this.currencyObject,
                       true,
                    ),
                 },
                 /*Previous*/ {
                    class: 'currency col-bhr-blue',
                    value: formatFloatAsCurrencySymbol(
                       this.calculatedValues.AdditionalStorageCosts
                          .previousTotalPrice,
                       this.currencyObject,
                    ),
                 },
                 /*Difference*/ formatDiffPriceDifference(
                    this.calculatedValues.AdditionalStorageCosts
                       .pdfDisplayDifferenceTotal,
                    this.currencyObject,
                 ),
              ]
            : [],
         /*Test Account*/
         (this.ProductTestAccount &&
            !this.product_list['testAccount']?.NotInPricebook) ||
         this.calculatedValues.TestAccountCosts.previousTotalPrice > 0
            ? [
                 /*Product*/ {
                    value:
                       this.prefix +
                       this.product_list['testAccount']['ProductName'],
                    class: 'indent',
                 },
                 /*New*/ {
                    class: 'currency',
                    value: formatFloatAsCurrencySymbol(
                       this.calculatedValues.TestAccountCosts.pdfDisplayTotal,
                       this.currencyObject,
                       true,
                    ),
                 },
                 /*Previous*/ {
                    class: 'currency col-bhr-blue',
                    value: formatFloatAsCurrencySymbol(
                       this.calculatedValues.TestAccountCosts
                          .previousTotalPrice,
                       this.currencyObject,
                    ),
                 },
                 /*Difference*/ formatDiffPriceDifference(
                    this.calculatedValues.TestAccountCosts.pdfDisplayDifferenceTotal,
                    this.currencyObject,
                 ),
              ]
            : [],
      ]

      let monthlyPreviousTotal = 0
      monthlyPreviousTotal +=
         this.calculatedValues.productCoreMonthlyCosts.previousTotalPrice
      monthlyPreviousTotal +=
         this.calculatedValues.productBenefitsAdministrationMonthlyCosts
            .previousTotalPrice
      monthlyPreviousTotal +=
         this.TimeProductSelected === 'timeAttendance'
            ? this.calculatedValues.productTimeAttendanceMonthlyCosts
                 .previousTotalPrice
            : this.calculatedValues.productTimeTrackingMonthlyCosts
                 .previousTotalPrice
      monthlyPreviousTotal +=
         this.calculatedValues.AdditionalJobsCosts.previousTotalPrice
      monthlyPreviousTotal +=
         this.calculatedValues.AdditionalStorageCosts.previousTotalPrice
      monthlyPreviousTotal +=
         this.calculatedValues.TestAccountCosts.previousTotalPrice
      monthlyPreviousTotal +=
         this.calculatedValues.TimeClockMonthlyCost.previousTotalPrice
      if (!this.IsForeignCurrencyQuote)
         monthlyPreviousTotal +=
            this.calculatedValues.payrollMonthlyTotal.previousTotalPrice

      let monthlyDifference =
         this.calculatedValues.monthlyTotal.pdfDisplayTotal - monthlyPreviousTotal

      let hasPreviousHRProduct =
         this.calculatedValues.hrMonthlyTotal.previousTotalPrice > 0
      if (
         this.IsForeignCurrencyQuote &&
         (this.HasHRProducts || hasPreviousHRProduct)
      ) {
         diff_rows.push(
            /*Monthly Total Row*/
            [
               /*Product*/ {
                  class: 'productHeader desc',
                  value: this.prefix + 'Monthly Total',
                  style: { textAlign: 'left' },
               },
               /*New*/ {
                  class: 'productHeader currency',
                  value: formatFloatAsCurrencySymbol(
                     this.calculatedValues.monthlyTotal.pdfDisplayTotal,
                     this.currencyObject,
                     true,
                  ),
               },
               /*Previous*/ {
                  class: 'currency col-bhr-blue',
                  value: formatFloatAsCurrencySymbol(
                     monthlyPreviousTotal,
                     this.currencyObject,
                     true,
                  ),
               },
               /*Difference*/ formatDiffPriceDifference(
                  this.calculatedValues.monthlyTotal.pdfDisplayDifferenceTotal,
                  this.currencyObject,
                  true,
               ),
            ],
         )
      }
      let payrollDifferenceHeader = ''
      if (
         this.IsForeignCurrencyQuote &&
         (this.HasHRProducts || hasPreviousHRProduct) &&
         this.HasPayrollSelected
      ) {
         payrollDifferenceHeader = `Difference (${this.currencyObjectUSD.Code})`
      }
      diff_rows.push(
         /*Payroll Header*/
         this.HasPayrollSelected ||
            this.calculatedValues.payrollMonthlyTotal.previousTotalPrice > 0
            ? [
                 /*Product*/ {
                    class: 'productHeader desc',
                    value: 'Payroll',
                    style: { textAlign: 'left', verticalAlign: 'bottom' },
                 },
                 /*New*/ {
                    value: '',
                    class: 'tableheader2 currency',
                    style: {
                       textAlign: 'right',
                       minWidth: '2em',
                       width: '5em',
                       verticalAlign: 'top',
                       paddingTop: '0.5em',
                    },
                 },
                 /*Previous*/ {
                    value: '',
                    class: 'tableheader2 currency col-bhr-blue',
                    style: {
                       textAlign: 'right',
                       minWidth: '2em',
                       width: '5em',
                       verticalAlign: 'top',
                       paddingTop: '0.5em',
                    },
                 },
                 /*Difference*/ ...(this.IsForeignCurrencyQuote
                    ? [
                         {
                            value: payrollDifferenceHeader,
                            class: 'productHeader desc',
                            style: {
                               textAlign: 'right',
                               verticalAlign: 'bottom',
                            },
                         },
                      ]
                    : [{ value: '', class: 'productHeader desc' }]),
              ]
            : [],
      )

      if (this.ProductPayrollEINCount > 1) {
         for (
            let EINNumber = 0;
            EINNumber < this.ProductPayrollEINCount;
            EINNumber++
         ) {
            if (this.ProductPayrollEECounts[EINNumber] > 0) {
               let EINName = this.ProductPayrollEINNames[EINNumber]
               if (EINName) {
                  EINName = ' ' + EINName
               } else if (this.ProductPayrollEINCount > 1) {
                  EINName = ' EIN ' + (EINNumber + 1).toString()
               } else {
                  EINName = ''
               }
               diff_rows.push(
                  /*Payroll Row*/
                  [
                     /*Product*/ {
                        value: this.prefix + EINName,
                        class: 'indent',
                     },
                     /*New*/ {
                        class: 'currency',
                        value: formatFloatAsCurrencySymbol(
                           this.ProductPayrollMonthlyCosts[EINNumber]
                              .pdfDisplayTotal +
                              this.ProductPayrollMonthlyStateCosts[EINNumber]
                                 .pdfDisplayTotal,
                           this.currencyObjectUSD,
                           true,
                        ),
                     },
                     /*Previous*/ {
                        class: 'currency col-bhr-blue',
                        value: formatFloatAsCurrencySymbol(
                           this.ProductPayrollMonthlyCosts[EINNumber]
                              .previousTotalPrice +
                              this.ProductPayrollMonthlyStateCosts[EINNumber]
                                 .previousTotalPrice,
                           this.currencyObjectUSD,
                           true,
                        ),
                     },
                     /*Difference*/ formatDiffPriceDifference(
                        this.ProductPayrollMonthlyCosts[EINNumber]
                           .pdfDisplayDifferenceTotal +
                           this.ProductPayrollMonthlyStateCosts[EINNumber]
                              .pdfDisplayDifferenceTotal -
                           this.ProductPayrollMonthlyCosts[EINNumber]
                              .previousTotalPrice,
                        this.currencyObjectUSD,
                     ),
                  ],
               )
            }
         }
      }
      if (
         this.HasPayrollSelected ||
         this.calculatedValues.payrollMonthlyTotal.previousTotalPrice > 0
      ) {
         let payrollLabel =
            this.prefix + this.product_list['payroll']['ProductName']
         if (
            this.ProductPayrollEINCount > 1 ||
            this.ProductPayrollTotalStateCount > 1
         )
            payrollLabel += ' Total'
         let diff = formatDiffPriceDifference(
            this.calculatedValues.payrollMonthlyTotal.pdfDisplayTotal -
               this.calculatedValues.payrollMonthlyTotal.previousTotalPrice,
            this.currencyObjectUSD,
         )
         diff_rows.push(
            /*Payroll Total Row*/
            [
               /*Product*/ { value: payrollLabel, class: 'indent' },
               /*New*/ {
                  class: 'currency',
                  value: formatFloatAsCurrencySymbol(
                     this.calculatedValues.payrollMonthlyTotal.pdfDisplayTotal,
                     this.currencyObjectUSD,
                     true,
                  ),
               },
               /*Previous*/ {
                  class: 'currency col-bhr-blue',
                  value: formatFloatAsCurrencySymbol(
                     this.calculatedValues.payrollMonthlyTotal
                        .previousTotalPrice,
                     this.currencyObjectUSD,
                     true,
                  ),
               },
               /*Difference*/ diff,
            ],
         )
      }

      if (!this.IsForeignCurrencyQuote) {
         diff_rows.push(
            /*Monthly Total Row*/
            [
               /*Product*/ {
                  class: 'productHeader desc',
                  value: this.prefix + 'Monthly Total',
                  style: { textAlign: 'left' },
               },
               /*New*/ {
                  class: 'productHeader currency',
                  value: formatFloatAsCurrencySymbol(
                     this.calculatedValues.monthlyTotal.pdfDisplayTotal,
                     this.currencyObject,
                     true,
                  ),
               },
               /*Previous*/ {
                  class: 'currency col-bhr-blue',
                  value: formatFloatAsCurrencySymbol(
                     monthlyPreviousTotal,
                     this.currencyObject,
                     true,
                  ),
               },
               /*Difference*/ formatDiffPriceDifference(
                  monthlyDifference,
                  this.currencyObject,
                  true,
               ),
            ],
         )
      }

      // Removes empty rows
      diff_rows = diff_rows.filter((row) => row.length > 0)

      return diff_rows
   }

   async generateEstimatedPrepaymentCoverage() {
      if (this.MonthsSelected > 1) {
         let activationDate = new Date(this.QuoteActivationDate)
         let coverageStart =
            activationDate.toLocaleString('default', {
               month: 'short',
               timeZone: 'UTC',
            }) +
            ' ' +
            activationDate.getUTCFullYear()
         let coverageEnd =
            new Date(
               activationDate.setUTCMonth(
                  activationDate.getUTCMonth() + this.MonthsSelected,
               ),
            ).toLocaleString('default', { month: 'short', timeZone: 'UTC' }) +
            ' ' +
            activationDate.getUTCFullYear()
         return coverageStart + ' - ' + coverageEnd
      }
   }

   async initialize(
      getTimeData,
      getFeaturePermissions,
      getOppData,
      getProductList,
      getDiscountList,
      getCurrencyObject,
      getPricebooks,
      getOpportunityProducts,
      getStrings,
      getTermLengths,
   ) {
      const [timeData, allPermissions, strings, parsed] = await Promise.all([
         getTimeData(),
         getFeaturePermissions(),
         getStrings(),
         getOppData(this.opportunity_id),
      ])
      this.timeData = timeData
      this.AllPermissions = allPermissions
      this.Strings = strings
      if (this.AllPermissions) {
         this.PayrollImpInActivation =
            'PayrollImpInActivation' in this.AllPermissions
               ? this.AllPermissions['PayrollImpInActivation']
               : []
         this.PromotionalDiscountColumnUTMs =
            'PromotionalDiscountColumn' in this.AllPermissions
               ? this.AllPermissions['PromotionalDiscountColumn']
               : []
         this.ShowEffectiveDiscountOnQuotePermissions =
            'ShowEffectiveDiscountOnQuote' in this.AllPermissions
               ? this.AllPermissions['ShowEffectiveDiscountOnQuote']
               : []
         this.UseAltProductDescription =
            'UseAltProductDescription' in this.AllPermissions
               ? this.AllPermissions['UseAltProductDescription']
               : []
         this.ShowEstimatedCoveragePeriodPermissions =
            'ShowEstimatedCoveragePeriod' in this.AllPermissions
               ? this.AllPermissions['ShowEstimatedCoveragePeriod']
               : []
         this.TermContractFeature =
            'TermContractFeature' in this.AllPermissions
               ? this.AllPermissions['TermContractFeature']
               : []
         this.VolumeComponentToDisplay =
            'VolumeComponentToDisplay' in this.AllPermissions
               ? this.AllPermissions['VolumeComponentToDisplay']
               : []
      }

      // Use the saved opp's user_role for calculation permissions (e.g. LimitVolumeDiscount).
      // Falls back to this.user_role (the API request's role) if no saved opp or no saved user_role.
      this.calculationUserRole = parsed?.user_role || this.user_role
      this.LimitVolumeDiscount =
         this.AllPermissions['LimitVolumeDiscount']?.includes(this.calculationUserRole) ?? false
      const termLengthsPromise = getTermLengths(
         parsed?.term_length,
         this.user_role,
      )
      if (parsed) {
         //  input states to parsed opp data
         if (parsed.book_id != null) this.pricebookId = parsed.book_id
         if (parsed.book_id) this.IsForeignCurrencyQuote = IsForeignCurrencyQuote(parsed.book_id)
         this.QuoteType = parsed.quote_type ? parsed.quote_type : 'full'
         if (parsed.expiration_date != null) {
            const expirationDateStr =
               typeof parsed.expiration_date === 'string'
                  ? parsed.expiration_date
                  : new Date(parsed.expiration_date).toISOString()
            this.QuoteExpirationDate = expirationDateStr.split('T')[0]
         }
         if (parsed.core_selected != null)
            this.CoreProductSelected = parsed.core_selected
         if (parsed.core_count != null)
            this.ProductCoreEECount = parsed.core_count
         if (parsed.performance_management_count != null)
            this.ProductPerformanceEECount = parsed.performance_management_count
         if (parsed.time_tracking_count != null)
            this.ProductTimeTrackingEECount = parsed.time_tracking_count
         this.TimeProductSelected =
            parsed.time_product_selected === 'timeAttendance'
               ? 'timeAttendance'
               : 'timeTracking'
         if (parsed.has_time_clocks != null)
            this.HasTimeClocks = parsed.has_time_clocks
         if (parsed.time_clock_details != null)
            this.ProductTimeClockDetails = parsed.time_clock_details
         if (parsed.us_employees != null)
            this.HasUSEmployees = parsed.us_employees
         if (parsed.benefits_administration_count != null)
            this.ProductBenefitsAdministrationEECount =
               parsed.benefits_administration_count
         //TODO: this isn't working!
         if (parsed.payroll_count)
            this.ProductPayrollEECount = parsed.payroll_count
         else this.ProductPayrollEECount = 0
         if (parsed.states_count != null)
            this.ProductPayrollTotalStateCount = parsed.states_count
         if ('imp_type' in parsed)
            this.ImpType =
               parsed.imp_type === 'null' ? 'selfSetup' : parsed.imp_type
         if (parsed.core_country_count != null)
            this.CoreCountryCount = parsed.core_country_count
         if ('ben_admin_imp_type' in parsed)
            this.BenAdminImpType =
               parsed.ben_admin_imp_type === 'null'
                  ? 'none'
                  : parsed.ben_admin_imp_type
         if ('payroll_imp_type' in parsed)
            this.PayrollImpType =
               parsed.payroll_imp_type === 'null'
                  ? 'none'
                  : parsed.payroll_imp_type
         if (parsed.bundle_discount != null)
            this.DiscountBundle = parsed.bundle_discount
         if (parsed.term_length != null) this.TermLength = parsed.term_length
         // NEW: Parse prepay_length directly from opportunity
         if (parsed.prepay_length != null && parsed.prepay_length > 0)
            this.PrepayLength = parsed.prepay_length
         if (parsed.nonprofit_discount != null)
            this.DiscountNonProfit = parsed.nonprofit_discount
         if (
            parsed.annual_prepayment_discount &&
            parsed.annual_prepayment_discount != 0
         )
            this.DiscountPrepaymentCode = parsed.annual_prepayment_discount
         if (parsed.multi_entity_discount != null)
            this.DiscountMultiEntity = parsed.multi_entity_discount
         if (parsed.hr_promotion_monthly_discount != null)
            this.DiscountHRMonthlyPromotion =
               parsed.hr_promotion_monthly_discount
         if (parsed.hr_dealdesk_monthly_discount != null)
            this.DiscountHRDealDeskMonthly = parsed.hr_dealdesk_monthly_discount
         if (parsed.reseller_discount != null)
            this.DiscountReseller = parsed.reseller_discount
         if (parsed.quickstart_training_discount != null)
            this.DiscountQuickstartTraining =
               parsed.quickstart_training_discount
         if (parsed.promotion_imp_discount != null)
            this.DiscountImpPromotion = parsed.promotion_imp_discount
         if (parsed.hr_dealdesk_imp_discount != null)
            this.DiscountDealDeskHRImp = parsed.hr_dealdesk_imp_discount
         if (parsed.payroll_dealdesk_imp_discount != null)
            this.DiscountDealDeskPayrollImp =
               parsed.payroll_dealdesk_imp_discount
         if (parsed.second_ein_discount != null)
            this.DiscountSecondEIN = parsed.second_ein_discount
         if (parsed.group_imp_discount != null)
            this.DiscountGroupImp = parsed.group_imp_discount
         if (parsed.hr_dealdesk_imp_discount_rate != null)
            this.DiscountDealDeskHRImpRate =
               parsed.hr_dealdesk_imp_discount_rate
         if (parsed.quickstart_dealdesk_imp_discount_rate != null)
            this.DiscountDealDeskQuickstartImpRate =
               parsed.quickstart_dealdesk_imp_discount_rate
         if (parsed.payroll_dealdesk_imp_discount_rate != null)
            this.DiscountDealDeskPayrollImpRate =
               parsed.payroll_dealdesk_imp_discount_rate
         if (parsed.additional_storage_count != null)
            this.ProductAdditionalStorageCount = parsed.additional_storage_count
         if (parsed.additional_job_count != null)
            this.ProductAdditionalJobsCount = parsed.additional_job_count
         if (parsed.historical_employee_import != null)
            this.ProductHistoricalDataImport = parsed.historical_employee_import
         if (parsed.marketplace_listing != null)
            this.ProductMarketplaceListing = parsed.marketplace_listing
         if (parsed.sponsorship != null)
            this.ProductSponsorship = parsed.sponsorship
         if (parsed.paid_service != null)
            this.ProductPaidServicesAmount = parsed.paid_service
         if (parsed.quote_notes)
            try {
               this.Comments = atob(parsed.quote_notes)
            } catch {
               this.Comments = parsed.quote_notes
            }
         if (parsed.test_account) this.ProductTestAccount = parsed.test_account
         if (parsed.stage) this.Stage = parsed.stage
         if (parsed.activation_date != null) {
            const activationDate =
               typeof parsed.activation_date === 'string'
                  ? parsed.activation_date
                  : new Date(parsed.activation_date).toISOString()
            this.QuoteActivationDate = activationDate.split('T')[0]
         }
         if (parsed.difference_quote != null)
            this.DifferenceQuote = parsed.difference_quote
         else this.DifferenceQuote = false
         if (parsed.is_co_term != null) this.IsCoTerm = parsed.is_co_term
         else this.IsCoTerm = false
         if (parsed.payroll_first_pay_date != null)
            this.PayrollFirstPayDate = parsed.payroll_first_pay_date
         if (parsed.is_ats_only != null) this.IsATSOnly = parsed.is_ats_only
         else this.IsATSOnly = false
         // Get account_snowflake from query string. If not present, try and get from db.
         // if (searchParams.get('account_snowflake') == null) this.AccountSnowflake = {};
         // else if (searchParams.get('account_snowflake') != null) this.AccountSnowflake = JSON.parse(decodeURIComponent(searchParams.get('account_snowflake')));
         if (parsed.account_snowflake != null)
            this.AccountSnowflake = parsed.account_snowflake
         this.OpportunityType =
            parsed.account_status === 'Subscriber' ? 'Expansion' : 'New'
         // Prefer the saved next_invoice_date — it is the resolved value the
         // app committed for this opp (see resolveNextInvoiceDate in dateDefaults.ts).
         // Fall back to the Snowflake NID, then derive from activation date, then today.
         if (parsed.next_invoice_date != null) {
            const nid =
               typeof parsed.next_invoice_date === 'string'
                  ? parsed.next_invoice_date
                  : new Date(parsed.next_invoice_date).toISOString()
            this.NextInvoiceDate = nid.split('T')[0]
            if (parsed.activation_date == null)
               this.QuoteActivationDate = this.NextInvoiceDate
         } else if (parsed?.account_snowflake?.Next_Invoice_Date__c) {
            this.NextInvoiceDate = parsed.account_snowflake.Next_Invoice_Date__c
            if (parsed.activation_date == null)
               this.QuoteActivationDate =
                  parsed.account_snowflake.Next_Invoice_Date__c
         } else {
            //Use last day of month of activation date as next invoice date (if provided), else use last day of this month from timeData
            if (this.QuoteActivationDate) {
               const activationDate = new Date(this.QuoteActivationDate)
               this.NextInvoiceDate = new Date(
                  Date.UTC(
                     activationDate.getUTCFullYear(),
                     activationDate.getUTCMonth() + 1,
                     0,
                  ),
               )
                  .toISOString()
                  .split('T')[0]
            } else {
               this.NextInvoiceDate = this.timeData.lastDayOfThisMonth
            }
            if (parsed.activation_date == null)
               this.QuoteActivationDate = this.NextInvoiceDate
         }
         if (parsed.core_country_count != null)
            this.CoreCountryCount = parsed.core_country_count
         if (parsed.additional_ein_quote != null)
            this.AdditionalEINQuote = parsed.additional_ein_quote
         if (parsed.promo_utm != null)
            this.PromoUTM = decodeURIComponent(parsed.promo_utm)
         if (parsed.account_flags != null)
            this.AccountFlags = parsed.account_flags
         if (parsed.promotional_discounts != null)
            this.PromotionalDiscounts = parsed.promotional_discounts

         // Retreives currency information
         if (parsed.currency_code != null)
            this.currencyObject = await getCurrencyObject(parsed.currency_code)
         if (!this.IsForeignCurrencyQuote) {
            this.currencyObjectUSD = this.currencyObject
         } else {
            this.currencyObjectUSD = await getCurrencyObject('USD')
         }
         //Use Current Month Invoice Currency (if available) or Last Month Invoice Currency if available
         let previousCurrencyCode = 'USD'
         if (parsed?.account_snowflake?.CMI_Local_Currency__c)
            previousCurrencyCode =
               parsed.account_snowflake.CMI_Local_Currency__c
         else if (parsed?.account_snowflake?.LMI_Local_Currency__c)
            previousCurrencyCode =
               parsed.account_snowflake.LMI_Local_Currency__c

         if (previousCurrencyCode === 'USD')
            this.previousCurrencyObject = this.currencyObjectUSD
         else if (previousCurrencyCode === this.currencyObject.Code)
            this.previousCurrencyObject = this.currencyObject
         else
            this.previousCurrencyObject =
               await getCurrencyObject(previousCurrencyCode)

         if (parsed.monthly_tax != null) this.MonthlyTax = parsed.monthly_tax
         if (parsed.account_balance != null)
            this.AccountBalance = parsed.account_balance
         if (parsed.professional_service_details != null)
            this.ProfessionalServices = parsed.professional_service_details
         if (parsed.consulting_service_details != null)
            this.ConsultingServices = parsed.consulting_service_details
         if (parsed.partner_account != null)
            this.PartnerAccount = decodeURIComponent(parsed.partner_account)
         if (parsed.region != null)
            this.Region = decodeURIComponent(parsed.region)

         // Retrieves EIN Integrations
         if (parsed.ein_integrations != null && parsed.ein_integrations != '') {
            this.ProductPayrollEINIntegrations = Array.isArray(
               parsed.ein_integrations,
            )
               ? parsed.ein_integrations
               : []
         }
         if (parsed?.prepayment_activation_use_difference) {
            this.prepaymentActivationUseDifference =
               parsed.prepayment_activation_use_difference
         }

         this.ProductPayrollEINCount = parsed.ein_count ? parsed.ein_count : 0
         this.ProductPayrollEINNames =
            parsed.ein_names && parsed.ein_names.constructor === Array
               ? parsed.ein_names
               : []
         this.ProductPayrollEECounts =
            parsed.ein_ee_counts && parsed.ein_ee_counts.constructor === Array
               ? parsed.ein_ee_counts
               : []
         this.ProductPayrollStateCounts =
            parsed.ein_state_counts &&
            parsed.ein_state_counts.constructor === Array
               ? parsed.ein_state_counts
               : []
         this.ProductPayrollEINImpTypes =
            parsed.ein_imp_types && parsed.ein_imp_types.constructor === Array
               ? parsed.ein_imp_types
               : []

         this.AccountStatus = parsed.account_status

         let DynamicProducts
         ;[
            this.product_list,
            this.discount_list,
            this.pricebooks,
            DynamicProducts,
         ] = await Promise.all([
            getProductList(this.pricebookId, this.currencyObject),
            getDiscountList(
               this.pricebookId,
               this.PromoUTM,
               this.AccountFlags,
               this.currencyObject,
               this.OpportunityType,
               parsed?.account_snowflake,
               this.PartnerAccount,
               this.Region,
               this.opportunity_id,
            ),
            getPricebooks(this.opportunity_id),
            getOpportunityProducts(this.opportunity_id),
         ])
         let hasMonthlyDynamicProducts = false
         let hasOneTimeDynamicProducts = false
         if (DynamicProducts) {
            this.DynamicProducts = new Map(DynamicProducts)
            // timeAttendance is rendered on the fixed time row, not as a dynamic
            // product row, but calculateQuote still needs its count via
            // dynamicProducts. Capture the count into a dedicated field and
            // leave it in the map so the calc receives it. Iteration loops that
            // render dynamic rows skip it by productCode.
            this.ProductTimeAttendanceEECount =
               Number(this.DynamicProducts.get('timeAttendance')) || 0
            for (const [productCode, count] of this.DynamicProducts) {
               if (productCode === 'timeAttendance') continue
               const product = this.product_list[productCode]
               if (product?.BillingFreq === 'monthly') {
                  hasMonthlyDynamicProducts = true
               } else if (product?.BillingFreq === 'once') {
                  hasOneTimeDynamicProducts = true
               }
               if (!this.HasEOR && product?.ProductFamily === 'EOR') {
                  this.HasEOR = true
               }
               if (
                  !this.HasManagedOrConsultingService &&
                  product?.ProductFamily === 'Managed Services'
               ) {
                  this.HasManagedOrConsultingService = true
               }
            }
         }

         if (
            !this.HasManagedOrConsultingService &&
            this.ConsultingServices.some((s) => s.count > 0)
         ) {
            this.HasManagedOrConsultingService = true
         }

         // if(this.product_list.length > 0 && this.discount_list.length > 0 && this.CoreProductSelected !== null) {
         if (parsed && this.product_list && this.discount_list) {
            this.TermLengthsList = await termLengthsPromise
            this.calculatedValues = calculateQuote(
               this.product_list,
               this.discount_list,
               this.pricebookId,
               this.QuoteType,
               this.CoreProductSelected,
               this.ProductCoreEECount,
               this.ProductPerformanceEECount,
               this.ProductTimeTrackingEECount,
               this.HasTimeClocks,
               this.ProductTimeClockDetails,
               this.ProductBenefitsAdministrationEECount,
               this.ProductPayrollEINCount,
               this.ProductPayrollEECount,
               this.ProductPayrollEECounts,
               this.ProductPayrollTotalStateCount,
               this.ProductPayrollStateCounts,
               this.ProfessionalServices,
               this.ConsultingServices,
               this.ImpType,
               this.BenAdminImpType,
               this.PayrollImpType,
               this.DiscountBundle,
               this.DiscountNonProfit,
               this.PrepayLength, // Use PrepayLength instead of DiscountPrepaymentCode
               this.DiscountMultiEntity,
               this.DiscountHRMonthlyPromotion,
               this.DiscountHRDealDeskMonthly,
               this.DiscountReseller,
               this.DiscountQuickstartTraining,
               this.DiscountImpPromotion,
               this.DiscountDealDeskHRImp,
               this.DiscountDealDeskPayrollImp,
               this.DiscountSecondEIN,
               this.DiscountGroupImp,
               this.DiscountDealDeskHRImpRate,
               this.DiscountDealDeskQuickstartImpRate,
               this.DiscountDealDeskPayrollImpRate,
               this.ProductAdditionalStorageCount,
               this.ProductAdditionalJobsCount,
               this.ProductHistoricalDataImport,
               this.ProductMarketplaceListing,
               this.ProductSponsorship,
               this.ProductPaidServicesAmount,
               this.ProductTestAccount,
               this.QuoteActivationDate,
               this.DifferenceQuote,
               this.AccountSnowflake,
               this.CoreCountryCount,
               this.PromotionalDiscounts,
               this.NextInvoiceDate,
               this.currencyObject,
               this.AccountBalance,
               this.MonthlyTax,
               this.ProductPayrollEINIntegrations,
               this.AllPermissions,
               this.calculationUserRole,
               this.AccountStatus,
               findObjectByKeyValue(
                  this.pricebooks,
                  'value',
                  parseInt(this.pricebookId),
               ),
               this.previousCurrencyObject,
               this.ProductPayrollEINImpTypes,
               this.prepaymentActivationUseDifference,
               this.DynamicProducts,
               this.TimeProductSelected,
               this.IsATSOnly,
               false, // isRecalculation
            )

            // Match row rendering: only show the PEPM column when at least one line has a
            // non-empty Effective PEPM cell (pdfDisplayDiscountedPEPMCost <= listPEPMCost),
            // including when the effective cost is $0.
            function hasDisplayablePEPM(costs) {
               if (!costs) return false
               const { pdfDisplayDiscountedPEPMCost, listPEPMCost, productEECount } = costs
               return pdfDisplayDiscountedPEPMCost <= listPEPMCost &&  productEECount > 0
            }

            //Fix hasCols values
            this.hasPEPMCols =
               this.hasPEPMCols ||
               (this.ProductCoreEECount >= 25 || hasDisplayablePEPM(this.calculatedValues?.productCoreMonthlyCosts)) ||
               (this.ProductBenefitsAdministrationEECount >= 25 || hasDisplayablePEPM(this.calculatedValues?.productBenefitsAdministrationMonthlyCosts)) ||
               (this.ProductTimeTrackingEECount >= 25 || hasDisplayablePEPM(this.calculatedValues?.productTimeTrackingMonthlyCosts)) ||
               (this.ProductTimeAttendanceEECount >= 25 || hasDisplayablePEPM(this.calculatedValues?.productTimeAttendanceMonthlyCosts)) ||
               this.ProductPerformanceEECount >= 25 ||
               (this.ProductPayrollEECounts &&
                  this.ProductPayrollEECounts.length > 0 &&
                  this.ProductPayrollEECounts.some((element) => element >= 25 || hasDisplayablePEPM(this.calculatedValues?.productPayrollMonthlyTotalCosts)))
            // One-time table prepayment slot + getOtherDiscountsSum exclusion when prepayment has its own column there
            this.hasDiscountPrepaymentCol =
               this.hasDiscountPrepaymentCol || this.hasPrepaymentDiscountApplied()
            this.HasCoreSelected =
               this.CoreProductSelected !== 'core0' &&
               this.CoreProductSelected !== null &&
               this.CoreProductSelected !== 'null'
            this.HasBenAdminSelected =
               this.ProductBenefitsAdministrationEECount > 0
            this.HasPayrollSelected =
               this.ProductPayrollEINCount > 0 &&
               this.ProductPayrollEECounts &&
               this.ProductPayrollEECounts.length > 0 &&
               this.ProductPayrollEINCount &&
               this.ProductPayrollEINCount > 0 &&
               this.ProductPayrollEECounts.slice(
                  0,
                  this.ProductPayrollEINCount,
               ).some((element) => element > 0)
            // Check if any HR products are selected, including dynamic products
            // Dynamic products only count as HR if QuoteHeading is explicitly 'HR'
            this.HasHRProducts =
               (this.HasCoreSelected && this.ProductCoreEECount > 0) ||
               this.HasBenAdminSelected ||
               this.ProductTimeTrackingEECount > 0 ||
               this.ProductTimeAttendanceEECount > 0 ||
               this.ProductAdditionalJobsCount > 0 ||
               this.ProductAdditionalStorageCount > 0 ||
               this.ProductPerformanceEECount > 0 ||
               this.ProductTestAccount > 0 ||
               this.calculatedValues?.TimeClockMonthlyCost?.totalPrice > 0 ||
               Array.from(this.DynamicProducts.entries()).some(
                  ([productCode, value]) => {
                     const product = this.product_list[productCode]
                     return value > 0 && product?.QuoteHeading === 'HR'
                  },
               )
            // Check if there are any non-Payroll dynamic products (for foreign currency header display)
            this.HasNonPayrollDynamicProducts = Array.from(
               this.DynamicProducts.entries(),
            ).some(([productCode, value]) => {
               const product = this.product_list[productCode]
               return value > 0 && product?.QuoteHeading !== 'Payroll'
            })
            // if(this.ProductPayrollEECounts.length > 0)
            // for(let i=0;i<8;i++) {
            //     if(this.ProductPayrollEECounts[i] > 0) {
            //         this.HasPayrollSelected = true;
            //         break;
            //     }
            // }

            // Retrieve all the UTM codes from the promo_utm field if none are found, set to empty array
            const promoUTMList = this.PromoUTM ? this.PromoUTM.split(',') : []

            // If any of the UTM codes are in the UseAltProductDescription list, set useAltDescription to true
            this.UseAltDescription = this.UseAltProductDescription.some(
               (code) => promoUTMList.includes(code),
            )

            //Only show bundle or promotinonal column if either the discount or US Employee checkboxs are selected AND one of the "bundleable" products is selected
            this.ShowPromotionalCol =
               this.PromotionalDiscountColumnUTMs.some((code) =>
                  promoUTMList.includes(code),
               ) &&
               Object.keys(this.PromotionalDiscounts).filter(
                  (promotionalDiscount) =>
                     this.discount_list[promotionalDiscount] &&
                     this.PromotionalDiscounts[promotionalDiscount],
               ).length > 0
            this.ShowBundleCol =
               (this.DiscountBundle || this.HasUSEmployees) &&
               (this.HasCoreSelected ||
                  this.HasBenAdminSelected ||
                  this.HasPayrollSelected) &&
               !this.ShowPromotionalCol
            this.ShowDiscountCol =
               this.DiscountMultiEntity &&
               !(
                  this.hasPEPMCols ||
                  this.ShowBundleCol ||
                  this.hasDiscountPrepaymentCol
               )
            this.HasOnetimeDiscount =
               this.calculatedValues.oneTimeTotal.totalDiscount > 0
            this.ShowOnetimeDiscountCol =
               this.HasOnetimeDiscount &&
               !(
                  this.hasPEPMCols ||
                  this.ShowBundleCol ||
                  this.ShowDiscountCol ||
                  this.hasDiscountPrepaymentCol ||
                  this.ShowPromotionalCol
               )
            if (
               this.ProductCoreEECount < 1 &&
               this.ProductBenefitsAdministrationEECount < 1 &&
               this.ProductPayrollEECounts.every((element) => element < 1)
            )
               this.ShowBundleCol = false

            // Helper: build top discount codes/names from a list of cost objects
            const buildTopDiscountCodes = (costsList, excludeDiscountCodes) => {
               const exclude = excludeDiscountCodes ?? new Set()
               const maxByCode = new Map()
               const nameByCode = new Map()
               const familyByCode = new Map()
               for (const costs of costsList) {
                  for (const d of costs?.appliedDiscounts ?? []) {
                     if (exclude.has(d.DiscountCode)) continue
                     if (!(d.ApplyToPDFDisplayedValue ?? this.discount_list?.[d.DiscountCode]?.ApplyToPDFDisplayedValue)) continue
                     const current = maxByCode.get(d.DiscountCode) ?? 0
                     if (d.DiscountAmount > current) maxByCode.set(d.DiscountCode, d.DiscountAmount)
                     if (!nameByCode.has(d.DiscountCode))
                        nameByCode.set(d.DiscountCode, d.DiscountCode === 'volume' ? 'Volume\nDiscount' : (d.PdfHeaderDisplayName || d.DiscountName))
                     if (!familyByCode.has(d.DiscountCode))
                        familyByCode.set(d.DiscountCode, d.DiscountFamily ?? null)
                  }
               }
               const priorityRank = (code) => {
                  if (code === 'volume') return 0
                  if (code === 'nonProfit') return 1
                  if (familyByCode.get(code) === 'Prepayment Discount') return 2
                  return 3
               }
               const hasBundle = maxByCode.has('bundleDiscount') && maxByCode.get('bundleDiscount') > 0
               const hasVolume = maxByCode.has('volume') && maxByCode.get('volume') > 0
               const sorted = [...maxByCode.entries()]
                  .filter(([code]) => code !== 'bundleDiscount' && code !== 'volume')
                  .sort((a, b) => {
                     const rankDiff = priorityRank(a[0]) - priorityRank(b[0])
                     if (rankDiff !== 0) return rankDiff
                     return b[1] - a[1]
                  })
                  .map(([code]) => code)
               // Group codes that share the same PdfHeaderDisplayName into a single column
               const groupsByName = new Map()
               const groupOrder = []
               for (const code of sorted) {
                  const name = nameByCode.get(code) ?? code
                  if (!groupsByName.has(name)) {
                     groupsByName.set(name, [code])
                     groupOrder.push(name)
                  } else {
                     groupsByName.get(name).push(code)
                  }
               }
               const groupedCodes = groupOrder.map(name => {
                  const group = groupsByName.get(name)
                  return group.length === 1 ? group[0] : group
               })
               const groupedNames = groupOrder.slice()
               return { codes: groupedCodes.slice(0, 7), names: groupedNames.slice(0, 7), total: groupOrder.length, hasBundle, hasVolume }
            }

            // Monthly-only products
            const allMonthlyProductCosts = [
               this.calculatedValues.productCoreMonthlyCosts,
               this.calculatedValues.productBenefitsAdministrationMonthlyCosts,
               this.calculatedValues.productTimeTrackingMonthlyCosts,
               this.calculatedValues.productTimeAttendanceMonthlyCosts,
               this.calculatedValues.productPerformanceMonthlyCosts,
               this.calculatedValues.TimeClockMonthlyCost,
               ...(this.calculatedValues.productPayrollMonthlyCosts ?? []),
               // Monthly dynamic (pricebook-driven) products. Derived from DynamicProducts +
               // selectedProductCosts the same way the one-time list below is: calculateQuote
               // keeps MonthlyDynamicCosts as a local and never puts it on calculatedValues,
               // so reading it here always yielded [] and dynamic-product discounts were
               // never allocated a PDF column. timeAttendance is already listed above.
               ...([...this.DynamicProducts.keys()]
                  .filter(code =>
                     code !== 'timeAttendance' &&
                     this.product_list[code]?.BillingFreq === 'monthly' &&
                     this.DynamicProducts.get(code) > 0)
                  .map(code => this.calculatedValues?.selectedProductCosts?.[code])
                  .filter(Boolean)),
            ].filter(Boolean)
            const monthlyPdfDedicatedDiscountCodes = new Set()

            this.MonthlyPdfExcludeAppliedFromOtherSum = [
               ...monthlyPdfDedicatedDiscountCodes,
            ]

            const monthly = buildTopDiscountCodes(
               allMonthlyProductCosts,
               monthlyPdfDedicatedDiscountCodes,
            )

            // One-time-only products
            const allOneTimeProductCosts = [
               ...([...this.DynamicProducts.keys()]
                  .filter(code => this.product_list[code]?.BillingFreq === 'once' && this.DynamicProducts.get(code) > 0)
                  .map(code => this.calculatedValues?.selectedProductCosts?.[code])
                  .filter(Boolean)),
               this.calculatedValues.hrImplementationCosts,
               this.calculatedValues.benefitsAdministrationImplementationCosts,
               this.calculatedValues.payrollImplementationTotalCosts,
               this.calculatedValues.AccountBalanceCosts,
               this.calculatedValues.HistoricalDataImportCosts,
               this.calculatedValues.MarketplaceListingCosts,
               this.calculatedValues.SponsorshipCosts,
               ...(this.calculatedValues.TimeClockCosts ?? []),
               ...(this.calculatedValues.ConsultingServicesCosts ?? []),
               ...(this.calculatedValues.ProfessionalServicesCosts ?? []),
            ].filter(Boolean)
            const oneTime = buildTopDiscountCodes(allOneTimeProductCosts)

            // Total visible discount columns = max needed by either table. This keeps both tables
            // column-aligned without adding extra empty slots beyond the max either side needs.
            //   maxMonthlyDiscountCols = (volume?) + (bundle?) + monthly.total
            //   maxOneTimeDiscountCols = (volume?) + oneTime.total   (OT has no bundle slot)
            // `monthly.total` / `oneTime.total` exclude both volume and bundle.
            const hasVolume = monthly.hasVolume || oneTime.hasVolume
            const hasBundle = monthly.hasBundle || oneTime.hasBundle
            // AD1 = Volume Discount column — shown only when a volume discount is actually present
            // (no "reserve when US employees" behavior; volume just appears when it exists).
            this.VolumeInAD1 = hasVolume
            // AD2 = Bundle Discount column. Shown when either:
            //   - HasUSEmployees and there's room (<7 reserved+rest slots), reserving even when 0%, or
            //   - a bundle discount is actually applied (regardless of US employees).
            // Uses raw monthly/oneTime totals for the room check, before computing final rest slots.
            const tentativeRest = Math.max(monthly.total, oneTime.total)
            this.BundleInAD2 = hasBundle || (this.HasUSEmployees && (tentativeRest + (this.VolumeInAD1 ? 1 : 0)) < 7)
            // Rest slots (AD3–AD7) must cover whichever table needs more:
            //   - Monthly's rest goes into AD3+ (Volume in AD1, Bundle in AD2 are dedicated).
            //   - One-Time has no dedicated Bundle slot, so OT can reuse AD2 — its rest only needs
            //     to overflow beyond AD2 (when Bundle reserved) and AD1 (when OT uses Volume).
            const maxMonthlyDiscountCols = (this.VolumeInAD1 ? 1 : 0) + (this.BundleInAD2 ? 1 : 0) + monthly.total
            const maxOneTimeDiscountCols = (oneTime.hasVolume ? 1 : 0) + oneTime.total
            const totalDiscountCols = Math.max(maxMonthlyDiscountCols, maxOneTimeDiscountCols)
            const dedicatedSlots = (this.VolumeInAD1 ? 1 : 0) + (this.BundleInAD2 ? 1 : 0)
            const REST_CAPACITY = 5
            const restNeeded = Math.max(0, totalDiscountCols - dedicatedSlots)
            const maxRestCodes = Math.min(restNeeded, REST_CAPACITY)
            // Monthly: positional array — [0]=volume slot, [1]=bundle slot, [2..6]=rest in priority order.
            // Cell sites reference these by fixed AD index (no ternaries needed). AD1 and AD2 are
            // dedicated reserved slots — hidden when absent, never reused — so rest only fills AD3–AD7 (5 slots).
            this.TopAppliedDiscountCodes = [
               this.VolumeInAD1 ? 'volume' : null,
               this.BundleInAD2 ? 'bundleDiscount' : null,
               ...monthly.codes.slice(0, REST_CAPACITY),
            ]
            this.TopAppliedDiscountNames = [
               this.VolumeInAD1 ? 'Volume\nDiscount' : '',
               this.BundleInAD2 ? 'Bundle\nDiscount' : '',
               ...monthly.names.slice(0, REST_CAPACITY),
            ]
            // One-time: compact ordered list (priority order). Volume is included when OT has it,
            // since the OT table fills its column slots left-to-right from this list and Volume is
            // a real OT discount that should appear in OT's columns. Bundle is not included —
            // bundle discounts only appear in the monthly table.
            this.TopAppliedDiscountCodesOneTime = [
               ...(oneTime.hasVolume ? ['volume'] : []),
               ...oneTime.codes,
            ]
            this.TopAppliedDiscountNamesOneTime = [
               ...(oneTime.hasVolume ? ['Volume\nDiscount'] : []),
               ...oneTime.names,
            ]

            // Column visibility — AD1/AD2 are slot-reserved; AD3–AD7 fill from rest in priority order.
            this.ShowAppliedDiscount1Col = this.VolumeInAD1
            this.ShowAppliedDiscount2Col = this.BundleInAD2
            this.ShowAppliedDiscount3Col = maxRestCodes >= 1
            this.ShowAppliedDiscount4Col = maxRestCodes >= 2
            this.ShowAppliedDiscount5Col = maxRestCodes >= 3
            this.ShowAppliedDiscount6Col = maxRestCodes >= 4
            this.ShowAppliedDiscount7Col = maxRestCodes >= 5
            this.ShowAppliedDiscountSummaryCol = restNeeded > REST_CAPACITY

            // Use prepay length; fall back to legacy discount code; default to monthly
            this.MonthsSelected = this.PrepayLength > 1
               ? this.PrepayLength
               : discountPrepaymentCodeToMonths(this.DiscountPrepaymentCode)

            this.IsProrated =
               this.calculatedValues.proratedActivationDetails.prorated
            if (this.IsProrated)
               this.DaysPaidInMonth =
                  this.calculatedValues.proratedActivationDetails.daysPaidInMonth

            if (
               this.hasPEPMCols &&
               ((this.ProductBenefitsAdministrationEECount < 25 &&
                  this.ProductBenefitsAdministrationEECount != 0) ||
                  (this.ProductPerformanceEECount < 25 &&
                     this.ProductPerformanceEECount != 0) ||
                  this.ProductPayrollEECounts.some(
                     (element) => element < 25 && element > 0,
                  ) ||
                  (this.ProductCoreEECount < 25 && this.ProductCoreEECount > 0))
            )
               this.MixedOverUnder25 = true

            this.NumDiscountCols = 0
            if (
               this.ShowDiscountCol ||
               this.ShowOnetimeDiscountCol ||
               this.ShowPromotionalCol
            ) {
               this.NumDiscountCols++
            }
            if (this.ShowAppliedDiscount1Col) this.NumDiscountCols++
            if (this.ShowAppliedDiscount2Col) this.NumDiscountCols++
            if (this.ShowAppliedDiscount3Col) this.NumDiscountCols++
            if (this.ShowAppliedDiscount4Col) this.NumDiscountCols++
            if (this.ShowAppliedDiscount5Col) this.NumDiscountCols++
            if (this.ShowAppliedDiscount6Col) this.NumDiscountCols++
            if (this.ShowAppliedDiscount7Col) this.NumDiscountCols++
            if (this.ShowAppliedDiscountSummaryCol) this.NumDiscountCols++

            await this.generateColumnWidths()

            // Include the row if the legacy aggregate type OR any per-EIN type
            // has payroll implementation. With multiple EINs the legacy
            // PayrollImpType can be 'none' (e.g. EIN 1) while a later EIN is
            // 'full', so checking only the scalar would zero out the
            // activation total even though its cost is included.
            const isValidPayrollImpTypeForActivation = (type) =>
               type !== null && type !== 'null' && type !== 'none'
            this.IncludePayrollImpInActivation =
               this.QuoteType !== 'monthly' &&
               (isValidPayrollImpTypeForActivation(this.PayrollImpType) ||
                  this.ProductPayrollEINImpTypes.some(
                     isValidPayrollImpTypeForActivation,
                  )) &&
               this.PayrollImpInActivation.includes(this.user_role)
            this.ShowEffectiveDiscountOnQuote =
               this.ShowEffectiveDiscountOnQuotePermissions?.includes(
                  this.user_role,
               ) &&
               !(
                  (this.user_role == 'Admin' ||
                     this.user_role == 'Super Admin') &&
                  this.DiscountReseller > 0
               )
            this.ShowEstimatedCoveragePeriod =
               this.ShowEstimatedCoveragePeriodPermissions?.includes(
                  this.user_role,
               ) || this.user_role === 'Super Admin'

            if (
               (this.ImpType !== null &&
                  this.ImpType !== 'null' &&
                  this.ImpType !== 'selfSetup') ||
               this.AccountBalance != 0 ||
               this.calculatedValues.totalImplementationCosts.totalPrice > 0 ||
               (this.calculatedValues.totalImplementationCosts.totalPrice ===
                  0 &&
                  this.calculatedValues.totalImplementationCosts.listPrice &&
                  this.calculatedValues.totalImplementationCosts
                     .totalDiscount ===
                     this.calculatedValues.totalImplementationCosts.listPrice)
            )
               this.HasOneTimeServices = true
            else if (this.ProductHistoricalDataImport)
               this.HasOneTimeServices = true
            else if (this.ProductMarketplaceListing)
               this.HasOneTimeServices = true
            else if (
               this.HasTimeClocks &&
               this.product_list.hasOwnProperty('clockMonthlySupport') &&
               this.product_list['clockMonthlySupport'] &&
               this.ProductTimeClockDetails &&
               this.ProductTimeClockDetails.reduce(
                  (acc, clock) => acc + clock.count,
                  0,
               ) > 0
            )
               this.HasOneTimeServices = true
            else if (
               (this.ProfessionalServices.length > 0 &&
                  this.ProfessionalServices.reduce(
                     (acc, service) => acc + service.count,
                     0,
                  ) > 0) ||
               this.calculatedValues.ConsultingServicesCosts.reduce(
                  (acc, val) => acc + val.totalPrice,
                  0,
               ) > 0
            )
               this.HasOneTimeServices = true
            else if (this.ProductSponsorship) this.HasOneTimeServices = true
            else if (this.ProductPaidServicesAmount > 0)
               this.HasOneTimeServices = true
            else if (hasOneTimeDynamicProducts) this.HasOneTimeServices = true
            if (this.QuoteType === 'monthly') this.HasOneTimeServices = false
            if (
               this.QuoteType !== 'onetime' &&
               ((this.CoreProductSelected !== 'core0' &&
                  this.ProductCoreEECount > 0) ||
                  this.ProductTimeTrackingEECount > 0 ||
                  this.ProductTimeAttendanceEECount > 0 ||
                  this.ProductBenefitsAdministrationEECount > 0 ||
                  this.ProductPerformanceEECount > 0 ||
                  this.ProductAdditionalJobsCount > 0 ||
                  this.ProductPayrollEECounts.some((element) => element > 0) ||
                  this.ProductTestAccount ||
                  this.ProductAdditionalStorageCount > 0 ||
                  this.MonthlyTax > 0 ||
                  this.calculatedValues?.TimeClockMonthlyCost?.totalPrice > 0 ||
                  hasMonthlyDynamicProducts)
            )
               this.HasMonthlyServices = true
            let allOverMin = true
            for (const [productCode, productInfo] of Object.entries(
               this.product_list,
            )) {
               if (productCode in this.ProductCodeToProductCount) {
                  let min = 0
                  if (productInfo['MinimumEmployees'] !== null)
                     min = productInfo['MinimumEmployees']
                  if (productCode === 'payroll') {
                     if (
                        this.ProductCodeToProductCount[productCode].every(
                           (element) => element > min || element === 0,
                        )
                     )
                        continue
                     else {
                        allOverMin = false
                        break
                     }
                  } else if (
                     this.ProductCodeToProductCount[productCode] > min ||
                     this.ProductCodeToProductCount[productCode] === 0
                  ) {
                     continue
                  } else {
                     allOverMin = false
                     break
                  }
               } else {
                  continue
               }
            }
            this.AllOverMin = allOverMin
            if (this.HasHRProducts)
               this.HRHeaderRow = await this.generateHRProductHeader()
            if (this.ProductCoreEECount > 0)
               this.ProductCoreRow = await this.generateProductCoreRow()
            const activeTimeEECount =
               this.TimeProductSelected === 'timeAttendance'
                  ? this.ProductTimeAttendanceEECount
                  : this.ProductTimeTrackingEECount
            if (activeTimeEECount > 0)
               this.ProductTimeTrackingRow =
                  await this.generateProductTimeTrackingRow()
            if (
               this.HasTimeClocks &&
               this.product_list.hasOwnProperty('clockMonthlySupport') &&
               this.product_list['clockMonthlySupport'] &&
               this.ProductTimeClockDetails &&
               this.ProductTimeClockDetails.reduce(
                  (acc, clock) => acc + clock.count,
                  0,
               ) > 0
            ) {
               this.ProductTimeClockMonthlySupportRow =
                  await this.generateProductTimeClockMonthlySupportRow() // Generates the Monthly cost of Time Clocks
               this.TimeClocksHeaderRow =
                  await this.generateTimeClocksProductHeader() // Generates the One Time Cost Header
               this.calculatedValues.TimeClockCosts.forEach((clockCost) => {
                  if (clockCost.productEECount > 0)
                     this.TimeClocksProductRows.push(
                        this.generateTimeClockProductRow(clockCost),
                     )
               })
            }
            if (
               this.ProfessionalServices.length > 0 &&
               this.ProfessionalServices.reduce(
                  (acc, service) => acc + service.count,
                  0,
               ) > 0
            ) {
               this.ProfessionalServicesHeaderRow =
                  await this.generateProfessionalServicesHeader()
               this.calculatedValues.ProfessionalServicesCosts.forEach(
                  (serviceCost) => {
                     if (serviceCost.productEECount > 0)
                        this.ProfessionalServicesRow.push(
                           this.generateProfessionalServicesRow(serviceCost),
                        )
                  },
               )
            }
            if (this.ConsultingServices.length > 0) {
               this.ConsultingServicesHeaderRow =
                  await this.generateConsultingServicesHeader()
               this.calculatedValues.ConsultingServicesCosts.forEach(
                  (serviceCost) => {
                     if (serviceCost.productEECount > 0)
                        this.ConsultingServicesRow.push(
                           this.generateConsultingServicesRow(serviceCost),
                        )
                  },
               )
            }

            if (this.ProductBenefitsAdministrationEECount > 0)
               this.ProductBenefitsAdministrationRow =
                  await this.generateProductBenefitsAdministrationRow()
            if (this.ProductPerformanceEECount > 0)
               this.ProductPerformanceRow =
                  await this.generateProductPerformanceRow()
            if (this.ProductAdditionalJobsCount > 0)
               this.ProductAdditionalJobsRow =
                  await this.generateProductAdditionalJobsRow()
            if (this.ProductAdditionalStorageCount > 0)
               this.ProductAdditionalStorageRow =
                  await this.generateProductAdditionalStorageRow()
            if (this.HasPayrollSelected) {
               this.ProductPayrollMonthlyStateCosts =
                  this.calculatedValues.productPayrollMonthlyStateCosts

               for (let i = 0; i < this.ProductPayrollEINCount; i++) {
                  // Creates the state rows for each EIN
                  if (this.ProductPayrollStateCounts[i] > 1) {
                     this.ProductPayrollStateRows[i] =
                        await this.generateProductPayrollAdditionalStateTaxRow(
                           i,
                        )
                  } else {
                     this.ProductPayrollStateRows[i] = []
                  }
                  // Creates the Integration rows for each EIN
                  if (
                     this.ProductPayrollEINIntegrations[i] &&
                     Object.keys(this.ProductPayrollEINIntegrations[i]).length >
                        0
                  ) {
                     this.ProductPayrollIntegrationsRows[i] =
                        await this.generateProductPayrollIntegrationsRow(i)
                  } else {
                     this.ProductPayrollIntegrationsRows[i] = []
                  }
               }

               this.ProductPayrollMonthlyCosts =
                  this.calculatedValues.productPayrollMonthlyCosts

               this.ProductPayrollImpMonthlyCosts =
                  this.calculatedValues.payrollImplementationTotalCosts

               for (let i = 0; i < this.ProductPayrollEINCount; i++) {
                  if (this.ProductPayrollEECounts[i] > 0) {
                     this.ProductPayrollRows.push(
                        await this.generateProductPayrollRow(i),
                     )
                     //this.ProductPayrollImpRows.push(await this.generatePayrollImpRow(i));
                  }
               }
            }

            //Set up productCode to count dict
            this.ProductCodeToProductCount = {
               [this.coreProductSelected]: this.ProductCoreEECount,
               performanceMan: this.ProductPerformanceEECount,
               timeTracking: this.ProductTimeTrackingEECount,
               benAdmin: this.ProductBenefitsAdministrationEECount,
               payroll: this.ProductPayrollEECounts,
            }

            this.AllPayrollRows = await this.generateAllPayrollRows()
            if (this.HasPayrollSelected) {
               this.PayrollHeaderRow = await this.generatePayrollProductHeader()
            }

            this.MonthlyHeaderRow = await this.generateMonthlyHeader()
            this.OneTimeHeaderRow = await this.generateOneTimeHeader()
            //this.HRImpRow = await this.generateHRImpRow();
            if (this.ImpType !== null && this.ImpType !== 'null' && this.ImpType !== 'selfSetup' && this.ProductCoreEECount > 0)
               this.HRImplementationRow = await this.generateHRImplementationRow()
            if (this.BenAdminImpType !== null && this.BenAdminImpType !== 'null' && this.BenAdminImpType !== 'none' && this.ProductBenefitsAdministrationEECount > 0)
               this.BenAdminImplementationRow = await this.generateBenAdminImplementationRow()
            const isValidPayrollImpType = (type) =>
               type !== null && type !== 'null' && type !== 'none'
            // Render the row if the legacy aggregate type OR any per-EIN type has
            // payroll implementation. With multiple EINs the legacy PayrollImpType
            // can be 'none' (e.g. EIN 1) while a later EIN is 'full', so checking
            // only the scalar would drop the row even though its cost is included.
            if (
               (isValidPayrollImpType(this.PayrollImpType) ||
                  this.ProductPayrollEINImpTypes.some(isValidPayrollImpType)) &&
               this.ProductPayrollEECounts.some((element) => element > 0)
            )
               this.PayrollImplementationRow =
                  await this.generatePayrollImplementationRow()
            // if(this.CoreCountryCount > 1)
            //     this.ImplementationCountriesRow = await this.generateImplementationCountriesRow();

            if (Number(this.AccountBalance) != 0)
               this.AccountBalanceRow = await this.generateAccountBalanceRow()

            if (Number(this.MonthlyTax) > 0)
               this.MonthlyTaxRow = await this.generateMonthlyTaxRow()

            if (this.ProductHistoricalDataImport)
               this.ProductHistoricalDataImportRow =
                  await this.generateProductHistoricalDataImportRow()
            if (this.ProductMarketplaceListing)
               this.ProductMarketplaceListingRow =
                  await this.generateProductMarketplaceListingRow()
            if (this.ProductSponsorship)
               this.ProductSponsorshipRow =
                  await this.generateProductSponsorshipRow()
            if (this.ProductTestAccount)
               this.ProductTestAccountRow =
                  await this.generateProductTestAccountRow()
            if (this.ProductPaidServicesAmount > 0)
               this.ProductPaidServicesRow =
                  await this.generateProductPaidServicesRow()
            if (this.DiscountMultiEntity)
               this.RelatedEntityDiscountRow =
                  await this.generateRelatedEntityDiscountRow()
            // if(this.DiscountNonProfit)
            //     this.NonprofitDiscountRow = await this.generateNonprofitDiscountRow();
            // if(this.DiscountPrepaymentCode)
            //     this.PrepaymentDiscountRow = await this.generatePrepaymentDiscountRow();

            this.HRMonthlyTotal = await this.generateHRMonthlyTotal()
            this.PayrollMonthlyTotal = await this.generatePayrollMonthlyTotal()
            // Combined payroll + alwaysUSD values for the Payroll Costs (USD) summary table
            const alwaysUSD = this.calculatedValues.alwaysUSDMonthlyTotal
            this.PayrollCombinedListPrice =
               (this.calculatedValues.payrollMonthlyTotal.listPrice || 0) +
               (alwaysUSD?.listPrice || 0)
            this.PayrollCombinedTotalPrice =
               (this.calculatedValues.payrollMonthlyTotal.totalPrice || 0) +
               (alwaysUSD?.totalPrice || 0)
            // Previous/difference monthly totals, for the payroll USD arrow row on
            // difference (expansion) quotes.
            // alwaysUSD (dynamic, always-USD products like managedPayrollServices) has no
            // previous-quote tracking at all — calculateBooleanProduct/IntegerProduct/
            // DecimalProduct never set previousTotalPrice/totalPriceDifference, so they
            // stay at 0 regardless of whether this is a difference quote. Since we have no
            // record of it being billed before, its entire current amount is treated as
            // incremental (added to the difference, not the previous total) so that
            // PayrollCombinedPreviousTotalPrice + PayrollCombinedTotalPriceDifference
            // always equals PayrollCombinedTotalPrice — otherwise "new" silently included
            // the full alwaysUSD amount while "previous"/"difference" ignored it entirely.
            this.PayrollCombinedPreviousTotalPrice =
               this.calculatedValues.payrollMonthlyTotal.previousTotalPrice || 0
            this.PayrollCombinedTotalPriceDifference =
               (this.calculatedValues.payrollMonthlyTotal.totalPriceDifference ||
                  0) + (alwaysUSD?.totalPrice || 0)
            this.PayrollCombinedTotalDiscount =
               (this.calculatedValues.payrollMonthlyTotal.totalDiscount || 0) +
               (alwaysUSD?.totalDiscount || 0)
            this.PayrollCombinedTotalDiscountPercent =
               this.PayrollCombinedListPrice > 0
                  ? (this.PayrollCombinedTotalDiscount / this.PayrollCombinedListPrice) * 100
                  : 0
            this.PayrollCombinedAnnualListPrice =
               (this.calculatedValues.payrollAnnualTotal.listPrice || 0) +
               (alwaysUSD?.listPrice || 0) * 12
            this.PayrollCombinedAnnualTotalPrice =
               (this.calculatedValues.payrollAnnualTotal.totalPrice || 0) +
               (alwaysUSD?.totalPrice || 0) * 12
            // Always-USD one-time charges (e.g. managedPayrollServicesStates). On USD quotes these are
            // already inside OneTimeTotal; on foreign quotes they are reported separately in USD.
            this.AlwaysUSDOneTimeTotal =
               this.calculatedValues.alwaysUSDOneTimeTotal?.totalPrice ?? 0
            this.AlwaysUSDOneTimeTotalListPrice =
               this.calculatedValues.alwaysUSDOneTimeTotal?.listPrice ?? 0
            // First-year (annual payroll + one-time) pre-discount total and savings, in USD —
            // mirrors ListPriceTotal / TotalFirstYearDiscount for the USD payroll block.
            // Uses PayrollCombinedListPrice/TotalPrice * 12 rather than
            // PayrollCombinedAnnualListPrice/TotalPrice (which depend on
            // calculatePayrollAnnualTotal's month-by-month recalculation and can be
            // undefined/NaN for quote shapes that path doesn't handle, e.g. no core
            // payroll headcount selected) — consistent with the same monthly-total
            // tie-out tradeoff already accepted above for PayrollCombinedTotalPrice.
            this.PayrollCombinedFirstYearListPrice =
               this.PayrollCombinedListPrice * 12 +
               this.AlwaysUSDOneTimeTotalListPrice
            this.PayrollCombinedFirstYearTotalPrice =
               this.PayrollCombinedTotalPrice * 12 + this.AlwaysUSDOneTimeTotal
            this.PayrollCombinedFirstYearDiscount =
               this.PayrollCombinedFirstYearListPrice -
               this.PayrollCombinedFirstYearTotalPrice
            //NOTE: this is assumed to NOT include payroll for a foreign currency quote
            this.MonthlyGrandTotal =
               this.calculatedValues.monthlyTotal.pdfDisplayTotal
            this.MonthlyOngoingTotal =
               this.calculatedValues.selectedProductCosts?.ongoingMonthlyTotal
                  ?.totalPrice ?? this.MonthlyGrandTotal
            this.MonthlyGrandTotalDiscount =
               this.calculatedValues.monthlyTotal.totalDiscount
            this.MonthlyGrandTotalDiscountPercent =
               this.calculatedValues.monthlyTotal.totalDiscountPercent
            //NOTE: These are assumed to NOT include payroll for a foreign currency quote
            this.MonthlyGrandTotalListPrice =
               this.calculatedValues.monthlyTotal.listPrice
            this.AnnualGrandTotalListPrice =
               this.calculatedValues.annualTotal.listPrice
            this.AnnualOngoingTotal =
               this.calculatedValues.annualOngoingTotal?.totalPrice ?? 0
            this.AnnualOngoingTotalListPrice =
               this.calculatedValues.annualOngoingTotal?.listPrice ?? 0
            this.PreviousMonthlyTotal =
               this.calculatedValues.monthlyTotal?.previousTotalPrice ?? 0
            this.PreviousAnnualTotal =
               this.calculatedValues.annualOngoingTotal?.previousTotalPrice ?? 0
            this.MonthlyDifference =
               this.calculatedValues.monthlyTotal?.totalPriceDifference ?? 0
            this.MonthlyOngoingDifference =
               this.calculatedValues.selectedProductCosts?.ongoingMonthlyTotal
                  ?.totalPriceDifference ?? 0
            this.AnnualDifference =
               this.calculatedValues.annualOngoingTotal?.totalPriceDifference ?? 0
            this.AnnualGrandTotal = this.calculatedValues.annualTotal.totalPrice
            this.FirstYearTotal =
               this.calculatedValues?.selectedProductCosts?.firstYearTotalCost
                  ?.totalPrice ?? 0
            if (
               this.HasMonthlyServices &&
               (!this.IsForeignCurrencyQuote ||
                  (this.IsForeignCurrencyQuote &&
                     (this.HasHRProducts || this.HasNonPayrollDynamicProducts)))
            ) {
               this.MonthlyTotalRow = await this.generateMonthlyTotalRow()
            }
            if (this.IsForeignCurrencyQuote && this.HasPayrollSelected) {
               this.PayrollTotalRow = await this.generatePayrollTotalRow()
            }
            this.OneTimeTotal = this.calculatedValues.oneTimeTotal.totalPrice
            this.OneTimeTotalDiscount =
               this.calculatedValues.oneTimeTotal.totalDiscount
            this.OneTimeTotalDiscountPercent =
               this.calculatedValues.oneTimeTotal.totalDiscountPercent
            this.OneTimeTotalListPrice =
               this.calculatedValues.oneTimeTotal.listPrice
            this.OneTimeTotalRow = await this.generateOneTimeTotalRow()
            if (!this.IncludePayrollImpInActivation) {
               // ANCHOR Due at Activation is here
               this.ActivationPrice =
                  typeof (
                     this.calculatedValues.totalDueAtActivation -
                     this.calculatedValues.payrollImplementationTotalCosts
                        .totalPrice
                  ) === 'number'
                     ? (
                          this.calculatedValues.totalDueAtActivation -
                          this.calculatedValues.payrollImplementationTotalCosts
                             .totalPrice
                       ).toFixed(2)
                     : this.calculatedValues.totalDueAtActivation -
                       this.calculatedValues.payrollImplementationTotalCosts
                          .totalPrice
            } else {
               this.ActivationPrice =
                  typeof this.calculatedValues.totalDueAtActivation === 'number'
                     ? this.calculatedValues.totalDueAtActivation.toFixed(2)
                     : this.calculatedValues.totalDueAtActivation
            }
            this.ActivationListPrice =
               this.calculatedValues.DueAtActivation.listPrice
            this.ActivationDiscount =
               this.calculatedValues.DueAtActivation.totalDiscount
            this.ActivationDiscountPercent =
               this.calculatedValues.DueAtActivation.totalDiscountPercent
            this.TotalDiscount = await this.generateTotalDiscount()
            if (typeof this.TotalDiscount === 'number')
               this.TotalDiscount = this.TotalDiscount.toFixed(2)
            this.TotalItems = await this.generateTotalItems()
            this.TotalDiscountItems = await this.generateTotalDiscountItems()
            this.PayrollTotalItems = await this.generatePayrollTotalItems()
            this.PayrollTotalDiscountItems =
               await this.generatePayrollTotalDiscountItems()
            this.ActivationTotalItems =
               await this.generateActivationTotalItems()
            this.AnnualTotalListPrice =
               this.calculatedValues.annualTotal.listPrice
            this.AnnualTotal = this.calculatedValues.annualTotal.totalPrice
            this.AnnualDiscount =
               this.calculatedValues.annualTotal.totalDiscount
            this.AnnualDiscountPercent =
               this.calculatedValues.annualTotal.totalDiscountPercent
            this.EstimatedPrepaymentCoverage =
               await this.generateEstimatedPrepaymentCoverage()

            //TODO: EASYBUTTON Remove this once we go live
            if (this.TermContractFeature.includes(this.user_role)) {
               // Get the Product Term based on the Term Length form DB
               this.TermLengthObject = this.TermLengthsList.find(
                  (term) => term.term_length === this.TermLength,
               )

               // For co-term quotes, update the pdf_name to show "Co-Term" instead of "Extended Term"
               if (this.IsCoTerm && this.TermLength) {
                  if (this.TermLengthObject) {
                     this.TermLengthObject.pdf_name = `Co-Term - ${this.TermLength} Months`
                  } else {
                     this.TermLengthObject = {
                        id: -1,
                        name: `${this.TermLength} Month Co-Term`,
                        pdf_name: `Co-Term - ${this.TermLength} Months`,
                        term_length: this.TermLength,
                        display_order: 0,
                        role_required: [],
                     }
                  }
               }

               // TODO: EASYBUTTON Double check this number before we go live.
               this.HRISEstimateCost =
                  calculateOngoingMonthlyCost(
                     //MonthlyPrice objects for each monthly product which factors into the activation total
                     this.calculatedValues.productCoreMonthlyCosts,
                     this.calculatedValues.productPerformanceMonthlyCosts,
                     this.calculatedValues.productTimeTrackingMonthlyCosts,
                     this.calculatedValues.productTimeAttendanceMonthlyCosts,
                     this.calculatedValues
                        .productBenefitsAdministrationMonthlyCosts,
                     this.calculatedValues.AdditionalJobsCosts,
                     this.calculatedValues.AdditionalStorageCosts,
                     this.calculatedValues.TestAccountCosts,
                     this.calculatedValues.MonthlyTaxCosts,
                     this.calculatedValues.TimeClockMonthlyCost,
                     this.calculatedValues.MonthlyDynamicCosts,
                     this.calculatedValues.productPayrollMonthlyTotalCosts,
                     this.calculatedValues.productPayrollMonthlyStateTotalCosts,
                     // Required inputs to perform a calculation.
                     this.product_list,
                     this.discount_list,
                     this.pricebookId,
                     this.QuoteType,
                     this.CoreProductSelected,
                     this.ProductCoreEECount,
                     this.ProductPerformanceEECount,
                     this.ProductTimeTrackingEECount,
                     this.HasTimeClocks,
                     this.ProductTimeClockDetails,
                     this.ProductBenefitsAdministrationEECount,
                     this.ProductPayrollEINCount,
                     this.ProductPayrollEECount,
                     this.ProductPayrollEECounts,
                     this.ProductPayrollTotalStateCount,
                     this.ProductPayrollStateCounts,
                     this.ProfessionalServices,
                     this.ConsultingServices,
                     this.ImpType,
                     this.BenAdminImpType,
                     this.PayrollImpType,
                     this.DiscountBundle,
                     this.DiscountNonProfit,
                     this.PrepayLength, // Use PrepayLength instead of DiscountPrepaymentCode
                     this.DiscountMultiEntity,
                     this.DiscountHRMonthlyPromotion,
                     this.DiscountHRDealDeskMonthly,
                     this.DiscountReseller,
                     this.DiscountQuickstartTraining,
                     this.DiscountImpPromotion,
                     this.DiscountDealDeskHRImp,
                     this.DiscountDealDeskPayrollImp,
                     this.DiscountSecondEIN,
                     this.DiscountGroupImp,
                     this.DiscountDealDeskHRImpRate,
                     this.DiscountDealDeskQuickstartImpRate,
                     this.DiscountDealDeskPayrollImpRate,
                     this.ProductAdditionalStorageCount,
                     this.ProductAdditionalJobsCount,
                     this.ProductHistoricalDataImport,
                     this.ProductMarketplaceListing,
                     this.ProductSponsorship,
                     this.ProductPaidServicesAmount,
                     this.ProductTestAccount,
                     this.QuoteActivationDate,
                     this.DifferenceQuote,
                     this.AccountSnowflake,
                     this.CoreCountryCount,
                     this.PromotionalDiscounts,
                     this.NextInvoiceDate,
                     this.currencyObject,
                     this.AccountBalance,
                     this.MonthlyTax,
                     this.ProductPayrollEINIntegrations,
                     this.AllPermissions,
                     this.calculationUserRole,
                     this.AccountStatus,
                     findObjectByKeyValue(
                        this.pricebooks,
                        'value',
                        parseInt(this.pricebookId),
                     ),
                     this.previousCurrencyObject,
                     this.ProductPayrollEINImpTypes,
                     this.prepaymentActivationUseDifference,
                     this.DynamicProducts,
                     this.TimeProductSelected,
                     this.IsATSOnly,
                     false,
                  )[0] || 0 // Gets the first value from the array returned by calculateOngoingMonthlyCost which represents from
            }

            if (this.hasPEPMCols) this.NumColumns += 2
            if (this.hasDiscountPrepaymentCol) this.NumColumns++

            if (this.ProductPayrollEECounts.length > 0)
               for (let i = 0; i < this.ProductPayrollEINCount; i++) {
                  if (this.ProductPayrollEECounts[i] > 0) {
                     this.HasPayrollSelected = true
                     break
                  }
               }

            if (this.DifferenceQuote) {
               this.DifferenceQuoteHeader = await this.generateDiffQuoteHeader()
               this.DifferenceQuoteRows = await this.generateDiffRows(
                  this.currencyObject,
               )
            }

            if (this.DynamicProducts.size > 0) {
               const monthlyDynamicResult = this.generateMonthlyDynamicProductRows()
               this.MonthlyDynamicProductRows = monthlyDynamicResult.rows
               this.PayrollDynamicProductRows = monthlyDynamicResult.payrollRows
               this.OneTimeDynamicProductRows = this.generateOneTimeDynamicProductRows()
               if (this.IsForeignCurrencyQuote) {
                  this.AlwaysUSDPayrollRows =
                     this.generateAlwaysUSDPayrollRows()
                  this.AlwaysUSDPayrollOneTimeRows =
                     this.generateAlwaysUSDPayrollOneTimeRows()
                  // Generate header if we have one-time AlwaysUSD products
                  if (this.AlwaysUSDPayrollOneTimeRows.length > 0) {
                     this.AlwaysUSDPayrollOneTimeHeaderRow =
                        this.generateAlwaysUSDPayrollOneTimeHeader()
                  }
               }
            }

            // Assigns disclaims to monthly products
            assignDisclaimers(this)

            //Set MaxEECount to be the max PEPM dynamic product count
            for (const [productCode, count] of this.DynamicProducts) {
               const product = this.product_list[productCode]
               const productCount = this.DynamicProducts.get(productCode)
               if (product && productCount > 0 && product?.IsPEPM) {
                  this.maxEECount = Math.max(this.maxEECount, productCount)
               }
            }
            //Set MaxEECount to be the max of all products(including any pepm dynamic products)
            this.maxEECount = Math.max(
               this.maxEECount,
               this.ProductCoreEECount,
               this.ProductBenefitsAdministrationEECount,
               this.ProductPerformanceEECount,
               this.ProductTimeTrackingEECount,
               this.ProductPayrollEECount,
            )

            //Calculate total effective discount and first year discount
            this.TotalEffectiveDiscount =
               this.MonthlyGrandTotalDiscount + this.OneTimeTotalDiscount
            this.TotalEffectiveDiscountPercent =
               this.MonthlyGrandTotal + this.OneTimeTotal > 0
                  ? (this.TotalEffectiveDiscount /
                       (this.MonthlyGrandTotal + this.OneTimeTotal)) *
                    100
                  : 0

            this.TotalFirstYearDiscount =
               this.calculatedValues?.selectedProductCosts?.firstYearTotalCost?.totalDiscount
            this.TotalFirstYearDiscountPercent =
               this.calculatedValues?.selectedProductCosts?.firstYearTotalCost?.totalDiscountPercent

            //Check if there are any time limited discounts
            for (const discount of this.calculatedValues.monthlyTotal
               .appliedDiscounts) {
               if ((discount?.EndMonth ?? Number.MAX_SAFE_INTEGER) < 12) {
                  this.HasTimeLimitedDiscount = true
                  break
               }
            }

            return true
         }
      } else {
         return false
      }
   }
}

function assignDisclaimers(quoteData) {
   let nextSymbol = 0
   let symbols = [
      '¶',
      '#',
      '§',
      '†',
      '‡',
      '¶¶',
      '##',
      '§§',
      '††',
      '‡‡',
      '¶¶¶',
      '###',
      '§§§',
      '†††',
      '‡‡‡',
   ]

   // Assigned footnote symbol to each discount disclaimer
   let summarizedDiscounts = []

   // Process monthly discounts for disclaimer symbols
   for (
      let i = 0;
      i <
      (quoteData?.calculatedValues?.monthlyTotal?.appliedDiscounts?.length ??
         0);
      i++
   ) {
      const monthlyDiscount = quoteData?.calculatedValues?.monthlyTotal?.appliedDiscounts[i]
      const hasValidDisclaimer = monthlyDiscount?.Disclaimer && 
         typeof monthlyDiscount.Disclaimer === 'string' && 
         monthlyDiscount.Disclaimer.trim() !== ''
      const hasValidShowOn = monthlyDiscount?.ShowDisclaimerOn && 
         (Array.isArray(monthlyDiscount.ShowDisclaimerOn) 
            ? monthlyDiscount.ShowDisclaimerOn.length > 0 
            : typeof monthlyDiscount.ShowDisclaimerOn === 'string' && monthlyDiscount.ShowDisclaimerOn.trim() !== '')
      
      if (hasValidDisclaimer && hasValidShowOn) {
         // Add disclaimer symbol to the discount
         quoteData.calculatedValues.monthlyTotal.appliedDiscounts[
            i
         ].DisclaimerSymbol = symbols[nextSymbol]
         summarizedDiscounts.push(
            quoteData.calculatedValues.monthlyTotal.appliedDiscounts[i],
         )
         nextSymbol++
      }
   }

   // Process one-time discounts for disclaimer symbols (same logic as monthly)
   let oneTimeSummarizedDiscounts = []
   for (
      let i = 0;
      i <
      (quoteData?.calculatedValues?.oneTimeTotal?.appliedDiscounts?.length ??
         0);
      i++
   ) {
      const oneTimeDiscount = quoteData?.calculatedValues?.oneTimeTotal?.appliedDiscounts[i]
      const hasValidOneTimeDisclaimer = oneTimeDiscount?.Disclaimer && 
         typeof oneTimeDiscount.Disclaimer === 'string' && 
         oneTimeDiscount.Disclaimer.trim() !== ''
      const hasValidOneTimeShowOn = oneTimeDiscount?.ShowDisclaimerOn && 
         (Array.isArray(oneTimeDiscount.ShowDisclaimerOn) 
            ? oneTimeDiscount.ShowDisclaimerOn.length > 0 
            : typeof oneTimeDiscount.ShowDisclaimerOn === 'string' && oneTimeDiscount.ShowDisclaimerOn.trim() !== '')
      
      if (hasValidOneTimeDisclaimer && hasValidOneTimeShowOn) {
         // Add disclaimer symbol to the discount
         quoteData.calculatedValues.oneTimeTotal.appliedDiscounts[
            i
         ].DisclaimerSymbol = symbols[nextSymbol]
         oneTimeSummarizedDiscounts.push(
            quoteData.calculatedValues.oneTimeTotal.appliedDiscounts[i],
         )
         nextSymbol++
      }
   }

   // Combine both monthly and one-time discounts for total summarizedDiscounts
   summarizedDiscounts = summarizedDiscounts.concat(oneTimeSummarizedDiscounts)

   // No need to add disclaimer symbols to TotalItems, PayrollTotalItems, or ActivationTotalItems
   // The disclaimer symbols will be added directly to the line items in the Monthly and One-Time sections
   // by the addDisclaimerSymbolsToLineItems function in disclaimerUtils.js
}

/**
 * Returns the discount as a percentage for display.
 * For percent-type discounts, returns DiscountAmount directly (already a percent).
 * For fixed/flatrate-type discounts, computes the percent off from actualEffect
 * so a $800 discount on a $12,000 line shows as 6.67%, not 800%.
 * @param {Object} productCosts - The product costs object containing appliedDiscounts.
 * @param {string} code - The DiscountCode to look up.
 * @returns {number}
 */
function getDiscountAmountByCode(productCosts, code) {
   if (!code || !productCosts?.appliedDiscounts?.length) return 0
   const codes = Array.isArray(code) ? code : [code]
   let total = 0
   for (const c of codes) {
      const d = productCosts.appliedDiscounts.find(d => d.DiscountCode === c)
      if (!d) continue
      if (d.DiscountType === 'percent') {
         // DiscountAmount can arrive as a string (DB returns DECIMAL as string for
         // pricebook discounts); coerce to avoid string concatenation into `total`.
         total += Number(d.DiscountAmount) || 0
      } else {
         const previousTotalPrice = d.actualEffect?.previousTotalPrice
         const changeInDollars = d.actualEffect?.changeInDollars
         if (previousTotalPrice && changeInDollars)
            total += (changeInDollars / previousTotalPrice) * 100
      }
   }
   return total
}

/**
 * Returns the sum of all applied discounts NOT in the provided top codes list.
 * @param {Object} productCosts - The product costs object containing appliedDiscounts.
 * @param {string[]} topCodes - The top discount codes to exclude from the sum.
 * @param {string[]} [alsoExcludeCodes] - Extra codes to exclude (e.g. volume/bundle shown in dedicated monthly columns).
 * @param {{ excludePrepaymentFamily?: boolean }} [options] - When `excludePrepaymentFamily`, omits prepayment discounts (e.g. one-time table dedicated prepayment slot).
 * @returns {number}
 */
function getOtherDiscountsSum(productCosts, topCodes, alsoExcludeCodes, options) {
   if (!productCosts?.appliedDiscounts?.length) return 0
   const exclude = new Set()
   for (const c of topCodes ?? []) {
      if (Array.isArray(c)) { for (const sc of c) exclude.add(sc) }
      else exclude.add(c)
   }
   for (const c of alsoExcludeCodes ?? []) exclude.add(c)
   return productCosts.appliedDiscounts
      .filter(d => {
         if (exclude.has(d.DiscountCode)) return false
         if (
            options?.excludePrepaymentFamily &&
            d.DiscountFamily === 'Prepayment Discount'
         )
            return false
         return true
      })
      .reduce((sum, d) => sum + d.DiscountAmount, 0)
}

/**
 * Sums all the applied promotional discounts with a related UTM code. Then formats the sum into a discount percentage value.
 * Per Amanda Halupnik, the discount percentage is calculated by multiplying the inverted discount amounts in priority order then subtracting that from 100%.
 *
 * @param {*} productCosts - The product costs object.
 * @param {*} promoUTM - The promotional UTM code to filter the applied discounts.
 * @returns {string} The formatted discount percentage as a string with a '%' symbol.
 */
function getPromotionalDiscountPercent(productCosts, promoUTM) {
   const promoUTMList = promoUTM ? promoUTM.split(',') : []
   let percent = 1
   if ('appliedDiscounts' in productCosts)
      if (productCosts.appliedDiscounts != 0)
         if (productCosts.appliedDiscounts.length > 0) {
            const filteredDiscounts = productCosts.appliedDiscounts.filter(
               ({ PromoUTM }) => {
                  if (!PromoUTM) return false
                  return PromoUTM.split(',').some((code) =>
                     promoUTMList.includes(code),
                  )
               },
            )

            filteredDiscounts.sort((a, b) => a.Priority - b.Priority)
            if (filteredDiscounts.length > 0) {
               for (const { DiscountAmount } of filteredDiscounts) {
                  if (DiscountAmount < 100) {
                     let invertedDiscountAmount = 1 - DiscountAmount / 100
                     percent *= invertedDiscountAmount
                  } else {
                     percent = 0
                     break
                  }
               }
            }
         }
   return formatDiscountPercent(100 - percent * 100)
}

export { GenerateQuoteData, formatDiscountPercent }
